/**
 * Counterpunch App - Single Page Application Navigation
 *
 * This module handles:
 * - Intercepting internal link clicks
 * - Fetching and loading page content dynamically
 * - Updating browser history and URL
 * - Browser back/forward button support
 * - Page caching
 */

import {
  init as plausibleInit,
  track as plausibleTrack,
} from "@plausible-analytics/tracker";
import { marked } from "marked";
import {
  describeLoginCodeError,
  extractLoginCode,
  LOGIN_CODE_LENGTH,
  sanitizeLoginCodeInput,
} from "./login-code.js";
import { settings } from "./settings.js";
import {
  getFreeTrialProduct,
  getPricingProducts,
  paidPlanCard,
  yearlyMonths,
} from "./pricing-config.js";
import {
  cloudflareCostLines,
  estimateCloudflarePlanCost,
  marginBand,
  monthlyIncludedAiCreditEur,
  usageAssumptionRows,
} from "./cloudflare-cost.js";
import {
  applyPppOverrides,
  calculatePrice,
  loadPPPData,
  pppCountryToStripe,
  pppMultiplier,
} from "../../utils/ppp.js";
import {
  CONNECT_PAYOUT_COUNTRIES,
  connectCountryName,
} from "../../utils/connect-countries.js";
import {
  formatPluginPriceRange,
  pluginPriceRange,
} from "../../utils/plugin-pricing.js";
import {
  pluginImportRows,
  pluginReleaseInProgress,
  versionReleaseRows,
} from "../../utils/plugin-release-status.js";
import { cloudFontAccountRows } from "../../utils/plan-change.js";
import { payoutStatusText } from "../../utils/payout-status.js";
import { euPriceDisplay } from "../../utils/subscription-pricing.js";

// Page titles for SPA navigation
// The middleware rejects cookie-authenticated POSTs that are not JSON or form
// data (utils/cloud-csrf.js), so a POST with no body still declares JSON.
const JSON_POST_HEADERS = { "content-type": "application/json" };

// utils/payout-status.js names the state; an older API response has no status.
function payoutStatusOf(payout) {
  if (!payout) return "not_started";
  if (payout.status) return payout.status;
  return payout.payouts_enabled && payout.transfers_active
    ? "enabled"
    : "action_required";
}

const PAGE_TITLES = {
  "/": "Counterpunch - Modern Font Editor",
  "/about": "About Counterpunch",
  "/pricing": "Pricing - Counterpunch",
  "/articles": "Articles - Counterpunch",
  "/documentation": "Documentation - Counterpunch",
  "/login": "Login - Counterpunch",
  "/invite": "Invitation - Counterpunch",
  "/transfer": "Ownership Transfer - Counterpunch",
  "/account": "Account - Counterpunch",
  "/admin/dashboard": "Admin Dashboard - Counterpunch",
  "/admin/errors": "Error Details - Counterpunch",
  "/privacy": "Website Privacy Policy - Counterpunch",
  "/terms": "Terms of Service - Counterpunch",
};

// Helper function to get model display name
// Maps model IDs to their short names (must stay in sync with utils/ai-settings.js)
function getModelDisplayName(modelId) {
  const modelNames = {
    "claude-sonnet-4-6": "Sonnet",
    "kimi-2.6": "Kimi",
    "deepseek-v4-flash": "DeepSeek",
  };
  return modelNames[modelId] || modelId;
}

const yearlyIntro = `<p>Yearly cost and margin, excl. VAT. Revenue is ${yearlyMonths} months of the exclusive fee. Below 50% is green, below 25% yellow, below 0% red.</p>`;

const PLAN_CHART_LABELS = ["Free", "Trial", "Basic", "Adv.", "Pro", "Ent."];
const PLAN_CHART_COLORS = [
  "var(--accent-lightblue)",
  "var(--accent-purple)",
  "var(--accent-blue)",
];

function formatPlanChartValue(value, unit) {
  if (unit === "%") {
    const pct = value * 100;
    return `${Math.abs(pct) < 10 ? pct.toFixed(1) : Math.round(pct)}%`;
  }
  if (unit === "€") {
    if (Math.abs(value) >= 10) {
      const rounded = Math.round(value * 10) / 10;
      return Number.isInteger(rounded)
        ? `€${rounded}`
        : `€${rounded.toFixed(1)}`;
    }
    return `€${value.toFixed(2)}`;
  }
  if (Math.abs(value) >= 1000) {
    const thousands = value / 1000;
    return `${Number.isInteger(thousands) ? thousands : thousands.toFixed(1)}k`;
  }
  return String(Math.round(value));
}

function chartHoverValue(value, unit) {
  if (unit === "%") {
    const pct = value * 100;
    return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
  }
  if (unit === "€") return `€${value.toFixed(2)}`;
  return Number(value).toLocaleString("en-US");
}

function planLineChart(title, unit, series) {
  const width = 420;
  const height = 210;
  const pad = { left: 52, right: 12, top: 22, bottom: 36 };
  const values = series.flatMap((item) => item.values);
  let yMin = Math.min(0, ...values);
  let yMax = Math.max(0, ...values);
  if (yMax === yMin) yMax = yMin + 1;
  const span = yMax - yMin;
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const xAt = (index) =>
    pad.left + (index / (PLAN_CHART_LABELS.length - 1)) * plotW;
  const yAt = (value) => pad.top + (1 - (value - yMin) / span) * plotH;
  const ticks = [...new Set([yMin, yMin + span / 2, yMax])];
  const grid = ticks
    .map((tick) => {
      const y = yAt(tick).toFixed(1);
      return `<line x1="${pad.left}" y1="${y}" x2="${width - pad.right}" y2="${y}" class="pricing-chart-grid"></line><text x="${pad.left - 6}" y="${y}" class="pricing-chart-tick">${formatPlanChartValue(tick, unit)}</text>`;
    })
    .join("");
  const xLabels = PLAN_CHART_LABELS.map(
    (label, index) =>
      `<text x="${xAt(index).toFixed(1)}" y="${height - 10}" class="pricing-chart-xlabel">${label}</text>`,
  ).join("");
  const lines = series
    .map((item, seriesIndex) => {
      const color = PLAN_CHART_COLORS[seriesIndex % PLAN_CHART_COLORS.length];
      const points = item.values
        .map(
          (value, index) => `${xAt(index).toFixed(1)},${yAt(value).toFixed(1)}`,
        )
        .join(" ");
      const dots = item.values
        .map((value, index) => {
          const x = xAt(index);
          const y = yAt(value);
          const tip = `${PLAN_CHART_LABELS[index]}${item.name ? ` · ${item.name}` : ""}: ${chartHoverValue(value, unit)}`;
          const tipWidth = Math.max(54, tip.length * 6.2 + 12);
          const above = y > pad.top + 18;
          const tipY = above ? y - 22 : y + 10;
          return `<g class="pricing-chart-dot" role="img" aria-label="${tip}">
        <circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3.5" fill="${color}"></circle>
        <circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="11" fill="transparent"></circle>
        <g class="pricing-chart-tip" transform="translate(${x.toFixed(1)} ${tipY.toFixed(1)})">
          <rect x="${(-tipWidth / 2).toFixed(1)}" y="0" width="${tipWidth.toFixed(1)}" height="16" rx="3"></rect>
          <text y="11.5">${tip}</text>
        </g>
        <title>${tip}</title>
      </g>`;
        })
        .join("");
      const labels =
        series.length === 1
          ? item.values
              .map(
                (value, index) =>
                  `<text x="${xAt(index).toFixed(1)}" y="${Math.max(12, yAt(value) - 8).toFixed(1)}" class="pricing-chart-value">${formatPlanChartValue(value, unit)}</text>`,
              )
              .join("")
          : "";
      return `<polyline points="${points}" fill="none" stroke="${color}" stroke-width="2"></polyline>${dots}${labels}`;
    })
    .join("");
  const legend =
    series.length > 1
      ? `<div class="pricing-chart-legend">${series
          .map(
            (item, index) =>
              `<span><i style="background:${PLAN_CHART_COLORS[index % PLAN_CHART_COLORS.length]}"></i>${item.name}</span>`,
          )
          .join("")}</div>`
      : "";
  return `<figure class="pricing-progression-chart"><figcaption>${title}</figcaption>${legend}<svg viewBox="0 0 ${width} ${height}" role="group" aria-label="${title}">${grid}${lines}${xLabels}</svg></figure>`;
}

function usageAssumptionMarkup() {
  const profiles = ["Light", "Medium", "Heavy"];
  const percent = (value) => {
    const shown = value * 100;
    if (Math.abs(shown - Math.round(shown)) < 0.05)
      return `${Math.round(shown)}%`;
    const text = shown.toFixed(2).replace(/0$/, "");
    return `${text}%`;
  };
  const head = profiles.map((label) => `<th>${label}</th>`).join("");
  const body = usageAssumptionRows()
    .map(
      (row) =>
        `<tr><th scope="row">${row.label}</th>${row.values.map((value) => `<td>${percent(value)}</td>`).join("")}</tr>`,
    )
    .join("");
  return `<h2>Usage assumed</h2><p>Share of the plan allowance, or of a 160-hour month.</p><table class="pricing-assumption-table"><thead><tr><th></th>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

function pricingProgressionMarkup(products, germanyMultiplier) {
  const numberOrZero = (value) => Number(value) || 0;
  const monthlyAt = (product, rawMultiplier) =>
    calculatePrice(numberOrZero(product.pricing?.monthly?.base), rawMultiplier);
  const marginFor = (label, product, rawMultiplier) => {
    const lines = cloudflareCostLines(
      product,
      monthlyAt(product, rawMultiplier),
    );
    return lines.find((line) => line.label === label)?.margin ?? 0;
  };
  const marginChart = (label) =>
    planLineChart(`${label}-use margin`, "%", [
      {
        name: "Germany",
        values: products.map((product) =>
          marginFor(label, product, germanyMultiplier),
        ),
      },
      {
        name: "PPP floor",
        values: products.map((product) => marginFor(label, product, 0)),
      },
    ]);
  const charts = [
    planLineChart("Fonts included", "", [
      {
        values: products.map((product) => numberOrZero(product.maxFontsOwned)),
      },
    ]),
    planLineChart("Invitees per font", "", [
      {
        values: products.map((product) =>
          numberOrZero(product.maxInvitesPerAsset),
        ),
      },
    ]),
    planLineChart("Glyphs per font", "", [
      {
        values: products.map((product) =>
          numberOrZero(product.maxGlyphsPerFont),
        ),
      },
    ]),
    planLineChart("Monthly price and credit, Germany excl. VAT", "€", [
      {
        name: "Price",
        values: products.map((product) =>
          monthlyAt(product, germanyMultiplier),
        ),
      },
      {
        name: "Included credit",
        values: products.map((product) =>
          monthlyIncludedAiCreditEur(
            product,
            monthlyAt(product, germanyMultiplier),
          ),
        ),
      },
    ]),
    planLineChart(
      "Monthly server cost",
      "€",
      ["light", "medium", "heavy"].map((profileId) => ({
        name: profileId[0].toUpperCase() + profileId.slice(1),
        values: products.map(
          (product) => estimateCloudflarePlanCost(product, profileId).eur,
        ),
      })),
    ),
    marginChart("Light"),
    marginChart("Medium"),
    marginChart("Heavy"),
  ];
  return `<h2>Plan progression</h2><p>Each chart has its own scale. Plans run from Free to Enterprise.</p><div class="pricing-progression">${charts.join("")}</div>${usageAssumptionMarkup()}`;
}

class AppNavigator {
  constructor() {
    this.pageCache = new Map();
    this.contentContainer = null;
    this.currentPath = window.location.pathname;
    this.trackingParams = {}; // Store tracking parameters
    this.currentUser = null; // Store current user session
    this.isAdminUser = false;
    this.accountMembership = null;
    this.adminMembershipLookupResult = null;
    this.adminCloudLookupResult = null;

    // List of common tracking/marketing parameters to remove from URL
    this.trackingParamNames = [
      // UTM parameters
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
      // Facebook
      "fbclid",
      "fb_action_ids",
      "fb_action_types",
      "fb_source",
      "fb_ref",
      // Google
      "gclid",
      "gclsrc",
      "dclid",
      // Microsoft/Bing
      "msclkid",
      // Twitter
      "twclid",
      // LinkedIn
      "li_fat_id",
      // TikTok
      "ttclid",
      // Other common parameters
      "mc_cid",
      "mc_eid",
      "_ga",
      "ref",
      "referrer",
    ];

    // Initialize Plausible Analytics with proxy endpoint
    plausibleInit({
      domain: "counterpunch.space", // Always use production domain for unified analytics
      captureOnLocalhost: true, // Allow tracking on localhost for development
      autoCapturePageviews: false, // We'll manually track pageviews
      endpoint: window.location.origin + "/api/event", // Use our own domain as proxy
    });
    console.log(
      "✅ Plausible initialized with endpoint:",
      window.location.origin + "/api/event",
    );
    console.log("📊 Tracking domain:", "counterpunch.space");

    // Capture tracking params immediately, even before DOM is ready
    this.captureAndCleanTrackingParams();

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setup());
    } else {
      this.setup();
    }
  }

  async setup() {
    this.contentContainer = document.querySelector(".main-content");

    if (!this.contentContainer) {
      console.error("Main content container not found");
      return;
    }

    // Keep handbook screenshots in sync when the theme attribute changes
    new MutationObserver(() => this.refreshDocumentationScreenshots()).observe(
      document.documentElement,
      { attributes: true, attributeFilter: ["data-theme"] },
    );

    // Check for URL parameters
    const params = new URLSearchParams(window.location.search);

    // Check for login success
    if (params.get("login") === "success") {
      // Clear the parameter from URL
      window.history.replaceState({}, "", window.location.pathname);
      console.log("✅ Login successful");
    }

    // Check for error messages and display them
    if (params.get("error")) {
      this.showAuthError(params.get("error"));
      // Clear the error from URL
      window.history.replaceState({}, "", window.location.pathname);
    }

    // Bind the sign-in form before asking who is signed in. That request can
    // take seconds, and a submit before the handler exists posts the form
    // natively: the page reloads with the email in the URL and no code is
    // sent. If someone turns out to be signed in, the login handler below
    // still hides the form and redirects.
    if (document.querySelector("[data-login-form]")) {
      this.initializeLoginForm();
    }

    await this.refreshCurrentUser();

    // Initialize auth UI
    this.updateAuthUI();

    // Intercept all clicks on links
    document.addEventListener("click", (e) => this.handleLinkClick(e));

    // Handle browser back/forward buttons
    window.addEventListener("popstate", (e) => this.handlePopState(e));

    // Load initial page based on current URL
    const currentPath = window.location.pathname;
    console.log("📍 Initial page load, currentPath:", currentPath);

    // Set up footer width syncing (do this for all pages)
    // Sync footer section widths with header (with delay for layout to settle)
    requestAnimationFrame(() => {
      setTimeout(() => this.syncFooterWidths(), 100);
    });

    // Re-sync on window resize
    window.addEventListener("resize", () => this.syncFooterWidths());

    // Re-sync when fonts are loaded
    if (document.fonts) {
      document.fonts.ready.then(() => this.syncFooterWidths());
    }

    // Check if content is already in the main container (from static HTML)
    const hasContent =
      this.contentContainer &&
      this.contentContainer.innerHTML.trim().length > 50 &&
      !this.contentContainer.innerHTML.includes(
        "<!-- Content loaded dynamically -->",
      );

    const isDeepLink = currentPath !== "/" && currentPath !== "/index.html";
    const isHomepageFallback =
      isDeepLink && this.contentContainer.querySelector(".home-hero") !== null;

    if (hasContent && !isHomepageFallback) {
      // Content already loaded from static HTML, just initialize handlers
      console.log(
        "📍 Content already present from static HTML, skipping fetch",
      );
      this.currentPath = currentPath;

      // /documentation serves the first handbook page — canonicalize the URL
      if (currentPath === "/documentation") {
        const currentDoc = this.contentContainer.querySelector(
          ".article-toc a.is-current",
        );
        const canonical = currentDoc?.getAttribute("href");
        if (canonical && canonical !== "/documentation") {
          window.history.replaceState({ path: canonical }, "", canonical);
          this.currentPath = canonical;
        }
      }

      this.initializePageHandlers(this.currentPath);
      this.trackPageView(this.currentPath);
      return;
    }

    if (isDeepLink) {
      // We're on a deep link, load the appropriate content
      console.log("📍 Loading deep link page");
      this.loadPage(currentPath, false);
    } else {
      // Load home page by default
      console.log("📍 Loading home page");
      this.loadPage("/", false);
    }
  }

  /**
   * Sync footer left/right widths with header left/right widths
   */
  syncFooterWidths() {
    const headerLeft = document.querySelector(".title-bar-left");
    const headerRight = document.querySelector(".title-bar-right");
    const footerLeft = document.querySelector(".footer-left");
    const footerRight = document.querySelector(".footer-right");

    console.log("🔄 Syncing footer widths...");
    console.log("Header left:", headerLeft, "width:", headerLeft?.offsetWidth);
    console.log(
      "Header right:",
      headerRight,
      "width:",
      headerRight?.offsetWidth,
    );
    console.log("Footer left:", footerLeft);
    console.log("Footer right:", footerRight);

    if (headerLeft && headerRight) {
      const leftWidth = headerLeft.offsetWidth;
      // Set header right to be at least as wide as header left
      headerRight.style.minWidth = `${leftWidth}px`;
      console.log("✓ Set header-right min-width to:", leftWidth);
    }

    if (headerLeft && footerLeft) {
      const leftWidth = headerLeft.offsetWidth;
      footerLeft.style.width = `${leftWidth}px`;
      footerLeft.style.flexShrink = "0";
      footerLeft.style.minWidth = "unset";
      footerLeft.style.flexGrow = "0";
      console.log("✓ Set footer-left width to:", leftWidth);
    }

    if (headerRight && footerRight) {
      const rightWidth = headerRight.offsetWidth;
      footerRight.style.width = `${rightWidth}px`;
      footerRight.style.flexShrink = "0";
      footerRight.style.minWidth = "unset";
      footerRight.style.flexGrow = "0";
      console.log("✓ Set footer-right width to:", rightWidth);
    }
  }

  /**
   * Capture tracking parameters from URL and remove them from display
   */
  captureAndCleanTrackingParams() {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    let hasTrackingParams = false;

    // Extract and store tracking parameters
    this.trackingParamNames.forEach((paramName) => {
      if (params.has(paramName)) {
        this.trackingParams[paramName] = params.get(paramName);
        params.delete(paramName);
        hasTrackingParams = true;
      }
    });

    // If we found tracking params, clean the URL
    if (hasTrackingParams) {
      const cleanUrl =
        url.pathname + (params.toString() ? "?" + params.toString() : "");
      window.history.replaceState({}, "", cleanUrl);

      console.log("Tracking parameters:", this.trackingParams);
    }
  }

  /**
   * Get stored tracking parameters
   */
  getTrackingParams() {
    return { ...this.trackingParams };
  }

  /**
   * Track pageview with Plausible including UTM parameters
   */
  trackPageView(path) {
    // Build URL with UTM parameters for Plausible to detect campaign sources
    let url = path || window.location.pathname;

    if (Object.keys(this.trackingParams).length > 0) {
      const params = new URLSearchParams(this.trackingParams);
      url = `${url}?${params.toString()}`;
    }

    // Plausible requires full URL including domain
    const fullUrl = window.location.origin + url;

    const eventData = {
      url: fullUrl,
      // Also include as props for custom properties tracking
      props:
        Object.keys(this.trackingParams).length > 0
          ? this.trackingParams
          : undefined,
    };

    console.log("📊 Plausible pageview:", eventData);
    plausibleTrack("pageview", eventData);
  }

  /**
   * Track custom event with Plausible
   */
  trackEvent(eventName, customProps = {}) {
    const props = {
      ...this.trackingParams,
      ...customProps,
    };

    // Build URL with UTM parameters for Plausible to detect campaign sources
    let url = window.location.pathname;
    if (Object.keys(this.trackingParams).length > 0) {
      const params = new URLSearchParams(this.trackingParams);
      url = `${url}?${params.toString()}`;
    }

    // Plausible requires full URL including domain
    const fullUrl = window.location.origin + url;

    const eventData = {
      url: fullUrl,
      props: Object.keys(props).length > 0 ? props : undefined,
    };

    console.log(`📊 Plausible event "${eventName}":`, eventData);
    plausibleTrack(eventName, eventData);
  }

  /**
   * Determine if a URL is internal to this application
   */
  isInternalLink(url) {
    try {
      const link = new URL(url, window.location.origin);
      return link.origin === window.location.origin;
    } catch (e) {
      return false;
    }
  }

  /**
   * Handle click events on links
   */
  handleLinkClick(e) {
    // Find the closest <a> tag
    const link = e.target.closest("a");

    if (!link) return;

    // Respect browser-native behaviors like opening in a new tab/window.
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    ) {
      return;
    }

    if (link.hasAttribute("download")) {
      return;
    }

    const target = (link.getAttribute("target") || "").toLowerCase();
    if (target && target !== "_self") {
      return;
    }

    const href = link.getAttribute("href");

    // Handle hash links (anchor links on same page)
    if (href && href.startsWith("#")) {
      e.preventDefault();
      const targetId = href.substring(1);
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
        // Update URL hash
        window.history.replaceState(null, "", href);
      }
      return;
    }

    // Ignore if no href or special links
    if (!href || href.startsWith("javascript:")) {
      return;
    }

    // Check if it's an internal link
    if (this.isInternalLink(href)) {
      e.preventDefault();

      const url = new URL(href, window.location.origin);
      const path = url.pathname;

      // Don't reload if we're already on this page
      if (path === this.currentPath) {
        return;
      }

      this.loadPage(path, true);
    }
    // External links will open normally (default behavior)
  }

  /**
   * Handle browser back/forward button clicks
   */
  handlePopState(e) {
    const path = window.location.pathname;

    this.loadPage(path, false);
  }

  /**
   * Load a page and update the UI
   */
  async loadPage(path, pushState = true) {
    try {
      // Show loading state (optional - can be enhanced later)
      this.showLoading();

      await this.refreshCurrentUser();
      this.updateAuthUI();

      // Separate pathname and query string
      const [pathname, queryString] = path.includes("?")
        ? path.split("?")
        : [path, ""];
      const fullPath = queryString ? `${pathname}?${queryString}` : pathname;

      // Get page content (from cache or fetch) - use pathname only
      const content = await this.getPageContent(pathname);

      // Extract main content if this is a full HTML document
      let contentToInsert = content;
      if (content.includes("<!DOCTYPE html>") || content.includes("<html")) {
        // Parse the HTML and extract just the main content
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, "text/html");
        const main = doc.querySelector("main");
        if (main) {
          contentToInsert = main.innerHTML;
        }
      }

      // Update the main content container
      this.contentContainer.innerHTML = contentToInsert;

      // Update page title
      let pageTitle = PAGE_TITLES[pathname];

      // For articles, extract title from h1
      if (pathname.startsWith("/articles/") && pathname !== "/articles") {
        const h1 = this.contentContainer.querySelector("h1");
        if (h1) {
          pageTitle = `${h1.textContent} - Counterpunch`;
        }
      }

      // For documentation pages, extract title from h1
      if (
        pathname.startsWith("/documentation/") ||
        pathname === "/documentation"
      ) {
        const h1 = this.contentContainer.querySelector("h1");
        if (h1) {
          pageTitle = `${h1.textContent} - Counterpunch Documentation`;
        }
      }

      if (!pageTitle) {
        pageTitle = PAGE_TITLES["/"];
      }

      document.title = pageTitle;

      // Update browser URL and history - use full path with query string
      if (pushState) {
        window.history.pushState({ path: fullPath }, "", fullPath);
      }

      // Update current path to pathname only
      this.currentPath = pathname;

      // /documentation is the first handbook page — canonicalize the URL
      if (pathname === "/documentation") {
        const currentDoc = this.contentContainer.querySelector(
          ".article-toc a.is-current",
        );
        const canonical = currentDoc?.getAttribute("href");
        if (canonical && canonical !== "/documentation") {
          window.history.replaceState({ path: canonical }, "", canonical);
          this.currentPath = canonical;
        }
      }

      // Initialize page-specific functionality
      this.initializePageHandlers(this.currentPath);

      // Scroll to top or hash target
      if (window.location.hash) {
        // If there's a hash, scroll to it after a brief delay to ensure layout is ready
        requestAnimationFrame(() => {
          setTimeout(() => {
            const target = document.getElementById(
              window.location.hash.substring(1),
            );
            if (target) {
              target.scrollIntoView({ behavior: "smooth" });
            }
          }, 50);
        });
      } else {
        window.scrollTo(0, 0);
      }

      // Track pageview with Plausible including UTM parameters
      console.log("📍 About to track pageview for path:", path);
      this.trackPageView(path);

      // Hide loading state
      this.hideLoading();
    } catch (error) {
      console.error("Error loading page:", error);
      this.showError(error);
    }
  }

  /**
   * Get page content from cache or fetch it
   */
  async getPageContent(path) {
    // Check cache first
    if (this.pageCache.has(path)) {
      return this.pageCache.get(path);
    }

    // Determine the file path for the content
    let contentPath = path;

    // Convert root path to home
    if (path === "/" || path === "") {
      contentPath = "/pages/home";
    }
    // Handle articles overview page
    else if (path === "/articles") {
      contentPath = "/pages/articles";
    }
    // Handle individual article pages - load as SPA content
    else if (path.startsWith("/articles/")) {
      // Extract slug from path like /articles/slug-name/ or /articles/slug-name
      const slug = path.split("/").filter(Boolean)[1];
      contentPath = `/pages/article-${slug}`;
      console.log("📄 Article page - slug:", slug, "contentPath:", contentPath);
    }
    // Handle other pages
    else if (!path.includes(".")) {
      // If no extension, assume .html and look in pages directory
      contentPath = `/pages${path}`;
    }

    // Fetch the content (Wrangler Pages handles .html extension automatically)
    const response = await fetch(contentPath);

    if (!response.ok) {
      throw new Error(
        `Failed to load page: ${response.status} ${response.statusText}`,
      );
    }

    const content = await response.text();

    // Cache the content
    this.pageCache.set(path, content);

    return content;
  }

  /**
   * Show loading indicator
   */
  showLoading() {
    this.contentContainer.style.opacity = "0.5";
  }

  /**
   * Hide loading indicator
   */
  hideLoading() {
    this.contentContainer.style.opacity = "1";
  }

  /**
   * Show error message
   */
  showError(error) {
    this.contentContainer.innerHTML = `
      <div class="content-container error-message">
        <h1>Oops!</h1>
        <p>Failed to load page: ${error.message}</p>
        <p><a href="/">Return to home</a></p>
      </div>
    `;
    this.hideLoading();
  }

  /**
   * Clear the page cache (can be used for forced refresh)
   */
  clearCache() {
    this.pageCache.clear();
  }

  /**
   * Clear cache for a specific page
   */
  clearPageCache(path) {
    this.pageCache.delete(path);
  }

  /**
   * Refresh the user associated with the HttpOnly website session cookie.
   */
  async refreshCurrentUser() {
    try {
      const response = await fetch("/api/auth/me", {
        credentials: "include",
      });
      if (!response.ok) {
        this.currentUser = null;
        return null;
      }

      const data = await response.json();
      this.currentUser = data.authenticated
        ? { ...data.user, userId: data.user.id }
        : null;
      return this.currentUser;
    } catch (error) {
      console.error("Failed to refresh website authentication:", error);
      this.currentUser = null;
      return null;
    }
  }

  /**
   * Return the server-verified user cached from the HttpOnly session cookie.
   */
  getSessionUser() {
    return this.currentUser;
  }

  /**
   * Update the auth UI in the title bar
   */
  updateAuthUI() {
    const authSection = document.getElementById("authSection");
    if (!authSection) return;

    const user = this.currentUser;

    if (user) {
      // User is logged in
      authSection.innerHTML = `
        <div class="user-menu">
          <a href="/account" class="user-account-link">
            <span class="material-symbols-outlined">account_circle</span>
            <span>${user.email}</span>
          </a>
          <button class="button-base auth-button" onclick="window.appNavigator.logout()">Log Out</button>
        </div>
      `;

      this.updateAdminDashboardLink();
    } else {
      // User is not logged in
      authSection.innerHTML = `
        <a href="/login" class="button-base auth-button">
          <span class="material-symbols-outlined">account_circle</span>
          <span>Sign In</span>
        </a>
      `;

      this.isAdminUser = false;
      this.updateAdminDashboardLink();
    }

    // Re-sync footer widths after auth UI changes (header right width may have changed)
    requestAnimationFrame(() => {
      setTimeout(() => this.syncFooterWidths(), 50);
    });
  }

  getAdminNavLink() {
    const nav = document.querySelector(".title-bar-nav");
    if (!nav) return null;

    let link = document.getElementById("adminDashboardNavLink");
    if (!link) {
      link = document.createElement("a");
      link.id = "adminDashboardNavLink";
      link.href = "/admin/dashboard";
      link.textContent = "Dashboard";
      link.style.display = "none";
      nav.appendChild(link);
    }

    return link;
  }

  async updateAdminDashboardLink() {
    const link = this.getAdminNavLink();
    if (!link) return;

    if (!this.currentUser) {
      link.style.display = "none";
      this.isAdminUser = false;
      this.renderPricingServerCosts();
      return;
    }

    try {
      const response = await fetch("/api/admin/status", {
        credentials: "include",
      });

      if (!response.ok) {
        link.style.display = "none";
        this.isAdminUser = false;
        this.renderPricingServerCosts();
        return;
      }

      const data = await response.json();
      this.isAdminUser = !!data.isAdmin;
      link.style.display = this.isAdminUser ? "" : "none";
      this.renderPricingServerCosts();
    } catch (error) {
      console.error("Failed to resolve admin status:", error);
      link.style.display = "none";
      this.isAdminUser = false;
      this.renderPricingServerCosts();
    }
  }

  renderPricingServerCosts() {
    document.querySelectorAll("[data-server-cost]").forEach((block) => {
      block.hidden = true;
    });
    const table = document.querySelector("[data-cost-table]");
    if (!table) return;
    const show = !!this.isAdminUser;
    table.hidden = !show;
    if (!show) return;
    const countries = this.pricingCostCountries;
    if (!countries) return;

    const products = Object.values(getPricingProducts());
    const marginText = (margin) => {
      if (margin == null) return "—";
      const pct = margin * 100;
      const rounded =
        Math.abs(pct) < 10 ? pct.toFixed(1) : String(Math.round(pct));
      return `${rounded}%`;
    };
    const cellsFor = (monthlyExclusive, product) =>
      cloudflareCostLines(product, monthlyExclusive)
        .map((line) => {
          const band = marginBand(line.margin);
          const klass = band ? ` class="pricing-margin-${band}"` : "";
          return `<td${klass}>€${line.costEur.toFixed(0)}<br>${marginText(line.margin)}</td>`;
        })
        .join("");
    const headerPlans = products
      .map((product) => `<th colspan="3">${this.escapeHtml(product.name)}</th>`)
      .join("");
    const headerBands = products
      .map(() => "<th>Light</th><th>Medium</th><th>Heavy</th>")
      .join("");
    const rows = countries
      .map((country) => {
        const cells = products
          .map((product) => {
            const base = Number(product.pricing?.monthly?.base) || 0;
            return cellsFor(calculatePrice(base, country.multiplier), product);
          })
          .join("");
        return `<tr><th scope="row">${this.escapeHtml(country.name)}</th>${cells}</tr>`;
      })
      .join("");
    const missing = this.pricingMissingCountries || [];
    const missingRows = missing
      .map(
        (country) =>
          `<li>${this.escapeHtml(country.name)} <span>${this.escapeHtml(country.code)}</span> — ${this.escapeHtml(country.reason)}</li>`,
      )
      .join("");
    const germany = countries.find((country) => country.code === "DEU");
    table.innerHTML = `${pricingProgressionMarkup(products, germany?.multiplier)}${yearlyIntro}<div class="pricing-cost-scroll"><table class="pricing-cost-table"><thead><tr><th rowspan="2">Country</th>${headerPlans}</tr><tr>${headerBands}</tr></thead><tbody>${rows}</tbody></table></div><h2>No PPP definition</h2><p>${missing.length} countries in the pricing list have no usable PPP figure.</p><ul class="pricing-missing-countries">${missingRows}</ul>`;
  }

  /**
   * Log out the current user
   */
  async logout() {
    try {
      const response = await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
        headers: JSON_POST_HEADERS,
        body: "{}",
      });
      if (!response.ok) {
        throw new Error(`logout failed: ${response.status}`);
      }
    } catch (error) {
      console.error("Failed to log out:", error);
      return;
    }

    this.currentUser = null;

    // Track logout event
    this.trackEvent("logout");

    // Update UI
    this.updateAuthUI();

    // Reload current page
    await this.loadPage(this.currentPath, false);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return this.currentUser !== null;
  }

  /**
   * Redirect to login with return path
   */
  redirectToLogin() {
    const returnTo = encodeURIComponent(this.currentPath);
    this.loadPage(`/login?returnTo=${returnTo}`, true);
  }

  redirectToLoginPath(returnToPath) {
    const returnTo = encodeURIComponent(returnToPath);
    this.loadPage(`/login?returnTo=${returnTo}`, true);
  }

  /**
   * Show authentication error message
   */
  authErrorMessage(errorCode) {
    const errorMessages = {
      invalid_token: "This sign-in link isn't valid. Sign in with a code instead.",
      expired_token: "This sign-in link has expired. Sign in with a code instead.",
      user_not_found: "User not found. Please try again.",
      verification_failed: "Verification failed. Please try again.",
    };
    return (
      errorMessages[errorCode] || errorCode || "An authentication error occurred."
    );
  }

  showAuthError(errorCode) {
    const message = this.authErrorMessage(errorCode);

    // Create a temporary notification
    const notification = document.createElement("div");
    notification.style.cssText = `
      position: fixed;
      top: 60px;
      right: 20px;
      background: rgba(255, 68, 68, 0.95);
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      z-index: 10000;
      font-family: var(--font-families-sans);
      font-size: 14px;
      max-width: 400px;
    `;
    notification.textContent = `✗ ${message}`;

    document.body.appendChild(notification);

    // Remove after 5 seconds
    setTimeout(() => {
      notification.style.opacity = "0";
      notification.style.transition = "opacity 0.3s ease";
      setTimeout(() => notification.remove(), 300);
    }, 5000);
  }

  /**
   * Initialize page-specific handlers after content is loaded
   */
  initializePageHandlers(path) {
    // Normalize path by removing trailing slash
    const normalizedPath =
      path.endsWith("/") && path !== "/" ? path.slice(0, -1) : path;

    // Handle article pages (in-page Contents) and documentation (handbook Contents)
    if (normalizedPath.startsWith("/articles/")) {
      requestAnimationFrame(() => {
        this.initializeArticleTOC();
      });
    } else if (
      normalizedPath.startsWith("/documentation/") ||
      normalizedPath === "/documentation"
    ) {
      requestAnimationFrame(() => {
        this.refreshDocumentationScreenshots();
        this.scrollDocumentationTocIntoView();
      });
    }
    // Handle login page
    else if (normalizedPath === "/login") {
      // If user is already logged in, redirect them back immediately
      if (this.currentUser) {
        const params = new URLSearchParams(window.location.search);
        const returnTo = params.get("returnTo");
        const messageContainer = document.getElementById("messageContainer");
        const loginForm = document.getElementById("loginForm");

        // Hide the login form
        if (loginForm) {
          loginForm.style.display = "none";
        }

        // Show message
        if (messageContainer) {
          messageContainer.innerHTML = `<div class="message success">✓ You are already logged in. Redirecting in 5 seconds...</div>`;
        }

        // Redirect after 5 seconds
        setTimeout(() => {
          if (returnTo) {
            window.location.href = returnTo;
          } else {
            this.loadPage("/account", true);
          }
        }, 5000);
        return;
      }
      this.initializeLoginForm();
    }
    // Handle invite page
    else if (normalizedPath === "/invite") {
      this.initializeInvitePage();
    }
    // Handle transfer page
    else if (normalizedPath === "/transfer") {
      this.initializeTransferPage();
    }
    // Handle account page
    else if (normalizedPath === "/account") {
      this.initializeAccountPage();
    }
    // Handle admin dashboard page
    else if (normalizedPath === "/admin/dashboard") {
      this.initializeAdminDashboardPage();
    }
    // Handle admin errors page
    else if (normalizedPath === "/admin/errors") {
      this.initializeAdminErrorsPage();
    }
    // Handle pricing page
    else if (normalizedPath === "/pricing") {
      this.initializePricingPage();
    }
    // Handle home page — load editor version info
    else if (normalizedPath === "/") {
      this.loadEditorVersions();
    }
  }

  async fetchAdminStatus() {
    const statusResponse = await fetch("/api/admin/status", {
      credentials: "include",
    });
    return statusResponse.ok ? await statusResponse.json() : null;
  }

  getAdminAppChannel() {
    const stored = localStorage.getItem("admin_app_channel");
    if (stored === "preview" || stored === "production") {
      return stored;
    }

    return "production";
  }

  setAdminAppChannel(channel) {
    if (channel !== "preview" && channel !== "production") {
      return;
    }

    localStorage.setItem("admin_app_channel", channel);
  }

  renderAdminAppChannelSwitch(mountId, onChange) {
    const mount = document.getElementById(mountId);
    if (!mount) return;

    const selectedChannel = this.getAdminAppChannel();
    mount.innerHTML = `
      <div class="admin-channel-switch" role="group" aria-label="App channel filter">
        <span class="admin-channel-switch-label">App:</span>
        <button type="button" class="admin-channel-button ${selectedChannel === "production" ? "is-active" : ""}" data-channel="production">Production</button>
        <button type="button" class="admin-channel-button ${selectedChannel === "preview" ? "is-active" : ""}" data-channel="preview">Preview</button>
      </div>
    `;

    const buttons = Array.from(mount.querySelectorAll(".admin-channel-button"));
    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        const nextChannel = button.getAttribute("data-channel");
        if (!nextChannel || nextChannel === this.getAdminAppChannel()) {
          return;
        }

        this.setAdminAppChannel(nextChannel);
        if (onChange) {
          onChange(nextChannel);
        }
      });
    });
  }

  async fetchAdminDashboardOverview(
    days = 60,
    channel = this.getAdminAppChannel(),
  ) {
    const query = new URLSearchParams({
      days: String(days),
      channel,
    });

    const response = await fetch(
      `/api/admin/dashboard/overview?${query.toString()}`,
      {
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to load admin dashboard overview");
    }

    return await response.json();
  }

  renderAdminRoomHealth(roomHealth) {
    const roomHealthEl = document.getElementById("adminDashboardRoomHealth");
    if (!roomHealthEl) return;

    const totals = roomHealth?.totals || {};
    const staleRooms = roomHealth?.staleRooms || [];
    const integrity = roomHealth?.integrity || {};
    const oldestEdit = totals.oldestUncheckpointedEditAt
      ? new Date(totals.oldestUncheckpointedEditAt).toLocaleString()
      : "None";

    roomHealthEl.innerHTML = `
      <div class="admin-errors-summary-grid admin-room-health-grid">
        <div class="admin-errors-metric">
          <div class="admin-errors-metric-label">Rooms</div>
          <div class="admin-errors-metric-value">${Number(totals.totalRooms) || 0}</div>
        </div>
        <div class="admin-errors-metric">
          <div class="admin-errors-metric-label">Pending / failed bootstrap</div>
          <div class="admin-errors-metric-value">${Number(totals.pendingBootstrapRooms) || 0} / ${Number(totals.bootstrapFailedRooms) || 0}</div>
        </div>
        <div class="admin-errors-metric">
          <div class="admin-errors-metric-label">Pending revocations</div>
          <div class="admin-errors-metric-value">${Number(integrity.pendingRevocations) || 0}</div>
        </div>
        <div class="admin-errors-metric">
          <div class="admin-errors-metric-label">Generations / attestations</div>
          <div class="admin-errors-metric-value">${Number(integrity.publishedGenerations) || 0} / ${Number(integrity.attestedShards) || 0}</div>
        </div>
        <div class="admin-errors-metric">
          <div class="admin-errors-metric-label">Seeding assets</div>
          <div class="admin-errors-metric-value">${Number(integrity.seedingAssets) || 0}</div>
        </div>
        <div class="admin-errors-metric">
          <div class="admin-errors-metric-label">Highest room version</div>
          <div class="admin-errors-metric-value">${Number(totals.highestRoomVersion) || 0}</div>
        </div>
        <div class="admin-errors-metric admin-room-health-wide">
          <div class="admin-errors-metric-label">Oldest uncheckpointed edit</div>
          <div class="admin-errors-metric-value admin-room-health-date">${this.escapeHtml(oldestEdit)}</div>
        </div>
      </div>
      <div class="admin-room-health-table-wrap">
        <table class="admin-room-health-table">
          <thead>
            <tr>
              <th>Room</th>
              <th>State</th>
              <th>Version</th>
              <th>Glyphs</th>
              <th>Updated</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            ${staleRooms
              .map((room) => {
                const updatedAt = room.updatedAt
                  ? new Date(room.updatedAt).toLocaleString()
                  : "Unknown";
                return `
                  <tr>
                    <td>
                      <div>${this.escapeHtml(room.name || room.id || "Unknown")}</div>
                      <div class="admin-room-health-id">${this.escapeHtml(room.id || "")}</div>
                    </td>
                    <td>${this.escapeHtml(room.lifecycleState || "unknown")}</td>
                    <td>${Number(room.currentRoomVersion) || 0}</td>
                    <td>${Number(room.glyphCount) || 0}</td>
                    <td>${this.escapeHtml(updatedAt)}</td>
                    <td>
                      <button type="button" class="admin-room-health-action" data-room-compact-id="${this.escapeHtml(room.id || "")}">Request compaction</button>
                    </td>
                  </tr>
                `;
              })
              .join("")}
          </tbody>
        </table>
        ${staleRooms.length === 0 ? '<p class="admin-room-health-empty">No active rooms with pending versions.</p>' : ""}
      </div>
      <h3>Fat-compactor / tail_full queue</h3>
      <div class="admin-room-health-table-wrap">
        <table class="admin-room-health-table">
          <thead>
            <tr>
              <th>Asset</th>
              <th>Shard</th>
              <th>Status</th>
              <th>Checkpoint</th>
              <th>Dirty</th>
              <th>Marked</th>
            </tr>
          </thead>
          <tbody>
            ${(roomHealth?.shardOps || [])
              .map((row) => {
                const markedAt = row.markedAt
                  ? new Date(row.markedAt).toLocaleString()
                  : "Unknown";
                return `
                  <tr>
                    <td>${this.escapeHtml(row.assetId || "")}</td>
                    <td>${this.escapeHtml(row.shardId || "")}</td>
                    <td>${this.escapeHtml(row.status || "")}</td>
                    <td>${Number(row.checkpointBytes) || 0}</td>
                    <td>${Number(row.dirtyBytes) || 0}</td>
                    <td>${this.escapeHtml(markedAt)}</td>
                  </tr>
                `;
              })
              .join("")}
          </tbody>
        </table>
        ${(roomHealth?.shardOps || []).length === 0 ? '<p class="admin-room-health-empty">No shards waiting for fat compaction.</p>' : ""}
      </div>
    `;

    Array.from(roomHealthEl.querySelectorAll("[data-room-compact-id]")).forEach(
      (button) => {
        button.addEventListener("click", async () => {
          const roomId = button.getAttribute("data-room-compact-id");
          if (!roomId) return;

          button.disabled = true;
          button.textContent = "Requesting...";

          try {
            const response = await fetch(
              `/api/admin/cloud/rooms/${encodeURIComponent(roomId)}/compact`,
              {
                method: "POST",
                credentials: "include",
                headers: JSON_POST_HEADERS,
                body: "{}",
              },
            );
            const payload = await response.json().catch(() => null);
            if (!response.ok) {
              throw new Error(payload?.error || "Compaction request failed");
            }
            button.textContent = payload?.compacted ? "Compacted" : "No tail";
          } catch (error) {
            console.error("Room compaction request failed:", error);
            button.disabled = false;
            button.textContent = "Retry compaction";
          }
        });
      },
    );
  }

  async fetchAdminMembershipLookup(query) {
    const params = new URLSearchParams({ query });
    const response = await fetch(
      `/api/admin/subscription/lookup?${params.toString()}`,
      {
        credentials: "include",
      },
    );

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to look up user");
    }

    return data;
  }

  async fetchAdminMembershipOverride(payload) {
    const response = await fetch("/api/admin/subscription/override", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to update membership override");
    }

    return data;
  }

  renderAdminMembershipTools(messageMarkup = "") {
    const toolsEl = document.getElementById("adminMembershipTools");
    if (!toolsEl) {
      return;
    }

    const result = this.adminMembershipLookupResult;
    const productOptions = Object.values(this.getPricingProducts())
      .filter((product) => product.pricing?.monthly?.base > 0)
      .map((product) => {
        const isSelected =
          result?.membershipOverride?.productId === product.id ||
          (!result?.membershipOverride?.productId && product.id === "basic");
        return `<option value="${this.escapeHtml(product.id)}" ${
          isSelected ? "selected" : ""
        }>${this.escapeHtml(product.name)}</option>`;
      })
      .join("");

    const effectiveMembership = result?.effectiveMembership;
    const stripeSubscription = result?.stripeSubscription;
    const membershipOverride = result?.membershipOverride;
    const effectiveLabel = effectiveMembership
      ? `${this.escapeHtml(effectiveMembership.planName)} (${this.escapeHtml(
          effectiveMembership.source,
        )})`
      : "No active membership";
    const effectiveEndsAt = effectiveMembership?.endsAt
      ? new Date(effectiveMembership.endsAt * 1000).toLocaleDateString()
      : null;
    const overrideEndsAt = membershipOverride?.endsAt
      ? new Date(membershipOverride.endsAt * 1000).toLocaleDateString()
      : "No end date";
    const defaultEndsAtValue = membershipOverride?.endsAt
      ? new Date(membershipOverride.endsAt * 1000).toISOString().slice(0, 10)
      : "";

    toolsEl.innerHTML = `
      <form id="adminMembershipLookupForm" style="display: grid; gap: 10px; margin-bottom: 16px;">
        <label for="adminMembershipLookupQuery">User ID or Email</label>
        <div style="display: flex; gap: 8px; flex-wrap: wrap; align-items: center;">
          <input
            id="adminMembershipLookupQuery"
            name="query"
            type="text"
            placeholder="user_123 or person@example.com"
            value="${this.escapeHtml(result?.user?.email || result?.user?.id || "")}"
            style="flex: 1 1 280px;"
            required
          />
          <button type="submit" class="button-base auth-button">Look Up User</button>
        </div>
      </form>
      <div id="adminMembershipMessage">${messageMarkup}</div>
      ${
        result
          ? `
        <div class="admin-membership-result" style="display: grid; gap: 16px;">
          <div>
            <div><strong>User:</strong> ${this.escapeHtml(result.user.email)}</div>
            <div><strong>User ID:</strong> ${this.escapeHtml(result.user.id)}</div>
            <div><strong>Effective membership:</strong> ${effectiveLabel}</div>
            ${
              effectiveEndsAt
                ? `<div><strong>Access until:</strong> ${this.escapeHtml(effectiveEndsAt)}</div>`
                : ""
            }
          </div>
          <div>
            <div><strong>Stripe subscription:</strong> ${
              stripeSubscription
                ? `${this.escapeHtml(stripeSubscription.planName)} (${this.escapeHtml(
                    stripeSubscription.status,
                  )})`
                : "None"
            }</div>
            <div><strong>Membership override:</strong> ${
              membershipOverride
                ? `${this.escapeHtml(membershipOverride.productId)} through ${this.escapeHtml(
                    overrideEndsAt,
                  )}`
                : "None"
            }</div>
            ${
              membershipOverride?.note
                ? `<div><strong>Note:</strong> ${this.escapeHtml(membershipOverride.note)}</div>`
                : ""
            }
          </div>
          <form id="adminMembershipOverrideForm" style="display: grid; gap: 10px;">
            <input type="hidden" name="userQuery" value="${this.escapeHtml(result.user.id)}" />
            <label for="adminMembershipProduct">Membership</label>
            <select id="adminMembershipProduct" name="productId">${productOptions}</select>
            <label for="adminMembershipEndsAt">End date</label>
            <input id="adminMembershipEndsAt" name="endsAt" type="date" value="${this.escapeHtml(defaultEndsAtValue)}" />
            <label for="adminMembershipNote">Admin note</label>
            <textarea id="adminMembershipNote" name="note" rows="3" placeholder="Optional note">${this.escapeHtml(
              membershipOverride?.note || "",
            )}</textarea>
            <div style="display: flex; gap: 8px; flex-wrap: wrap; align-items: center;">
              <button type="submit" class="button-base auth-button">Save Override</button>
              <button type="button" class="button-base subscription-button danger" id="adminMembershipRemoveButton" ${
                membershipOverride ? "" : "disabled"
              }>Remove Override</button>
            </div>
          </form>
        </div>
      `
          : '<p style="color: var(--text-secondary); margin: 0;">Look up a user to inspect and manage an override.</p>'
      }
    `;

    const lookupForm = document.getElementById("adminMembershipLookupForm");
    if (lookupForm) {
      lookupForm.addEventListener("submit", async (event) => {
        event.preventDefault();
        const formData = new FormData(lookupForm);
        const query = String(formData.get("query") || "").trim();
        const messageEl = document.getElementById("adminMembershipMessage");
        if (!query) {
          return;
        }

        if (messageEl) {
          messageEl.innerHTML =
            '<div class="message info">Looking up user...</div>';
        }

        try {
          this.adminMembershipLookupResult =
            await this.fetchAdminMembershipLookup(query);
          this.renderAdminMembershipTools(
            '<div class="message success">User loaded.</div>',
          );
        } catch (error) {
          this.renderAdminMembershipTools(
            `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          );
        }
      });
    }

    const overrideForm = document.getElementById("adminMembershipOverrideForm");
    if (overrideForm) {
      overrideForm.addEventListener("submit", async (event) => {
        event.preventDefault();
        const formData = new FormData(overrideForm);

        try {
          await this.fetchAdminMembershipOverride({
            action: "upsert",
            userQuery: String(formData.get("userQuery") || "").trim(),
            productId: String(formData.get("productId") || "").trim(),
            endsAt: String(formData.get("endsAt") || "").trim(),
            note: String(formData.get("note") || "").trim(),
          });
          this.adminMembershipLookupResult =
            await this.fetchAdminMembershipLookup(
              String(formData.get("userQuery") || "").trim(),
            );
          this.renderAdminMembershipTools(
            '<div class="message success">Override saved.</div>',
          );
        } catch (error) {
          this.renderAdminMembershipTools(
            `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          );
        }
      });
    }

    const removeButton = document.getElementById("adminMembershipRemoveButton");
    if (removeButton) {
      removeButton.addEventListener("click", async () => {
        if (!result?.user?.id) {
          return;
        }

        try {
          await this.fetchAdminMembershipOverride({
            action: "remove",
            userQuery: result.user.id,
          });
          this.adminMembershipLookupResult =
            await this.fetchAdminMembershipLookup(result.user.id);
          this.renderAdminMembershipTools(
            '<div class="message success">Override removed.</div>',
          );
        } catch (error) {
          this.renderAdminMembershipTools(
            `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          );
        }
      });
    }
  }

  async fetchAdminCloudOverride({ action, userQuery, note }) {
    const response = await fetch("/api/admin/cloud/override", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, userQuery, note }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to update cloud override");
    }
    return data;
  }

  renderAdminCloudTools(messageMarkup = "") {
    const toolsEl = document.getElementById("adminCloudTools");
    if (!toolsEl) return;

    const result = this.adminCloudLookupResult;

    toolsEl.innerHTML = `
      <form id="adminCloudLookupForm" style="display: grid; gap: 10px; margin-bottom: 16px;">
        <label for="adminCloudLookupQuery">User ID or Email</label>
        <div style="display: flex; gap: 8px; flex-wrap: wrap; align-items: center;">
          <input
            id="adminCloudLookupQuery"
            name="query"
            type="text"
            placeholder="user_123 or person@example.com"
            value="${this.escapeHtml(result?.user?.email || result?.user?.id || "")}"
            style="flex: 1 1 280px;"
            required
          />
          <button type="submit" class="button-base auth-button">Look Up User</button>
        </div>
      </form>
      <div id="adminCloudMessage">${messageMarkup}</div>
      ${
        result
          ? `
        <div class="admin-membership-result" style="display: grid; gap: 16px;">
          <div>
            <div><strong>User:</strong> ${this.escapeHtml(result.user.email)}</div>
            <div><strong>User ID:</strong> ${this.escapeHtml(result.user.id)}</div>
            <div><strong>Cloud hosting:</strong> ${result.cloudHostingEnabled ? "✓ Enabled" : "✗ Disabled"}</div>
            ${result.override?.note ? `<div><strong>Note:</strong> ${this.escapeHtml(result.override.note)}</div>` : ""}
          </div>
          <div style="display: flex; gap: 8px; flex-wrap: wrap;">
            <form id="adminCloudGrantForm" style="display: contents;">
              <input type="hidden" name="userQuery" value="${this.escapeHtml(result.user.id)}" />
              <label for="adminCloudNote" style="align-self: center;">Note</label>
              <input id="adminCloudNote" name="note" type="text" placeholder="Optional note" value="${this.escapeHtml(result.override?.note || "")}" style="flex: 1 1 180px;" />
              <button type="submit" class="button-base auth-button">Grant Access</button>
              <button type="button" class="button-base subscription-button danger" id="adminCloudRevokeButton" ${result.cloudHostingEnabled ? "" : "disabled"}>Revoke Access</button>
            </form>
          </div>
        </div>
      `
          : '<p style="color: var(--text-secondary); margin: 0;">Look up a user to grant or revoke cloud hosting access.</p>'
      }
    `;

    const lookupForm = document.getElementById("adminCloudLookupForm");
    if (lookupForm) {
      lookupForm.addEventListener("submit", async (event) => {
        event.preventDefault();
        const query = String(
          new FormData(lookupForm).get("query") || "",
        ).trim();
        const msgEl = document.getElementById("adminCloudMessage");
        if (!query) return;
        if (msgEl)
          msgEl.innerHTML =
            '<div class="message info">Looking up user...</div>';
        try {
          // Reuse membership lookup to get user info, then check eligibility
          const lookup = await this.fetchAdminMembershipLookup(query);
          // Fetch current cloud eligibility for this user by granting then immediately reading
          // Use the override endpoint with action=grant to probe — no, instead fetch eligibility
          const eligResp = await fetch(
            `/api/cloud/eligibility?userId=${encodeURIComponent(lookup.user.id)}`,
            { credentials: "include" },
          );
          const elig = eligResp.ok
            ? await eligResp.json()
            : { cloudHostingEnabled: false };
          this.adminCloudLookupResult = {
            user: lookup.user,
            cloudHostingEnabled: elig.cloudHostingEnabled,
            override: null,
          };
          this.renderAdminCloudTools(
            '<div class="message success">User loaded.</div>',
          );
        } catch (error) {
          this.renderAdminCloudTools(
            `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          );
        }
      });
    }

    const grantForm = document.getElementById("adminCloudGrantForm");
    if (grantForm) {
      grantForm.addEventListener("submit", async (event) => {
        event.preventDefault();
        const fd = new FormData(grantForm);
        const userQuery = String(fd.get("userQuery") || "").trim();
        const note = String(fd.get("note") || "").trim();
        try {
          const res = await this.fetchAdminCloudOverride({
            action: "grant",
            userQuery,
            note,
          });
          this.adminCloudLookupResult = {
            user: result.user,
            cloudHostingEnabled: res.cloudHostingEnabled,
            override: { note },
          };
          this.renderAdminCloudTools(
            '<div class="message success">Cloud access granted.</div>',
          );
        } catch (error) {
          this.renderAdminCloudTools(
            `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          );
        }
      });
    }

    const revokeButton = document.getElementById("adminCloudRevokeButton");
    if (revokeButton) {
      revokeButton.addEventListener("click", async () => {
        if (!result?.user?.id) return;
        try {
          const res = await this.fetchAdminCloudOverride({
            action: "revoke",
            userQuery: result.user.id,
          });
          this.adminCloudLookupResult = {
            user: result.user,
            cloudHostingEnabled: res.cloudHostingEnabled,
            override: null,
          };
          this.renderAdminCloudTools(
            '<div class="message success">Cloud access revoked.</div>',
          );
        } catch (error) {
          this.renderAdminCloudTools(
            `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          );
        }
      });
    }
  }

  async fetchAdminErrorDetails(days = 60, channel = this.getAdminAppChannel()) {
    const query = new URLSearchParams({
      days: String(days),
      channel,
    });

    const response = await fetch(
      `/api/admin/errors/details?${query.toString()}`,
      {
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to load admin error details");
    }

    return await response.json();
  }

  async fetchAdminErrorEvents(
    fingerprint,
    days = 60,
    limit = 200,
    channel = this.getAdminAppChannel(),
  ) {
    const query = new URLSearchParams({
      fingerprint,
      days: String(days),
      limit: String(limit),
      channel,
    });

    const response = await fetch(
      `/api/admin/errors/events?${query.toString()}`,
      {
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to load admin error events");
    }

    return await response.json();
  }

  async fetchAdminMuteErrorSignature(
    fingerprint,
    channel = this.getAdminAppChannel(),
  ) {
    const response = await fetch("/api/admin/errors/mute", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fingerprint,
        channel,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to mute error signature");
    }

    return await response.json();
  }

  async fetchAdminUnmuteErrorSignature(
    fingerprint,
    channel = this.getAdminAppChannel(),
  ) {
    const response = await fetch("/api/admin/errors/unmute", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fingerprint,
        channel,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to unmute error signature");
    }

    return await response.json();
  }

  async fetchAdminMutedErrorSignatures(channel = this.getAdminAppChannel()) {
    const query = new URLSearchParams({
      channel,
    });

    const response = await fetch(
      `/api/admin/errors/muted?${query.toString()}`,
      {
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to load muted error signatures");
    }

    return await response.json();
  }

  buildAdminErrorHandoff(issue, eventsResponse, channel) {
    const triageInstruction =
      "Analyze this error handoff and decide whether it's an issue that we need to react on in our own code or whether this is an issue caused by for example browser plugins that users have installed that cause noise that we can safely ignore. Don't implement any noise filters, just analyze and possibly propose a fix or suggest to ignore as noise.";
    const investigationInstruction =
      "Use state-manager.ts as the primary entry point for global capture/reporting, then follow stack-mapper.ts for mapped stacks, wasm-init.ts and coi-serviceworker.js for startup failures, and ai-assistant.ts plus mcp-transport.ts for feature-specific error paths.";

    const events = Array.isArray(eventsResponse?.events)
      ? eventsResponse.events
      : [];

    const compactEvents = events.slice(0, 1).map((eventRow) => ({
      id: eventRow.id || null,
      createdAt: eventRow.createdAt || null,
      message: eventRow.errorMessage || null,
      stack: eventRow.errorStack || null,
      source: eventRow.source || null,
      url: eventRow.url || null,
      userAgent: eventRow.userAgent || null,
      userEmail: eventRow.userEmail || null,
      userId: eventRow.userId || null,
      appVersion: eventRow.appVersion || null,
      buildHash: eventRow.buildHash || null,
      sessionId: eventRow.sessionId || null,
      payload: eventRow.payload || null,
    }));

    const handoffPayload = {
      type: "counterpunch-error-handoff-v1",
      generatedAt: new Date().toISOString(),
      request:
        "Analyze this production error and propose a concrete fix with likely root cause.",
      issue: {
        fingerprint: issue?.fingerprint || eventsResponse?.fingerprint || null,
        message: issue?.message || null,
        eventCountLast60d:
          issue?.eventCount || eventsResponse?.totalEvents || 0,
        firstSeen: issue?.firstSeen || null,
        lastSeen: issue?.lastSeen || null,
        users: issue?.users || [],
        isMuted: Boolean(issue?.isMuted || eventsResponse?.isMuted),
      },
      environment: {
        appChannel: channel || eventsResponse?.channel || "production",
      },
      eventsSummary: {
        totalEvents: eventsResponse?.totalEvents || 0,
        returnedEvents: eventsResponse?.returnedEvents || events.length,
      },
      recentEvents: compactEvents,
    };

    return `${triageInstruction}\n${investigationInstruction}\nCOUNTERPUNCH_ERROR_HANDOFF::${JSON.stringify(handoffPayload)}`;
  }

  async copyTextToClipboard(value) {
    if (!value) {
      throw new Error("Nothing to copy");
    }

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "readonly");
    textarea.style.position = "fixed";
    textarea.style.top = "-1000px";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }

  async initializeAdminDashboardPage() {
    const messageEl = document.getElementById("adminDashboardMessage");
    const metricsEl = document.getElementById("adminDashboardMetrics");
    const timelineEl = document.getElementById("adminDashboardTimeline");
    const roomHealthEl = document.getElementById("adminDashboardRoomHealth");
    const linksEl = document.getElementById("adminDashboardLinks");
    const membershipToolsEl = document.getElementById("adminMembershipTools");
    const cloudToolsEl = document.getElementById("adminCloudTools");

    if (
      !messageEl ||
      !metricsEl ||
      !timelineEl ||
      !roomHealthEl ||
      !linksEl ||
      !membershipToolsEl ||
      !cloudToolsEl
    ) {
      return;
    }

    this.renderAdminAppChannelSwitch("adminDashboardChannelSwitch", () => {
      this.initializeAdminDashboardPage();
    });

    const selectedChannel = this.getAdminAppChannel();

    const user = this.getSessionUser();
    if (!user) {
      const returnTo = encodeURIComponent("/admin/dashboard");
      this.loadPage(`/login?returnTo=${returnTo}`, true);
      return;
    }

    messageEl.innerHTML =
      '<div class="message info">Loading dashboard data...</div>';
    this.renderAdminMembershipTools();
    this.renderAdminCloudTools();

    try {
      const status = await this.fetchAdminStatus();

      if (!status?.isAdmin) {
        messageEl.innerHTML =
          '<div class="message error">✗ You do not have access to this dashboard.</div>';
        metricsEl.innerHTML = "";
        timelineEl.innerHTML = "";
        roomHealthEl.innerHTML = "";
        linksEl.innerHTML = "";
        membershipToolsEl.innerHTML = "";
        cloudToolsEl.innerHTML = "";
        return;
      }

      const overview = await this.fetchAdminDashboardOverview(
        60,
        selectedChannel,
      );

      const timeline = overview.timeline || [];
      const maxCount = Math.max(1, ...timeline.map((item) => item.count || 0));

      metricsEl.innerHTML = `
        <div class="admin-errors-summary-grid">
          <div class="admin-errors-metric">
            <div class="admin-errors-metric-label">Errors (60d)</div>
            <div class="admin-errors-metric-value">${overview.totals.totalEvents}</div>
          </div>
          <div class="admin-errors-metric">
            <div class="admin-errors-metric-label">Unique error issues</div>
            <div class="admin-errors-metric-value">${overview.totals.uniqueFingerprints}</div>
          </div>
          <div class="admin-errors-metric">
            <div class="admin-errors-metric-label">Users affected</div>
            <div class="admin-errors-metric-value">${overview.totals.usersAffected}</div>
          </div>
        </div>
      `;

      timelineEl.innerHTML = `
        <div class="admin-errors-chart">
          ${timeline
            .map((entry) => {
              const height = Math.max(
                2,
                Math.round((entry.count / maxCount) * 96),
              );
              return `
                <div class="admin-errors-bar-wrap" title="${entry.day}: ${entry.count}">
                  <div class="admin-errors-bar" style="height:${height}px"></div>
                </div>
              `;
            })
            .join("")}
        </div>
        <div class="admin-errors-chart-labels">
          <span>${overview.startDate}</span>
          <span>${overview.endDate}</span>
        </div>
      `;

      this.renderAdminRoomHealth(overview.cloudRoomHealth);

      linksEl.innerHTML = `
        <p style="color: var(--text-secondary); margin: 0 0 10px 0;">
          Showing: <strong>${this.escapeHtml(selectedChannel)}</strong>
        </p>
        <a href="/admin/errors" class="button-base auth-button">Open Error Details</a>
      `;

      messageEl.innerHTML = "";
      this.renderAdminMembershipTools();
    } catch (error) {
      console.error("Admin dashboard failed:", error);
      messageEl.innerHTML =
        '<div class="message error">✗ Failed to load dashboard data. Please try again.</div>';
    }
  }

  async initializeAdminErrorsPage() {
    const messageEl = document.getElementById("adminErrorsMessage");
    const summaryEl = document.getElementById("adminErrorsSummary");
    const issuesEl = document.getElementById("adminErrorsIssues");
    const mutedEl = document.getElementById("adminMutedSignatures");
    const eventsEl = document.getElementById("adminErrorEvents");

    if (!messageEl || !summaryEl || !issuesEl || !mutedEl || !eventsEl) {
      return;
    }

    this.renderAdminAppChannelSwitch("adminErrorsChannelSwitch", () => {
      this.initializeAdminErrorsPage();
    });

    const selectedChannel = this.getAdminAppChannel();
    const params = new URLSearchParams(window.location.search);
    const requestedFingerprint = (params.get("fingerprint") || "").trim();
    const isFocusedIssueView = Boolean(requestedFingerprint);

    const applyFocusedIssueLayout = () => {
      const introEl = document.getElementById("adminErrorsIntro");
      const backLinkEl = document.getElementById("adminErrorsBackLink");
      const channelSwitchEl = document.getElementById(
        "adminErrorsChannelSwitch",
      );

      if (introEl) {
        introEl.style.display = "none";
      }
      if (backLinkEl) {
        backLinkEl.style.display = "none";
      }
      if (channelSwitchEl) {
        channelSwitchEl.style.display = "none";
      }

      const accountSections = Array.from(
        document.querySelectorAll(".account-layout .account-section"),
      );
      accountSections.forEach((section) => {
        section.classList.add("admin-errors-hidden-for-focus");
      });

      const eventsSection = eventsEl.closest(".account-section");
      if (eventsSection) {
        eventsSection.classList.remove("admin-errors-hidden-for-focus");
      }
    };

    const user = this.getSessionUser();
    if (!user) {
      const returnTo = encodeURIComponent("/admin/errors");
      this.loadPage(`/login?returnTo=${returnTo}`, true);
      return;
    }

    messageEl.innerHTML =
      '<div class="message info">Loading error details...</div>';

    try {
      const status = await this.fetchAdminStatus();

      if (!status?.isAdmin) {
        messageEl.innerHTML =
          '<div class="message error">✗ You do not have access to this page.</div>';
        summaryEl.innerHTML = "";
        issuesEl.innerHTML = "";
        mutedEl.innerHTML = "";
        eventsEl.innerHTML = "";
        return;
      }

      const [overview, mutedResponse] = await Promise.all([
        this.fetchAdminErrorDetails(60, selectedChannel),
        this.fetchAdminMutedErrorSignatures(selectedChannel),
      ]);

      const issues = overview.issues || [];
      const issuesByFingerprint = new Map(
        issues.map((issue) => [issue.fingerprint, issue]),
      );
      const mutedByFingerprint = new Map(
        (mutedResponse?.muted || []).map((entry) => [entry.fingerprint, entry]),
      );

      issues.forEach((issue) => {
        if (mutedByFingerprint.has(issue.fingerprint)) {
          issue.isMuted = true;
        }
      });
      const visibleIssues = issues.filter((issue) => !issue?.isMuted);

      summaryEl.innerHTML = `
        <p style="color: var(--text-secondary); margin: 0 0 10px 0;">
          Showing: <strong>${this.escapeHtml(selectedChannel)}</strong>
        </p>
        <div class="admin-errors-summary-grid">
          <div class="admin-errors-metric">
            <div class="admin-errors-metric-label">Total events (60d)</div>
            <div class="admin-errors-metric-value">${overview.totals.totalEvents}</div>
          </div>
          <div class="admin-errors-metric">
            <div class="admin-errors-metric-label">Unique issues</div>
            <div class="admin-errors-metric-value">${overview.totals.uniqueFingerprints}</div>
          </div>
          <div class="admin-errors-metric">
            <div class="admin-errors-metric-label">Users affected</div>
            <div class="admin-errors-metric-value">${overview.totals.usersAffected}</div>
          </div>
        </div>
      `;

      if (isFocusedIssueView) {
        applyFocusedIssueLayout();
      }

      const renderMutedSignatures = () => {
        const mutedList = Array.from(mutedByFingerprint.values()).sort(
          (a, b) => (Number(b.mutedAt) || 0) - (Number(a.mutedAt) || 0),
        );

        if (!mutedList.length) {
          mutedEl.innerHTML =
            '<p style="color: var(--text-secondary);">No muted signatures for this channel.</p>';
          return;
        }

        mutedEl.innerHTML = `
          <div class="admin-errors-muted-list">
            ${mutedList
              .map((entry) => {
                const mutedAt = entry.mutedAt
                  ? new Date(entry.mutedAt).toLocaleString()
                  : "Unknown";
                const lastSeen = entry.lastSeen
                  ? new Date(entry.lastSeen).toLocaleString()
                  : "No events recorded";
                return `
                  <div class="admin-errors-muted-card" data-error-fingerprint="${this.escapeHtml(entry.fingerprint)}">
                    <div class="admin-errors-issue-message">${this.escapeHtml(entry.message || "Unknown error")}</div>
                    <div class="admin-errors-issue-meta">
                      <span>Fingerprint: ${this.escapeHtml(entry.fingerprint)}</span>
                      <span>Muted at: ${this.escapeHtml(mutedAt)}</span>
                      <span>Last seen: ${this.escapeHtml(lastSeen)}</span>
                    </div>
                    <div class="admin-errors-issue-meta">
                      <span>Events: ${Number(entry.totalEvents) || 0}</span>
                      <span>Muted by: ${this.escapeHtml(entry.mutedByEmail || entry.mutedByUserId || "Unknown")}</span>
                    </div>
                    <div class="admin-errors-issue-actions">
                      <a href="/admin/errors?fingerprint=${encodeURIComponent(entry.fingerprint)}" class="button-base auth-button admin-errors-issue-action admin-errors-light-button" target="_blank" rel="noopener noreferrer">Open in New Tab</a>
                      <button type="button" class="admin-errors-mute-button" data-error-action="unmute-muted-list" data-error-fingerprint="${this.escapeHtml(entry.fingerprint)}">Unmute Signature</button>
                    </div>
                  </div>
                `;
              })
              .join("")}
          </div>
        `;

        const unmuteButtons = Array.from(
          mutedEl.querySelectorAll(
            'button[data-error-action="unmute-muted-list"]',
          ),
        );
        unmuteButtons.forEach((button) => {
          button.addEventListener("click", async () => {
            const fingerprint = button.getAttribute("data-error-fingerprint");
            if (!fingerprint) return;
            await unmuteSignature(fingerprint);
          });
        });
      };

      const updateIssueCardState = (fingerprint, isMuted) => {
        const issueCards = Array.from(
          issuesEl.querySelectorAll(
            ".admin-errors-issue-card[data-error-fingerprint]",
          ),
        );
        const card = issueCards.find(
          (item) => item.getAttribute("data-error-fingerprint") === fingerprint,
        );
        if (!card) return;

        if (isMuted) {
          card.classList.add("is-muted");
        } else {
          card.classList.remove("is-muted");
        }

        const status = card.querySelector(".admin-errors-issue-status");
        if (status) {
          status.textContent = isMuted ? "Muted" : "Active";
        }

        const button = card.querySelector(
          'button[data-error-action="toggle-mute"]',
        );
        if (!button) return;
        button.textContent = isMuted ? "Unmute Signature" : "Mute Signature";
      };

      const syncSelectedEventActions = (fingerprint) => {
        const issue = issuesByFingerprint.get(fingerprint);
        if (!issue) return;

        const button = eventsEl.querySelector(
          'button[data-error-action="toggle-mute-selected"]',
        );
        if (!button) return;
        button.textContent = issue.isMuted
          ? "Unmute Signature"
          : "Mute Signature";
      };

      const muteSignature = async (fingerprint) => {
        try {
          messageEl.innerHTML =
            '<div class="message info">Muting this signature for future events...</div>';
          await this.fetchAdminMuteErrorSignature(fingerprint, selectedChannel);

          const issue = issuesByFingerprint.get(fingerprint);
          if (issue) {
            issue.isMuted = true;
          }

          const existing = mutedByFingerprint.get(fingerprint) || {};
          mutedByFingerprint.set(fingerprint, {
            fingerprint,
            mutedAt: Date.now(),
            mutedByEmail:
              this.currentUser?.email || existing.mutedByEmail || null,
            mutedByUserId:
              this.currentUser?.userId || existing.mutedByUserId || null,
            message: issue?.message || existing.message || "Unknown error",
            totalEvents: issue?.eventCount || existing.totalEvents || 0,
            lastSeen: issue?.lastSeen || existing.lastSeen || null,
          });

          updateIssueCardState(fingerprint, true);
          syncSelectedEventActions(fingerprint);
          renderMutedSignatures();

          messageEl.innerHTML =
            '<div class="message success">✓ Signature muted. Future events with this fingerprint will be ignored.</div>';
        } catch (error) {
          console.error("Muting error signature failed:", error);
          messageEl.innerHTML =
            '<div class="message error">✗ Failed to mute this signature.</div>';
        }
      };

      const unmuteSignature = async (fingerprint) => {
        try {
          messageEl.innerHTML =
            '<div class="message info">Removing mute for this signature...</div>';
          await this.fetchAdminUnmuteErrorSignature(
            fingerprint,
            selectedChannel,
          );

          const issue = issuesByFingerprint.get(fingerprint);
          if (issue) {
            issue.isMuted = false;
          }

          mutedByFingerprint.delete(fingerprint);
          updateIssueCardState(fingerprint, false);
          syncSelectedEventActions(fingerprint);
          renderMutedSignatures();

          messageEl.innerHTML =
            '<div class="message success">✓ Signature unmuted. Future events will be recorded again.</div>';
        } catch (error) {
          console.error("Unmuting error signature failed:", error);
          messageEl.innerHTML =
            '<div class="message error">✗ Failed to unmute this signature.</div>';
        }
      };

      const toggleMute = async (fingerprint) => {
        const issue = issuesByFingerprint.get(fingerprint);
        if (issue?.isMuted) {
          await unmuteSignature(fingerprint);
        } else {
          await muteSignature(fingerprint);
        }
      };

      renderMutedSignatures();

      if (visibleIssues.length === 0) {
        issuesEl.innerHTML =
          '<p style="color: var(--text-secondary);">No active (unmuted) errors recorded in the selected window.</p>';
        eventsEl.innerHTML =
          '<p style="color: var(--text-secondary);">No issue selected.</p>';
      } else {
        issuesEl.innerHTML = `
          <div class="admin-errors-issues-list">
            ${visibleIssues
              .map((issue) => {
                const users = issue.users?.length
                  ? issue.users.join(", ")
                  : "No authenticated users captured";
                const issueHref = `/admin/errors?fingerprint=${encodeURIComponent(issue.fingerprint)}`;
                const muteLabel = issue.isMuted
                  ? "Unmute Signature"
                  : "Mute Signature";
                return `
                  <div class="admin-errors-issue-card ${issue.isMuted ? "is-muted" : ""}" data-error-fingerprint="${this.escapeHtml(issue.fingerprint)}">
                    <div class="admin-errors-issue-message">${this.escapeHtml(issue.message)}</div>
                    <div class="admin-errors-issue-meta">
                      <span>Count: ${issue.eventCount}</span>
                      <span>Last seen: ${new Date(issue.lastSeen).toLocaleString()}</span>
                      <span>Status: <span class="admin-errors-issue-status">${issue.isMuted ? "Muted" : "Active"}</span></span>
                    </div>
                    <div class="admin-errors-issue-users"><strong>Users:</strong> ${this.escapeHtml(users)}</div>
                    <div class="admin-errors-issue-actions">
                      <a href="${issueHref}" class="button-base auth-button admin-errors-issue-action admin-errors-light-button" target="_blank" rel="noopener noreferrer">Open in New Tab</a>
                      <button type="button" class="admin-errors-mute-button" data-error-action="toggle-mute" data-error-fingerprint="${this.escapeHtml(issue.fingerprint)}">${muteLabel}</button>
                    </div>
                    <div class="admin-errors-issue-open">Open in a new tab to inspect full event data and generate a handoff payload.</div>
                  </div>
                `;
              })
              .join("")}
          </div>
        `;

        eventsEl.innerHTML = requestedFingerprint
          ? '<div class="message info">Loading full error events...</div>'
          : '<p style="color: var(--text-secondary);">Open an issue in a new tab to inspect full event data.</p>';

        const issueCards = Array.from(
          issuesEl.querySelectorAll(
            ".admin-errors-issue-card[data-error-fingerprint]",
          ),
        );

        const loadErrorEvents = async (fingerprint, cardElement) => {
          issueCards.forEach((card) => card.classList.remove("is-selected"));
          if (cardElement) {
            cardElement.classList.add("is-selected");
          }

          eventsEl.innerHTML =
            '<div class="message info">Loading full error events...</div>';

          try {
            const eventsResponse = await this.fetchAdminErrorEvents(
              fingerprint,
              60,
              200,
              selectedChannel,
            );
            const events = eventsResponse.events || [];
            const issue = issuesByFingerprint.get(fingerprint) || null;
            const isMuted = Boolean(issue?.isMuted || eventsResponse.isMuted);
            const handoffPayload = this.buildAdminErrorHandoff(
              issue,
              eventsResponse,
              selectedChannel,
            );

            if (!events.length) {
              eventsEl.innerHTML =
                '<p style="color: var(--text-secondary);">No events found for this issue in the selected window.</p>';
              return;
            }

            eventsEl.innerHTML = `
              <div class="admin-errors-events-header">
                <div><strong>Fingerprint:</strong> ${this.escapeHtml(eventsResponse.fingerprint || fingerprint)}</div>
                <div><strong>Channel:</strong> ${this.escapeHtml(eventsResponse.channel || selectedChannel)}</div>
                <div><strong>Showing:</strong> ${eventsResponse.returnedEvents} / ${eventsResponse.totalEvents} events</div>
                <div><strong>Status:</strong> ${isMuted ? "Muted" : "Active"}</div>
              </div>
              <div class="admin-errors-events-actions">
                <button type="button" class="button-base auth-button admin-errors-copy-button admin-errors-light-button" data-error-action="copy-handoff">Copy AI Handoff String</button>
                <button type="button" class="admin-errors-mute-button" data-error-action="toggle-mute-selected" data-error-fingerprint="${this.escapeHtml(fingerprint)}">${isMuted ? "Unmute Signature" : "Mute Signature"}</button>
              </div>
              <div class="admin-errors-events-list">
                ${events
                  .map((eventRow) => {
                    const payloadText = eventRow.payloadJson
                      ? this.escapeHtml(
                          JSON.stringify(
                            eventRow.payload || eventRow.payloadJson,
                            null,
                            2,
                          ),
                        )
                      : "null";

                    return `
                      <div class="admin-errors-event-card">
                        <div class="admin-errors-event-meta">
                          <span><strong>ID:</strong> ${this.escapeHtml(eventRow.id || "")}</span>
                          <span><strong>Created:</strong> ${eventRow.createdAt ? new Date(eventRow.createdAt).toLocaleString() : "Unknown"}</span>
                          <span><strong>User:</strong> ${this.escapeHtml(eventRow.userEmail || eventRow.userId || "Anonymous")}</span>
                        </div>
                        <div class="admin-errors-event-meta">
                          <span><strong>Source:</strong> ${this.escapeHtml(eventRow.source || "")}</span>
                          <span><strong>App:</strong> ${this.escapeHtml(eventRow.appVersion || "")}</span>
                          <span><strong>Build:</strong> ${this.escapeHtml(eventRow.buildHash || "")}</span>
                        </div>
                        <div class="admin-errors-event-field">
                          <strong>Message</strong>
                          <pre>${this.escapeHtml(eventRow.errorMessage || "")}</pre>
                        </div>
                        <div class="admin-errors-event-field">
                          <strong>Stack</strong>
                          <pre>${this.escapeHtml(eventRow.errorStack || "")}</pre>
                        </div>
                        <div class="admin-errors-event-field">
                          <strong>URL</strong>
                          <pre>${this.escapeHtml(eventRow.url || "")}</pre>
                        </div>
                        <div class="admin-errors-event-field">
                          <strong>User Agent</strong>
                          <pre>${this.escapeHtml(eventRow.userAgent || "")}</pre>
                        </div>
                        <div class="admin-errors-event-field">
                          <strong>Session / Assets</strong>
                          <pre>${this.escapeHtml(eventRow.sessionId || "")}${eventRow.assetsBytes ? `\nassets bytes: ${eventRow.assetsBytes}` : ""}</pre>
                        </div>
                        <div class="admin-errors-event-field">
                          <strong>Payload JSON</strong>
                          <pre>${payloadText}</pre>
                        </div>
                      </div>
                    `;
                  })
                  .join("")}
              </div>
            `;

            const copyButton = eventsEl.querySelector(
              'button[data-error-action="copy-handoff"]',
            );
            if (copyButton) {
              copyButton.addEventListener("click", async () => {
                try {
                  await this.copyTextToClipboard(handoffPayload);
                  const originalText = copyButton.textContent;
                  copyButton.textContent = "Copied";
                  setTimeout(() => {
                    copyButton.textContent = originalText;
                  }, 1200);
                } catch (error) {
                  console.error("Copy handoff failed:", error);
                  messageEl.innerHTML =
                    '<div class="message error">✗ Failed to copy handoff string.</div>';
                }
              });
            }

            const muteButton = eventsEl.querySelector(
              'button[data-error-action="toggle-mute-selected"]',
            );
            if (muteButton) {
              muteButton.addEventListener("click", async () => {
                await toggleMute(fingerprint);
              });
            }
          } catch (error) {
            console.error("Loading error events failed:", error);
            eventsEl.innerHTML =
              '<div class="message error">✗ Failed to load selected issue events.</div>';
          }
        };

        issueCards.forEach((card) => {
          const muteButton = card.querySelector(
            'button[data-error-action="toggle-mute"]',
          );
          if (!muteButton) return;

          muteButton.addEventListener("click", async (event) => {
            event.preventDefault();
            event.stopPropagation();
            const fingerprint = muteButton.getAttribute(
              "data-error-fingerprint",
            );
            if (!fingerprint) {
              return;
            }

            await toggleMute(fingerprint);
          });
        });

        if (requestedFingerprint) {
          const requestedCard = issueCards.find(
            (card) =>
              card.getAttribute("data-error-fingerprint") ===
              requestedFingerprint,
          );

          if (requestedCard) {
            await loadErrorEvents(requestedFingerprint, requestedCard);
          } else if (issuesByFingerprint.has(requestedFingerprint)) {
            await loadErrorEvents(requestedFingerprint, null);
          } else {
            eventsEl.innerHTML =
              '<p style="color: var(--text-secondary);">Requested issue was not found in this time window.</p>';
          }
        }
      }

      messageEl.innerHTML = "";
    } catch (error) {
      console.error("Admin error details failed:", error);
      messageEl.innerHTML =
        '<div class="message error">✗ Failed to load error details. Please try again.</div>';
    }
  }

  escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  formatOwnershipTransferPolicy(policy) {
    return policy === "remove"
      ? "Removed entirely"
      : `Kept as ${this.escapeHtml(policy)}`;
  }

  getCurrentPathWithSearch() {
    return `${window.location.pathname}${window.location.search}`;
  }

  renderInvitePageState({
    messageMarkup = "",
    detailsMarkup = "",
    actionsMarkup = "",
    leadText = "Review this invitation and accept it when you are ready.",
  }) {
    const lead = document.getElementById("inviteLead");
    const messageContainer = document.getElementById("inviteMessageContainer");
    const details = document.getElementById("inviteDetails");
    const actions = document.getElementById("inviteActions");

    if (lead) {
      lead.textContent = leadText;
    }
    if (messageContainer) {
      messageContainer.innerHTML = messageMarkup;
    }
    if (details) {
      details.innerHTML = detailsMarkup;
    }
    if (actions) {
      actions.innerHTML = actionsMarkup;
    }
  }

  renderTransferPageState({
    messageMarkup = "",
    detailsMarkup = "",
    actionsMarkup = "",
    leadText = "Review this ownership transfer and accept it when you are ready.",
  }) {
    const lead = document.getElementById("transferLead");
    const messageContainer = document.getElementById(
      "transferMessageContainer",
    );
    const details = document.getElementById("transferDetails");
    const actions = document.getElementById("transferActions");

    if (lead) {
      lead.textContent = leadText;
    }
    if (messageContainer) {
      messageContainer.innerHTML = messageMarkup;
    }
    if (details) {
      details.innerHTML = detailsMarkup;
    }
    if (actions) {
      actions.innerHTML = actionsMarkup;
    }
  }

  async fetchInvitationStatus(token) {
    const response = await fetch(
      `/api/cloud/invitations/accept?token=${encodeURIComponent(token)}`,
      {
        credentials: "include",
      },
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to load invitation");
    }
    return data;
  }

  async acceptInvitation(token) {
    const response = await fetch("/api/cloud/invitations/accept", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to accept invitation");
    }
    return data;
  }

  async declineInvitation(token) {
    const response = await fetch("/api/cloud/invitations/decline", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to decline invitation");
    }
    return data;
  }

  async fetchTransferStatus(token) {
    const response = await fetch(
      `/api/cloud/ownership-transfers/accept?token=${encodeURIComponent(token)}`,
      {
        credentials: "include",
      },
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to load ownership transfer");
    }
    return data;
  }

  async acceptOwnershipTransfer(token) {
    const response = await fetch("/api/cloud/ownership-transfers/accept", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to accept ownership transfer");
    }
    return data;
  }

  async declineOwnershipTransfer(token) {
    const response = await fetch("/api/cloud/ownership-transfers/decline", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to decline ownership transfer");
    }
    return data;
  }

  initializeInvitePage() {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (!token) {
      this.renderInvitePageState({
        messageMarkup:
          '<div class="message error">✗ This invitation link is missing its token.</div>',
        actionsMarkup:
          '<a href="/account" class="button-base auth-button">Go to account</a>',
      });
      return;
    }

    this.renderInvitePageState({
      messageMarkup:
        '<div class="message info">Loading invitation details...</div>',
    });

    void this.fetchInvitationStatus(token)
      .then((data) => {
        const invitation = data.invitation;
        const invitationDetails = `
          <div class="account-card">
            <p><strong>Font:</strong> ${this.escapeHtml(invitation.assetName)}</p>
            <p><strong>Access:</strong> ${this.escapeHtml(invitation.role)}</p>
            <p><strong>Invited email:</strong> ${this.escapeHtml(invitation.email)}</p>
            ${
              invitation.invitedByEmail
                ? `<p><strong>Invited by:</strong> ${this.escapeHtml(invitation.invitedByEmail)}</p>`
                : ""
            }
          </div>
        `;

        if (data.acceptedByCurrentUser) {
          this.renderInvitePageState({
            messageMarkup:
              '<div class="message success">✓ This invitation has already been accepted for your account.</div>',
            detailsMarkup: invitationDetails,
            actionsMarkup: `
              <a href="${this.escapeHtml(data.editorUrl)}" class="button-base auth-button">Open in editor</a>
              <a href="/account" class="button-base subscription-button">Maybe later</a>
            `,
            leadText: "You can open the shared font whenever you want.",
          });
          return;
        }

        if (data.requiresLogin) {
          this.renderInvitePageState({
            messageMarkup:
              '<div class="message info">Sign in with the invited email address to accept this invitation.</div>',
            detailsMarkup: invitationDetails,
            actionsMarkup:
              '<button id="inviteSignInButton" class="button-base auth-button">Sign in to accept</button>',
            leadText:
              "You do not need to open the asset immediately after accepting.",
          });

          document
            .getElementById("inviteSignInButton")
            ?.addEventListener("click", () => {
              this.redirectToLoginPath(this.getCurrentPathWithSearch());
            });
          return;
        }

        if (data.emailMatches === false) {
          this.renderInvitePageState({
            messageMarkup: `<div class="message error">✗ You are signed in as ${this.escapeHtml(
              data.currentUserEmail,
            )}, but this invitation was sent to ${this.escapeHtml(invitation.email)}.</div>`,
            detailsMarkup: invitationDetails,
            actionsMarkup:
              '<button id="inviteSwitchAccountButton" class="button-base auth-button">Use a different account</button>',
          });

          document
            .getElementById("inviteSwitchAccountButton")
            ?.addEventListener("click", () => {
              this.logout();
              this.redirectToLoginPath(this.getCurrentPathWithSearch());
            });
          return;
        }

        this.renderInvitePageState({
          messageMarkup:
            '<div class="message info">Accept this invitation to add the shared font to your cloud files.</div>',
          detailsMarkup: invitationDetails,
          actionsMarkup: `
            <button id="inviteAcceptButton" class="button-base auth-button">Accept invitation</button>
            <button id="inviteDeclineButton" class="button-base secondary-button">Decline invitation</button>
          `,
          leadText:
            "After you accept, you will stay on this page and get a button to open the font in the editor.",
        });

        document
          .getElementById("inviteAcceptButton")
          ?.addEventListener("click", async () => {
            const button = document.getElementById("inviteAcceptButton");
            if (button) {
              button.setAttribute("disabled", "disabled");
              button.textContent = "Accepting...";
            }

            try {
              const accepted = await this.acceptInvitation(token);
              this.renderInvitePageState({
                messageMarkup: `<div class="message success">✓ You now have ${this.escapeHtml(
                  accepted.role,
                )} access to ${this.escapeHtml(accepted.assetName)}.</div>`,
                detailsMarkup: invitationDetails,
                actionsMarkup: `
                  <a href="${this.escapeHtml(accepted.editorUrl)}" class="button-base auth-button">Open in editor</a>
                  <a href="/account" class="button-base subscription-button">Maybe later</a>
                `,
                leadText:
                  "The invitation is accepted. You can open the shared font now or come back later.",
              });
            } catch (error) {
              this.renderInvitePageState({
                messageMarkup: `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
                detailsMarkup: invitationDetails,
                actionsMarkup:
                  '<button id="inviteRetryButton" class="button-base auth-button">Try again</button>',
              });
              document
                .getElementById("inviteRetryButton")
                ?.addEventListener("click", () => this.initializeInvitePage());
            }
          });

        document
          .getElementById("inviteDeclineButton")
          ?.addEventListener("click", async () => {
            const declineButton = document.getElementById(
              "inviteDeclineButton",
            );
            const acceptButton = document.getElementById("inviteAcceptButton");
            if (declineButton) {
              declineButton.setAttribute("disabled", "disabled");
              declineButton.textContent = "Declining...";
            }
            if (acceptButton) {
              acceptButton.setAttribute("disabled", "disabled");
            }

            try {
              await this.declineInvitation(token);
              this.renderInvitePageState({
                messageMarkup:
                  '<div class="message success">✓ Invitation declined.</div>',
                detailsMarkup: invitationDetails,
                actionsMarkup:
                  '<a href="/account" class="button-base auth-button">Go to account</a>',
                leadText:
                  "This invitation has been declined. You can return to your account at any time.",
              });
            } catch (error) {
              this.renderInvitePageState({
                messageMarkup: `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
                detailsMarkup: invitationDetails,
                actionsMarkup: `
                  <button id="inviteAcceptButton" class="button-base auth-button">Accept invitation</button>
                  <button id="inviteDeclineButton" class="button-base secondary-button">Decline invitation</button>
                `,
              });
              this.initializeInvitePage();
            }
          });
      })
      .catch((error) => {
        this.renderInvitePageState({
          messageMarkup: `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          actionsMarkup:
            '<a href="/account" class="button-base auth-button">Go to account</a>',
        });
      });
  }

  initializeTransferPage() {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (!token) {
      this.renderTransferPageState({
        messageMarkup:
          '<div class="message error">✗ This ownership transfer link is missing its token.</div>',
        actionsMarkup:
          '<a href="/account" class="button-base auth-button">Go to account</a>',
      });
      return;
    }

    this.renderTransferPageState({
      messageMarkup:
        '<div class="message info">Loading ownership transfer details...</div>',
    });

    void this.fetchTransferStatus(token)
      .then((data) => {
        const transfer = data.transfer;
        const transferDetails = `
          <div class="account-card">
            <p><strong>Font:</strong> ${this.escapeHtml(transfer.assetName)}</p>
            <p><strong>New owner email:</strong> ${this.escapeHtml(transfer.targetEmail)}</p>
            <p><strong>Current owner:</strong> ${this.escapeHtml(transfer.sourceOwnerEmail)}</p>
            <p><strong>Current owner after transfer:</strong> ${this.formatOwnershipTransferPolicy(transfer.previousOwnerRole)}</p>
          </div>
        `;

        if (data.acceptedByCurrentUser) {
          this.renderTransferPageState({
            messageMarkup:
              '<div class="message success">✓ This ownership transfer has already been accepted for your account.</div>',
            detailsMarkup: transferDetails,
            actionsMarkup: `
              <a href="${this.escapeHtml(data.editorUrl)}" class="button-base auth-button">Open in editor</a>
              <a href="/account" class="button-base subscription-button">Maybe later</a>
            `,
            leadText: "You can open the transferred font whenever you want.",
          });
          return;
        }

        if (transfer.acceptedAt) {
          this.renderTransferPageState({
            messageMarkup:
              '<div class="message success">✓ This ownership transfer has already been completed.</div>',
            detailsMarkup: transferDetails,
            actionsMarkup:
              '<a href="/account" class="button-base auth-button">Go to account</a>',
            leadText: "This transfer is no longer pending.",
          });
          return;
        }

        if (data.requiresLogin) {
          this.renderTransferPageState({
            messageMarkup:
              '<div class="message info">Sign in with the target email address to accept this ownership transfer.</div>',
            detailsMarkup: transferDetails,
            actionsMarkup:
              '<button id="transferSignInButton" class="button-base auth-button">Sign in to review</button>',
            leadText:
              "You do not need to open the asset immediately after accepting ownership.",
          });

          document
            .getElementById("transferSignInButton")
            ?.addEventListener("click", () => {
              this.redirectToLoginPath(this.getCurrentPathWithSearch());
            });
          return;
        }

        if (data.emailMatches === false) {
          this.renderTransferPageState({
            messageMarkup: `<div class="message error">✗ You are signed in as ${this.escapeHtml(
              data.currentUserEmail,
            )}, but this ownership transfer was sent to ${this.escapeHtml(transfer.targetEmail)}.</div>`,
            detailsMarkup: transferDetails,
            actionsMarkup:
              '<button id="transferSwitchAccountButton" class="button-base auth-button">Use a different account</button>',
          });

          document
            .getElementById("transferSwitchAccountButton")
            ?.addEventListener("click", () => {
              this.logout();
              this.redirectToLoginPath(this.getCurrentPathWithSearch());
            });
          return;
        }

        this.renderTransferPageState({
          messageMarkup:
            '<div class="message info">Accept this transfer to become the new owner of the shared font.</div>',
          detailsMarkup: transferDetails,
          actionsMarkup: `
            <button id="transferAcceptButton" class="button-base auth-button">Accept transfer</button>
            <button id="transferDeclineButton" class="button-base secondary-button">Decline transfer</button>
          `,
          leadText:
            "After you accept, you will stay on this page and get a button to open the font in the editor.",
        });

        document
          .getElementById("transferAcceptButton")
          ?.addEventListener("click", async () => {
            const button = document.getElementById("transferAcceptButton");
            if (button) {
              button.setAttribute("disabled", "disabled");
              button.textContent = "Accepting...";
            }

            try {
              const accepted = await this.acceptOwnershipTransfer(token);
              this.renderTransferPageState({
                messageMarkup: `<div class="message success">✓ You are now the owner of ${this.escapeHtml(
                  accepted.assetName,
                )}.</div>`,
                detailsMarkup: transferDetails,
                actionsMarkup: `
                  <a href="${this.escapeHtml(accepted.editorUrl)}" class="button-base auth-button">Open in editor</a>
                  <a href="/account" class="button-base subscription-button">Maybe later</a>
                `,
                leadText:
                  "The ownership transfer is complete. You can open the font now or come back later.",
              });
            } catch (error) {
              this.renderTransferPageState({
                messageMarkup: `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
                detailsMarkup: transferDetails,
                actionsMarkup:
                  '<button id="transferRetryButton" class="button-base auth-button">Try again</button>',
              });
              document
                .getElementById("transferRetryButton")
                ?.addEventListener("click", () =>
                  this.initializeTransferPage(),
                );
            }
          });

        document
          .getElementById("transferDeclineButton")
          ?.addEventListener("click", async () => {
            const declineButton = document.getElementById(
              "transferDeclineButton",
            );
            const acceptButton = document.getElementById(
              "transferAcceptButton",
            );
            if (declineButton) {
              declineButton.setAttribute("disabled", "disabled");
              declineButton.textContent = "Declining...";
            }
            if (acceptButton) {
              acceptButton.setAttribute("disabled", "disabled");
            }

            try {
              await this.declineOwnershipTransfer(token);
              this.renderTransferPageState({
                messageMarkup:
                  '<div class="message success">✓ Ownership transfer declined.</div>',
                detailsMarkup: transferDetails,
                actionsMarkup:
                  '<a href="/account" class="button-base auth-button">Go to account</a>',
                leadText:
                  "This ownership transfer has been declined. You can return to your account at any time.",
              });
            } catch (error) {
              this.renderTransferPageState({
                messageMarkup: `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
                detailsMarkup: transferDetails,
                actionsMarkup: `
                  <button id="transferAcceptButton" class="button-base auth-button">Accept transfer</button>
                  <button id="transferDeclineButton" class="button-base secondary-button">Decline transfer</button>
                `,
              });
              this.initializeTransferPage();
            }
          });
      })
      .catch((error) => {
        this.renderTransferPageState({
          messageMarkup: `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          actionsMarkup:
            '<a href="/account" class="button-base auth-button">Go to account</a>',
        });
      });
  }

  /**
   * Sign-in page: the email, then the six-digit code from the email.
   * Pasting or autofilling a whole code submits it right away.
   */
  initializeLoginForm() {
    const emailForm = document.getElementById("loginForm");
    const codeForm = document.getElementById("loginCodeForm");
    const messageContainer = document.getElementById("messageContainer");
    if (!emailForm || !codeForm || !messageContainer) return;
    // Startup binds the form early; the page handler runs again later.
    if (emailForm.dataset.loginBound === "1") return;
    emailForm.dataset.loginBound = "1";

    const emailInput = document.getElementById("email");
    const sendButton = document.getElementById("submitBtn");
    const codeInput = document.getElementById("loginCode");
    const verifyButton = document.getElementById("verifyBtn");
    const resendButton = document.getElementById("resendBtn");
    const changeEmailButton = document.getElementById("loginChangeEmail");
    const sentTo = document.getElementById("loginCodeEmail");
    const returnTo =
      new URLSearchParams(window.location.search).get("returnTo") || null;

    const state = {
      email: "",
      sending: false,
      verifying: false,
      locked: false,
      lastSubmitted: "",
      resendTimer: null,
    };

    const showMessage = (kind, text) => {
      messageContainer.innerHTML = text
        ? `<div class="message ${kind}" role="${kind === "error" ? "alert" : "status"}">${this.escapeHtml(text)}</div>`
        : "";
    };

    const stopResendCooldown = () => {
      clearInterval(state.resendTimer);
      state.resendTimer = null;
      resendButton.disabled = false;
      resendButton.textContent = "Resend code";
    };

    const startResendCooldown = (seconds) => {
      clearInterval(state.resendTimer);
      const until = Date.now() + Math.max(0, Number(seconds) || 0) * 1000;
      const tick = () => {
        const left = Math.ceil((until - Date.now()) / 1000);
        if (left <= 0 || !codeForm.isConnected) {
          stopResendCooldown();
          return;
        }
        resendButton.disabled = true;
        resendButton.textContent = `Resend code in ${left}s`;
      };
      tick();
      state.resendTimer = setInterval(tick, 1000);
    };

    const requestCode = async () => {
      if (state.sending) return false;
      state.sending = true;
      sendButton.disabled = true;
      sendButton.textContent = "Sending…";
      resendButton.disabled = true;
      try {
        const response = await fetch("/api/auth/request-code", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: state.email,
            returnTo,
            trackingParams: this.getTrackingParams(),
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          showMessage("error", describeLoginCodeError(data, response.status));
          if (data.retryAfterSeconds) startResendCooldown(data.retryAfterSeconds);
          else if (!codeForm.hidden) stopResendCooldown();
          return false;
        }
        if (data.devCode) {
          console.info(`[auth] Local sign-in code: ${data.devCode}`);
        }
        startResendCooldown(data.resendAfterSeconds ?? 30);
        return true;
      } catch (error) {
        console.error("Sign-in code request failed:", error);
        showMessage("error", "Network error. Check your connection and try again.");
        if (!codeForm.hidden) stopResendCooldown();
        return false;
      } finally {
        state.sending = false;
        sendButton.disabled = false;
        sendButton.textContent = "Send code";
      }
    };

    const resetCodeField = () => {
      state.locked = false;
      state.lastSubmitted = "";
      codeInput.disabled = false;
      codeInput.value = "";
      codeInput.focus();
    };

    const verify = async (code) => {
      if (state.verifying || state.locked) return;
      state.verifying = true;
      state.lastSubmitted = code;
      codeInput.readOnly = true;
      verifyButton.disabled = true;
      verifyButton.textContent = "Signing in…";
      let signedIn = false;
      try {
        const response = await fetch("/api/auth/verify-code", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: state.email, code }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          showMessage("error", describeLoginCodeError(data, response.status));
          if (data.code === "too_many_attempts") {
            state.locked = true;
            codeInput.disabled = true;
          }
          return;
        }
        signedIn = true;
        showMessage("success", "Signed in.");
        await this.finishCodeLogin(data.redirect);
      } catch (error) {
        console.error("Sign-in code verification failed:", error);
        state.lastSubmitted = "";
        showMessage("error", "Network error. Check your connection and try again.");
      } finally {
        state.verifying = false;
        codeInput.readOnly = false;
        if (!signedIn && codeForm.isConnected) {
          verifyButton.disabled = false;
          verifyButton.textContent = "Sign in";
          if (!state.locked) codeInput.select();
        }
      }
    };

    const submitIfComplete = () => {
      const code = codeInput.value;
      if (code.length === LOGIN_CODE_LENGTH && code !== state.lastSubmitted) {
        verify(code);
      }
    };

    emailForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      state.email = emailInput.value.trim();
      if (!(await requestCode())) return;
      emailForm.hidden = true;
      codeForm.hidden = false;
      sentTo.textContent = state.email;
      showMessage("success", "Code sent. Check your inbox, and the spam folder if it isn't there.");
      resetCodeField();
      this.trackEvent("login-initiated", { method: "code" });
    });

    codeInput.addEventListener("paste", (event) => {
      const code = extractLoginCode(event.clipboardData?.getData("text"));
      if (!code) return;
      event.preventDefault();
      codeInput.value = code;
      // Pasting again is a deliberate retry, even with the same code.
      state.lastSubmitted = "";
      submitIfComplete();
    });

    codeInput.addEventListener("input", () => {
      const clean = sanitizeLoginCodeInput(codeInput.value);
      if (clean !== codeInput.value) codeInput.value = clean;
      if (clean.length < LOGIN_CODE_LENGTH) state.lastSubmitted = "";
      submitIfComplete();
    });

    codeForm.addEventListener("submit", (event) => {
      event.preventDefault();
      const code = sanitizeLoginCodeInput(codeInput.value);
      if (code.length !== LOGIN_CODE_LENGTH) {
        showMessage("error", `Enter the ${LOGIN_CODE_LENGTH}-digit code from the email.`);
        codeInput.focus();
        return;
      }
      verify(code);
    });

    resendButton.addEventListener("click", async () => {
      if (!(await requestCode())) return;
      showMessage("success", "We sent a new code.");
      resetCodeField();
    });

    changeEmailButton.addEventListener("click", () => {
      stopResendCooldown();
      codeForm.hidden = true;
      emailForm.hidden = false;
      showMessage("", "");
      emailInput.focus();
      emailInput.select();
    });

    // Links mailed before sign-in codes land here when they fail.
    const error = new URLSearchParams(window.location.search).get("error");
    if (error) {
      showMessage("error", this.authErrorMessage(error));
    }
  }

  /** After a code sign-in: back to returnTo, or the account page. */
  async finishCodeLogin(redirect) {
    if (redirect && !redirect.startsWith("/")) {
      // An editor that still sends people here; the URL carries a handoff code.
      window.location.href = redirect;
      return;
    }
    this.clearCache();
    await this.refreshCurrentUser();
    this.updateAuthUI();
    await this.loadPage(redirect || "/account", true);
  }

  /**
   * Initialize account page
   */
  initializeAccountPage() {
    const accountInfo = document.getElementById("accountInfo");
    const emailChangeSection = document.getElementById("emailChangeSection");
    if (!accountInfo) return;

    const user = this.getSessionUser();
    if (!user) {
      // Not logged in, redirect to login
      this.loadPage("/login", true);
      return;
    }

    // Display account information
    const createdDate = user.createdAt
      ? new Date(user.createdAt).toLocaleDateString()
      : "Unknown";
    accountInfo.innerHTML = `
      <dl class="account-kv">
        <dt>Email</dt><dd>${user.email}</dd>
        <dt>User ID</dt><dd><code>${user.userId}</code></dd>
        <dt>Member since</dt><dd>${createdDate}</dd>
      </dl>
    `;

    // Initialize subscription section
    this.accountMembership = null;
    this.initializeSubscriptionSection();
    const accountParams = new URLSearchParams(window.location.search);
    const setupSessionId =
      accountParams.get("setup") === "saved"
        ? accountParams.get("session_id")
        : null;
    this.paymentSetupDone = setupSessionId
      ? this.finishPaymentSetup(setupSessionId)
      : null;
    if (
      (accountParams.get("setup") ||
        accountParams.get("return") === "editor") &&
      window.location.hash !== "#payment"
    ) {
      history.replaceState(
        null,
        "",
        `/account${window.location.search}#payment`,
      );
    }
    this.bindAccountTabs();
    this.initializePluginsSection();

    // Show email change section
    if (emailChangeSection) {
      emailChangeSection.classList.remove("hidden");
      this.initializeEmailChangeForm();
    }

    // Check for success/error messages in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get("setup") === "saved") {
      this.showAuthSuccess(
        params.get("return") === "editor"
          ? "Payment method saved. You can close this tab and return to the editor."
          : "Payment method saved.",
      );
      const next = `${window.location.pathname}${window.location.hash || ""}`;
      window.history.replaceState({}, "", next);
    } else if (params.get("setup") === "canceled") {
      this.showAuthError("Payment method setup was canceled.");
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${window.location.hash || ""}`,
      );
    } else if (params.get("subscription") === "success") {
      this.showAuthSuccess(
        "Payment method saved. Setting up your subscription…",
      );
      window.history.replaceState({}, "", window.location.pathname);
      this.pollSubscriptionSetup();
    } else if (params.get("subscription") === "canceled") {
      this.showAuthError("Subscription signup was canceled.");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("email_changed") === "success") {
      this.showAuthSuccess("Your email address has been updated successfully!");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("error")) {
      this.showAuthError(params.get("error"));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }

  bindAccountTabs() {
    const tabs = [
      ["", "accountTab", "accountPanel"],
      ["#plugins", "pluginsTab", "pluginsPanel"],
      ["#payment", "paymentTab", "paymentPanel"],
      ["#purchases", "purchasesTab", "purchasesPanel"],
      ["#payouts", "payoutsTab", "payoutsPanel"],
    ].map(([hash, tabId, panelId]) => ({
      hash,
      tab: document.getElementById(tabId),
      panel: document.getElementById(panelId),
    }));
    if (tabs.some((item) => !item.tab || !item.panel) || this.accountTabsBound)
      return;
    this.accountTabsBound = true;
    const show = () => {
      const hash = ["#plugins", "#payouts", "#payment", "#purchases"].includes(
        window.location.hash,
      )
        ? window.location.hash
        : "";
      for (const item of tabs) {
        const selected = item.hash === hash;
        item.panel.hidden = !selected;
        item.tab.setAttribute("aria-selected", String(selected));
      }
      if (hash === "#payouts") this.renderPayoutsTab();
      if (hash === "#payment") this.renderPaymentTab();
      if (hash === "#purchases") this.renderPurchasesTab();
    };
    for (const item of tabs) {
      item.tab.addEventListener("click", () => {
        history.replaceState(null, "", `/account${item.hash}`);
        show();
      });
    }
    window.addEventListener("hashchange", show);
    window.addEventListener("focus", () => {
      if (window.location.hash === "#purchases") {
        const content = document.getElementById("purchasesInfo");
        if (content) content.dataset.loading = "";
        this.renderPurchasesTab();
      }
    });
    show();
  }

  pluginErrorText(code) {
    return (
      {
        authorization:
          "GitHub sent you back without linking your account. Click Connect GitHub again and approve the authorization screen.",
        unauthorized: "Sign in, then click Connect GitHub again.",
        installation:
          "That GitHub installation is not on the account you just signed in with. Click Connect GitHub and pick the account that owns the repository.",
        github: "GitHub could not be reached. Click Connect GitHub again.",
        missing:
          "GitHub did not finish connecting. Click Connect GitHub again and approve access to the repository.",
        credentials:
          "This site's GitHub App is set up with the wrong client secret. An administrator has to correct it before anyone can connect.",
      }[code] || "GitHub could not be connected. Click Connect GitHub again."
    );
  }

  renderPluginCheckTable(caption, rows) {
    const table = document.createElement("table");
    table.className = "plugin-check-table";
    const heading = document.createElement("caption");
    heading.textContent = caption;
    table.appendChild(heading);
    const body = document.createElement("tbody");
    const marks = {
      done: ["check", "Passed"],
      error: ["error", "Failed"],
      run: ["progress_activity", "In progress"],
      wait: ["radio_button_unchecked", "Waiting"],
    };
    for (const row of rows) {
      const [iconName, label] = marks[row.state] || marks.wait;
      const item = document.createElement("tr");
      const mark = document.createElement("td");
      const icon = document.createElement("span");
      icon.className = "material-symbols-outlined";
      if (row.emphasis) item.classList.add("plugin-check-strong");
      if (row.state === "run") icon.classList.add("plugin-check-spin");
      if (row.state === "error") icon.classList.add("plugin-check-error");
      if (row.state === "done") icon.classList.add("plugin-check-done");
      icon.textContent = iconName;
      icon.setAttribute("role", "img");
      icon.setAttribute("aria-label", label);
      mark.appendChild(icon);
      const name = document.createElement("td");
      name.textContent = row.label;
      const detail = document.createElement("td");
      detail.textContent = row.detail;
      item.append(mark, name, detail);
      body.appendChild(item);
    }
    table.appendChild(body);
    return table;
  }

  versionStatusLabel(status) {
    return (
      {
        preflighting: "Checking in a sandbox…",
        ready: "Ready to publish",
        published: "Published",
        static_failed: "Failed",
        preflight_failed: "Failed",
        security_failed: "Security check failed",
        security_timed_out:
          "The check failed on our side. Your plugin was not judged.",
        check_failed:
          "The check failed on our side. Your plugin was not judged.",
      }[status] || status
    );
  }

  schedulePluginRefresh(plugins) {
    clearTimeout(this.pluginPoll);
    const busy = (plugins || []).some(
      (plugin) =>
        plugin.busy ||
        (plugin.versions || []).some(
          (version) => version.status === "preflighting",
        ),
    );
    if (!busy) return;
    this.pluginPoll = setTimeout(() => {
      if (document.getElementById("pluginsInfo"))
        this.initializePluginsSection();
    }, 4000);
  }

  async initializePluginsSection() {
    const root = document.getElementById("pluginsInfo");
    if (!root) return;
    clearTimeout(this.pluginPoll);
    const params = new URLSearchParams(window.location.search);
    const pluginsError = params.get("plugins_error");
    if (pluginsError) {
      history.replaceState(null, "", "/account#plugins");
    }
    let overview;
    try {
      const response = await fetch("/api/creator/plugins");
      overview = response.ok ? await response.json() : null;
    } catch (error) {
      root.textContent = "Could not load plugins.";
      return;
    }
    if (!overview) {
      root.textContent = "Could not load plugins.";
      return;
    }
    let installations = [];
    const repos = await fetch("/api/creator/github/repos");
    if (repos.ok) {
      const body = await repos.json();
      installations = body.installations || [];
    }
    const repositories = installations.flatMap((installation) =>
      (installation.repositories || []).map((repo) => ({
        ...repo,
        installationId: installation.installationId,
      })),
    );
    this.pluginOverview = overview;
    const plugins = overview.plugins || [];
    // The refresh during a preflight must not wipe a form the creator is filling in.
    const touchedRegister = document
      .getElementById("pluginRegisterForm")
      ?.closest("[data-touched]");
    root.innerHTML = "";
    const publishReady = plugins.some(
      (plugin) =>
        plugin.status !== "live" &&
        (plugin.versions || []).some((version) => version.status === "ready"),
    );
    const checking = plugins.some(
      (plugin) =>
        plugin.busy ||
        (plugin.versions || []).some(
          (version) => version.status === "preflighting",
        ),
    );
    root.appendChild(
      this.renderPluginSteps({
        linked: repositories.length > 0,
        registered: plugins.length > 0,
        publishReady,
        checking,
        published: plugins.some((plugin) => plugin.status === "live"),
      }),
    );
    root.appendChild(
      this.renderPluginGitHub(overview, installations, pluginsError),
    );
    if (touchedRegister) {
      root.appendChild(touchedRegister);
    } else if (repositories.length) {
      root.appendChild(this.renderPluginRegister(overview, repositories));
    }
    root.appendChild(this.renderPluginListings(plugins));
    this.schedulePluginRefresh(plugins);
    // A rebuild would drop the chosen country and the mounted Stripe component.
    if (!document.querySelector("#payoutsInfo [data-payout-setup]"))
      this.payoutsRendered = false;
    this.renderPayoutsTab();
  }

  renderPluginSteps(state) {
    const { block, content } = this.pluginSection("Getting started");
    const list = document.createElement("ol");
    list.className = "plugin-steps";
    const steps = [
      {
        label: "Connect GitHub",
        done: state.linked,
        text: state.linked
          ? "Connect GitHub — done"
          : "Connect GitHub — do this next",
      },
      {
        label: "Register your plugin",
        done: state.registered,
        text: state.registered
          ? "Register your plugin — done"
          : state.linked
            ? "Register your plugin — do this next"
            : "Register your plugin",
      },
      {
        label: "Publish",
        done: state.published,
        text: state.published
          ? "Publish — done"
          : state.publishReady
            ? "Publish — do this next"
            : state.checking
              ? "Publish — waiting for the check"
              : "Publish",
      },
    ];
    for (const step of steps) {
      const item = document.createElement("li");
      const current = step.text.endsWith("do this next");
      item.className = step.done
        ? "plugin-step plugin-step-done"
        : current
          ? "plugin-step plugin-step-current"
          : "plugin-step";
      item.textContent = step.text;
      list.appendChild(item);
    }
    content.appendChild(list);
    return block;
  }

  pluginSection(title) {
    const block = document.createElement("section");
    block.className = "account-section plugins-block";
    const label = document.createElement("h2");
    label.className = "account-section-label";
    label.textContent = title;
    const content = document.createElement("div");
    content.className = "account-section-content";
    block.append(label, content);
    return { block, content };
  }

  renderPayoutsTab() {
    const root = document.getElementById("payoutsInfo");
    if (!root || !this.pluginOverview || this.payoutsRendered) return;
    this.payoutsRendered = true;
    root.replaceChildren();
    const { block, content } = this.pluginSection("Payouts");
    const intro = document.createElement("p");
    intro.textContent =
      "Paid plugins pay out to this account. Buyers can purchase a plugin only after payouts are enabled.";
    content.appendChild(intro);
    content.appendChild(this.renderPluginPayouts(this.pluginOverview));
    root.appendChild(block);
  }

  pluginGitHubButton(label, url) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "button-base account-button";
    button.textContent = label;
    button.addEventListener("click", () => {
      window.location.href = url;
    });
    return button;
  }

  pluginRepoList(installations) {
    const list = document.createElement("div");
    list.className = "plugin-accounts";
    for (const installation of installations) {
      const account = document.createElement("section");
      account.className = "plugin-account";
      const owner = installation.accountLogin || "GitHub account";
      const name = document.createElement("div");
      name.className = "plugin-account-name";
      name.textContent = owner;
      account.appendChild(name);
      const repos = installation.repositories || [];
      if (!repos.length) {
        const empty = document.createElement("p");
        empty.className = "account-note";
        empty.textContent = "No repositories you administer on this account.";
        account.appendChild(empty);
        list.appendChild(account);
        continue;
      }
      const rows = document.createElement("ul");
      rows.className = "plugin-repo-list";
      for (const repo of repos) {
        const short = repo.fullName?.startsWith(`${owner}/`)
          ? repo.fullName.slice(owner.length + 1)
          : repo.fullName;
        const row = document.createElement("li");
        row.className = "plugin-repo-row";
        row.title = repo.fullName || short;
        const repoName = document.createElement("span");
        repoName.className = "plugin-repo-name";
        repoName.textContent = short || repo.fullName;
        const visibility = document.createElement("span");
        visibility.className = "plugin-repo-visibility";
        visibility.textContent = repo.private ? "Private" : "Public";
        row.append(repoName, visibility);
        rows.appendChild(row);
      }
      account.appendChild(rows);
      list.appendChild(account);
    }
    return list;
  }

  renderPluginGitHub(overview, installations, pluginsError) {
    const { block, content } = this.pluginSection("GitHub");
    const intro = document.createElement("p");
    intro.textContent =
      "Counterpunch reads plugin releases from GitHub repositories you administer.";
    content.appendChild(intro);
    if (pluginsError) {
      const message = document.createElement("p");
      message.className = "message error";
      message.textContent = this.pluginErrorText(pluginsError);
      content.appendChild(message);
    }
    const github = overview.github || {};
    const repoCount = installations.reduce(
      (count, installation) => count + (installation.repositories || []).length,
      0,
    );
    if (!github.ready) {
      const notice = document.createElement("p");
      notice.textContent =
        "Publishing plugins is not available on this site yet.";
      content.appendChild(notice);
      if ((github.missing || []).length) {
        const detail = document.createElement("p");
        detail.className = "account-note";
        detail.textContent = `Missing settings: ${github.missing.join(", ")}`;
        content.appendChild(detail);
      }
      if (repoCount) content.appendChild(this.pluginRepoList(installations));
      return block;
    }
    if (!installations.length) {
      const help = document.createElement("p");
      help.textContent =
        "GitHub asks you to approve Counterpunch, then sends you back here. You need to be an admin of the repository that holds your plugin.";
      content.appendChild(help);
      const actions = document.createElement("div");
      actions.className = "plugin-github-actions";
      if (github.authorizeUrl)
        actions.appendChild(
          this.pluginGitHubButton("Connect GitHub", github.authorizeUrl),
        );
      content.appendChild(actions);
      if (github.installUrl) {
        const install = document.createElement("p");
        install.className = "account-note";
        const link = document.createElement("a");
        link.href = github.installUrl;
        link.textContent = "Install the GitHub App";
        install.append(
          "The app is not on your repository yet? ",
          link,
          ". Choose the repository, then come back and click Connect GitHub.",
        );
        content.appendChild(install);
      }
      return block;
    }
    if (!repoCount) {
      const help = document.createElement("p");
      help.textContent =
        "The GitHub App is connected, but it cannot see a repository you administer. On GitHub, grant it access to the repository that holds your plugin. You need to be an admin of that repository.";
      content.appendChild(help);
      if (github.installUrl) {
        const actions = document.createElement("div");
        actions.className = "plugin-github-actions";
        actions.appendChild(
          this.pluginGitHubButton(
            "Choose repositories on GitHub",
            github.installUrl,
          ),
        );
        content.appendChild(actions);
      }
      return block;
    }
    content.appendChild(this.pluginRepoList(installations));
    const actions = document.createElement("div");
    actions.className = "plugin-github-actions";
    if (github.installUrl)
      actions.appendChild(
        this.pluginGitHubButton("Add a repository", github.installUrl),
      );
    if (github.authorizeUrl)
      actions.appendChild(
        this.pluginGitHubButton("Refresh from GitHub", github.authorizeUrl),
      );
    content.appendChild(actions);
    return block;
  }

  async loadPluginPppRows() {
    if (this.pluginPppRows) return this.pluginPppRows;
    const [csv, overrides] = await Promise.all([
      fetch("/data/ppp/ppp.csv").then((response) => response.text()),
      fetch("/data/ppp/overrides.json")
        .then((response) => (response.ok ? response.json() : []))
        .catch(() => []),
    ]);
    const rows = loadPPPData(csv);
    const us = rows.find((row) => row.code === "USA");
    const entries = Array.isArray(overrides)
      ? overrides
      : overrides?.entries || [];
    this.pluginPppRows = applyPppOverrides(rows, entries, us?.gdp);
    return this.pluginPppRows;
  }

  async refreshPluginPricePreview(form, payoutCountry) {
    const preview =
      form.querySelector("#pluginPricePreview") ||
      form.querySelector(".plugin-price-preview");
    const priceInput =
      form.querySelector("#pluginPrice") || form.querySelector("[name=price]");
    const floorInput =
      form.querySelector("#pluginFloor") || form.querySelector("[name=floor]");
    if (!preview || !priceInput || !floorInput || !payoutCountry) return;
    preview.textContent = "Calculating country prices…";
    try {
      const rows = await this.loadPluginPppRows();
      const range = pluginPriceRange(
        {
          mode: "ppp",
          priceCents: Math.round(Number(priceInput.value) * 100),
          floorCents: Math.round(Number(floorInput.value) * 100),
          referenceCountry: payoutCountry,
        },
        rows,
      );
      preview.textContent = formatPluginPriceRange(range);
    } catch {
      preview.textContent = "Country prices could not be calculated.";
    }
  }

  renderPluginLicenseGuide(overview) {
    const section = document.createElement("section");
    section.className = "plugin-license-guide";
    const heading = document.createElement("h3");
    heading.textContent = "Plugin licenses";
    section.appendChild(heading);
    for (const paragraph of overview.licenseGuide || []) {
      const note = document.createElement("p");
      note.className = "account-note";
      note.textContent = paragraph;
      section.appendChild(note);
    }
    return section;
  }

  renderPluginRegister(overview, repositories) {
    const { block, content } = this.pluginSection("Register");
    content.appendChild(this.renderPluginLicenseGuide(overview));
    const options = repositories
      .map(
        (repo) =>
          `<option value="${repo.id}" data-name="${repo.fullName}" data-private="${repo.private ? "1" : "0"}" data-installation="${repo.installationId}">${repo.fullName}</option>`,
      )
      .join("");
    content.innerHTML = `
      <form id="pluginRegisterForm" class="account-form">
        <div class="form-group">
          <label for="pluginRepo">Repository</label>
          <select id="pluginRepo">
            <option value="">Choose a repository</option>
            ${options}
          </select>
        </div>
        <div id="pluginReleaseStatus"></div>
        <div class="plugin-card-actions">
          <button type="button" class="button-base account-button" id="pluginCheckAgain" hidden>Check again</button>
        </div>
        <div class="form-group">
          <label for="pluginKind">Listing</label>
          <select id="pluginKind" disabled>
            <option value="free">Free</option>
            <option value="commercial">Paid</option>
          </select>
        </div>
        <div class="form-group" id="pluginPriceGroup" hidden>
          <label for="pluginPricing">Pricing</label>
          <select id="pluginPricing" disabled>
            <option value="flat">Flat price</option>
            <option value="ppp">Fair price by country (PPP)</option>
          </select>
          <p class="account-note" id="pluginReference" hidden></p>
          <label for="pluginPrice" id="pluginPriceLabel">Price in euros, excluding VAT</label>
          <input id="pluginPrice" type="number" min="0.50" step="0.01" value="5.00" disabled />
          <div id="pluginFloorGroup" hidden>
            <label for="pluginFloor">Lowest price you accept, excluding VAT</label>
            <input id="pluginFloor" type="number" min="1" step="0.01" value="1.00" disabled />
          </div>
          <p class="account-note" id="pluginPricePreview" hidden></p>
          <p class="account-note">Tax is added when a buyer pays.</p>
        </div>
        <p class="account-note" id="pluginPaidNote" hidden>Paid plugins pay out from the <a href="#payouts">Payouts</a> tab. A paid plugin's repository must be private.</p>
        <p class="message error" id="pluginBlock" hidden></p>
        <div class="form-group">
          <label for="pluginLicense">License</label>
          <input id="pluginLicense" type="text" readonly />
          <p class="account-note">From the plugin repository. A free listing cannot use the proprietary license.</p>
        </div>
        <div class="form-group">
          <label for="pluginPackage">Package name</label>
          <input id="pluginPackage" type="text" required readonly />
          <p class="account-note">From the plugin repository. Not editable.</p>
        </div>
        <div class="form-group">
          <label for="pluginIds">Plugin id</label>
          <input id="pluginIds" type="text" readonly />
        </div>
        <div class="form-group">
          <label for="pluginName">Name</label>
          <input id="pluginName" type="text" readonly />
        </div>
        <div class="form-group">
          <label for="pluginDescription">Description</label>
          <textarea id="pluginDescription" rows="3" readonly></textarea>
          <p class="account-note">Name, description, and plugin id come from the repository. Publish a new release to change them.</p>
        </div>
        <details class="plugins-terms">
          <summary>Creator terms</summary>
          <div id="pluginTermsBody"></div>
        </details>
        <label class="account-check"><input id="pluginTerms" type="checkbox" required disabled /> I accept the creator terms</label>
        <div class="plugin-card-actions">
          <button type="submit" class="button-base account-button" disabled>Register plugin</button>
        </div>
        <div id="pluginFormMessage" class="message" hidden></div>
      </form>
    `;
    const form = content.querySelector("#pluginRegisterForm");
    const termsBody = form.querySelector("#pluginTermsBody");
    const termsLead = document.createElement("p");
    termsLead.textContent = overview.terms || "";
    termsBody.appendChild(termsLead);
    for (const paragraph of overview.licenseGuide || []) {
      const note = document.createElement("p");
      note.className = "account-note";
      note.textContent = paragraph;
      termsBody.appendChild(note);
    }
    const kind = form.querySelector("#pluginKind");
    const priceGroup = form.querySelector("#pluginPriceGroup");
    const pricing = form.querySelector("#pluginPricing");
    const floorGroup = form.querySelector("#pluginFloorGroup");
    const reference = form.querySelector("#pluginReference");
    const preview = form.querySelector("#pluginPricePreview");
    const paidNote = form.querySelector("#pluginPaidNote");
    const status = form.querySelector("#pluginReleaseStatus");
    const checkAgain = form.querySelector("#pluginCheckAgain");
    const blockNote = form.querySelector("#pluginBlock");
    const submit = form.querySelector("button[type=submit]");
    const locked = [
      "#pluginKind",
      "#pluginPricing",
      "#pluginPrice",
      "#pluginFloor",
      "#pluginTerms",
    ];
    let releaseOk = false;
    let payoutsReady = Boolean(
      overview.payout?.payouts_enabled && overview.payout?.transfers_active,
    );
    const selectedRepo = () =>
      form.querySelector("#pluginRepo").selectedOptions[0];
    const payoutCountry = overview.payout?.country || "";
    const applyPriceMode = () => {
      const ppp = pricing.value === "ppp";
      floorGroup.hidden = !ppp;
      reference.hidden = !ppp;
      preview.hidden = !ppp;
      form.querySelector("#pluginPrice").min = ppp ? "1" : "0.50";
      form.querySelector("#pluginPriceLabel").textContent = ppp
        ? `Price in ${connectCountryName(payoutCountry) || "your payout country"}, excluding VAT`
        : "Price in euros, excluding VAT";
      reference.textContent = payoutCountry
        ? `Reference country: ${connectCountryName(payoutCountry)} (from your payout account).`
        : "Reference country: finish payout setup first.";
      if (ppp) this.refreshPluginPricePreview(form, payoutCountry);
    };
    const applyForm = () => {
      const paid = kind.value === "commercial";
      priceGroup.hidden = !paid;
      paidNote.hidden = !paid;
      pricing.querySelector('option[value="ppp"]').disabled = !payoutsReady;
      if (!payoutsReady && pricing.value === "ppp") pricing.value = "flat";
      applyPriceMode();
      for (const selector of locked)
        form.querySelector(selector).disabled = !releaseOk;
      const selected = selectedRepo();
      const privateRepo = selected?.dataset.private === "1";
      const license = form.querySelector("#pluginLicense").value;
      blockNote.replaceChildren();
      let blocked = !releaseOk;
      if (releaseOk && paid && !payoutsReady) {
        blocked = true;
        const status = payoutStatusOf(this.pluginOverview?.payout ?? overview.payout);
        blockNote.append(payoutStatusText(status).paid);
        if (status === "not_started" || status === "action_required") {
          const link = document.createElement("a");
          link.href = "#payouts";
          link.textContent = "Open Payouts";
          blockNote.append(" ", link);
        }
      } else if (releaseOk && paid && !privateRepo) {
        blocked = true;
        blockNote.textContent = "A paid plugin's repository must be private.";
      } else if (
        releaseOk &&
        !paid &&
        license === overview.proprietaryLicense
      ) {
        blocked = true;
        blockNote.textContent =
          "A free listing must use a free-software license. Choose Paid, or publish a release with a free license.";
      }
      blockNote.hidden = blockNote.childNodes.length === 0;
      submit.disabled = blocked;
    };
    const runReleaseCheck = async () => {
      const selected = selectedRepo();
      releaseOk = false;
      status.replaceChildren();
      form.querySelector("#pluginPackage").value = "";
      form.querySelector("#pluginLicense").value = "";
      checkAgain.hidden = !selected?.value;
      applyForm();
      if (!selected?.value) return;
      const waiting = document.createElement("p");
      waiting.className = "account-note";
      waiting.textContent = "Checking the latest release…";
      status.appendChild(waiting);
      checkAgain.disabled = true;
      let body = null;
      try {
        const response = await fetch(
          `/api/creator/github/release-check?repo_id=${encodeURIComponent(selected.value)}`,
        );
        const text = await response.text();
        try {
          body = JSON.parse(text);
        } catch {
          body = null;
        }
        if (!response.ok) {
          status.replaceChildren();
          const error = document.createElement("p");
          error.className = "message error";
          error.textContent = text || "The release could not be checked.";
          status.appendChild(error);
          return;
        }
      } catch {
        status.replaceChildren();
        const error = document.createElement("p");
        error.className = "message error";
        error.textContent = "The release could not be checked.";
        status.appendChild(error);
        return;
      } finally {
        checkAgain.disabled = false;
      }
      status.replaceChildren();
      if (!body?.ok) {
        for (const problem of body?.problems || []) {
          const error = document.createElement("p");
          error.className = "message error";
          error.append(`${problem.message} ${problem.fix} `);
          if (problem.guide) {
            const guide = document.createElement("a");
            guide.href = problem.guide;
            guide.target = "_blank";
            guide.rel = "noopener";
            guide.textContent = "Publishing guide";
            error.append(guide);
          }
          status.appendChild(error);
        }
        if (!(body?.problems || []).length) {
          const error = document.createElement("p");
          error.className = "message error";
          error.textContent =
            "This repository's release is not ready to register.";
          status.appendChild(error);
        }
        return;
      }
      releaseOk = true;
      form.querySelector("#pluginPackage").value = body.package || "";
      form.querySelector("#pluginLicense").value = body.license || "";
      form.querySelector("#pluginIds").value = (body.pluginIds || []).join(
        ", ",
      );
      form.querySelector("#pluginName").value = body.name || "";
      form.querySelector("#pluginDescription").value = body.description || "";
      const ready = document.createElement("p");
      ready.className = "account-note";
      ready.textContent = body.tag
        ? `Release ${body.tag} is ready to register.`
        : "The latest release is ready to register.";
      status.appendChild(ready);
      applyForm();
    };
    kind.addEventListener("change", applyForm);
    pricing.addEventListener("change", applyForm);
    form.querySelector("#pluginPrice").addEventListener("input", () => {
      if (pricing.value === "ppp")
        this.refreshPluginPricePreview(form, payoutCountry);
    });
    form.querySelector("#pluginFloor").addEventListener("input", () => {
      if (pricing.value === "ppp")
        this.refreshPluginPricePreview(form, payoutCountry);
    });
    form.querySelector("#pluginRepo").addEventListener("change", () => {
      form.querySelector("#pluginLicense").value = "";
      form.querySelector("#pluginIds").value = "";
      form.querySelector("#pluginName").value = "";
      form.querySelector("#pluginDescription").value = "";
      runReleaseCheck();
    });
    checkAgain.addEventListener("click", runReleaseCheck);
    applyForm();
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const message = form.querySelector("#pluginFormMessage");
      const selected = selectedRepo();
      message.hidden = false;
      if (!releaseOk || !selected?.dataset.name) {
        message.className = "message error";
        message.textContent =
          "Choose a repository whose latest release is ready.";
        return;
      }
      if (kind.value === "commercial") {
        const fresh = await fetch("/api/creator/plugins");
        if (fresh.ok) {
          const body = await fresh.json();
          payoutsReady = Boolean(
            body.payout?.payouts_enabled && body.payout?.transfers_active,
          );
          if (this.pluginOverview)
            this.pluginOverview.payout = body.payout || null;
          applyForm();
        }
      }
      if (kind.value === "commercial" && !payoutsReady) {
        message.className = "message error";
        message.textContent = payoutStatusText(
          payoutStatusOf(this.pluginOverview?.payout ?? overview.payout),
        ).paid;
        submit.disabled = true;
        return;
      }
      if (kind.value === "commercial" && selected.dataset.private !== "1") {
        message.className = "message error";
        message.textContent = "A paid plugin's repository must be private.";
        submit.disabled = true;
        return;
      }
      submit.disabled = true;
      const response = await fetch("/api/creator/plugins", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          acceptTerms: form.querySelector("#pluginTerms").checked,
          package: form.querySelector("#pluginPackage").value.trim(),
          kind: kind.value,
          pricingMode: pricing.value,
          priceCents: Math.round(
            Number(form.querySelector("#pluginPrice").value) * 100,
          ),
          floorCents: Math.round(
            Number(form.querySelector("#pluginFloor").value) * 100,
          ),
          repository: selected.dataset.name,
          githubRepoId: Number(selected.value),
          installationId: Number(selected.dataset.installation),
          private: selected.dataset.private === "1",
        }),
      });
      const text = await response.text();
      let body = {};
      try {
        body = JSON.parse(text);
      } catch {
        body = {};
      }
      message.className = response.ok ? "message success" : "message error";
      message.textContent = response.ok
        ? body.imported?.queued
          ? "Registered. Looking for the latest release."
          : "Registered. Check for a new release to import the wheel."
        : text || "Registration failed";
      if (response.ok) {
        delete block.dataset.touched;
        this.initializePluginsSection();
      } else applyForm();
    });
    const touch = () => (block.dataset.touched = "1");
    form.addEventListener("input", touch);
    form.addEventListener("change", touch);
    return block;
  }

  renderPluginPriceEditor(plugin) {
    const details = document.createElement("details");
    details.className = "plugins-terms";
    const summary = document.createElement("summary");
    summary.textContent = "Edit price";
    details.appendChild(summary);
    const form = document.createElement("form");
    form.className = "account-form";
    const payoutCountry = this.pluginOverview?.payout?.country || "";
    const ppp = plugin.pricing_mode === "ppp";
    form.innerHTML = `
      <div class="form-group">
        <label>Pricing</label>
        <select name="pricing">
          <option value="flat" ${ppp ? "" : "selected"}>Flat price</option>
          <option value="ppp" ${ppp ? "selected" : ""}>Fair price by country (PPP)</option>
        </select>
        <p class="account-note">Reference country: ${this.escapeHtml(connectCountryName(payoutCountry) || "your payout account")}.</p>
        <label>Price excluding VAT</label>
        <input name="price" type="number" min="0.50" step="0.01" value="${(plugin.price_cents / 100).toFixed(2)}" />
        <label>Lowest price you accept, excluding VAT</label>
        <input name="floor" type="number" min="1" step="0.01" value="${(Number(plugin.floor_cents || 100) / 100).toFixed(2)}" />
        <p class="account-note plugin-price-preview" hidden></p>
      </div>
      <button type="submit" class="button-base account-button">Save price</button>
      <p class="message" hidden></p>
    `;
    const pricing = form.querySelector("[name=pricing]");
    const floor = form.querySelector("[name=floor]");
    const preview = form.querySelector(".plugin-price-preview");
    const sync = () => {
      const countryPricing = pricing.value === "ppp";
      floor.hidden = !countryPricing;
      floor.previousElementSibling.hidden = !countryPricing;
      preview.hidden = !countryPricing;
      if (countryPricing) this.refreshPluginPricePreview(form, payoutCountry);
    };
    pricing.addEventListener("change", sync);
    form.querySelector("[name=price]").addEventListener("input", sync);
    floor.addEventListener("input", sync);
    sync();
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const message = form.querySelector(".message");
      const response = await fetch(
        `/api/creator/plugins/${encodeURIComponent(plugin.package)}/pricing`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            pricingMode: pricing.value,
            priceCents: Math.round(
              Number(form.querySelector("[name=price]").value) * 100,
            ),
            floorCents: Math.round(Number(floor.value) * 100),
          }),
        },
      );
      message.hidden = false;
      message.className = response.ok ? "message success" : "message error";
      message.textContent = response.ok
        ? "Price saved."
        : await response.text();
      if (response.ok) this.initializePluginsSection();
    });
    details.appendChild(form);
    return details;
  }

  renderPluginListings(plugins) {
    const { block, content } = this.pluginSection("Your plugins");
    if (plugins.some((plugin) => plugin.kind === "commercial")) {
      const payoutLink = document.createElement("p");
      payoutLink.className = "account-note";
      const payoutAnchor = document.createElement("a");
      payoutAnchor.href = "#payouts";
      payoutAnchor.textContent = "Payouts";
      payoutLink.append(
        "Paid plugins are paid out from the ",
        payoutAnchor,
        " tab.",
      );
      content.appendChild(payoutLink);
    }
    if (!plugins.length) {
      const empty = document.createElement("p");
      empty.textContent = "You have not registered a plugin yet.";
      content.appendChild(empty);
      return block;
    }
    const list = document.createElement("div");
    list.className = "plugin-card-list";
    for (const plugin of plugins) {
      const card = document.createElement("article");
      card.className = "plugin-card";
      const title = document.createElement("h3");
      title.textContent = plugin.name;
      card.appendChild(title);
      const description = document.createElement("p");
      description.textContent = plugin.description || "";
      card.appendChild(description);
      const meta = document.createElement("p");
      meta.className = "plugin-card-meta";
      const price =
        plugin.kind !== "commercial"
          ? "Free"
          : plugin.pricing_mode === "ppp"
            ? `PPP · €${(plugin.price_cents / 100).toFixed(2)} in ${connectCountryName(this.pluginOverview?.payout?.country)} · floor €${(Number(plugin.floor_cents || 0) / 100).toFixed(2)}`
            : `€${(plugin.price_cents / 100).toFixed(2)}`;
      const listing = plugin.status === "live" ? "Live" : "Draft";
      meta.textContent = `${plugin.package} · ${price} · ${listing}`;
      card.appendChild(meta);
      if (plugin.kind === "commercial")
        card.appendChild(this.renderPluginPriceEditor(plugin));
      const versions = document.createElement("div");
      versions.className = "plugin-version-list";
      const importRows = pluginImportRows(plugin);
      if (importRows.length) {
        versions.appendChild(
          this.renderPluginCheckTable("Latest release", importRows),
        );
      } else if (!(plugin.versions || []).length) {
        const empty = document.createElement("p");
        empty.className = "account-note";
        empty.textContent = "No imported version yet.";
        versions.appendChild(empty);
      }
      for (const version of plugin.versions || []) {
        const caption =
          version.status === "ready"
            ? `${version.version} · ${this.versionStatusLabel(version.status)}`
            : version.version;
        const rows = versionReleaseRows(version);
        versions.appendChild(this.renderPluginCheckTable(caption, rows));
        if (rows.some((row) => row.state === "error" && !row.ours)) {
          const next = document.createElement("p");
          next.className = "account-note";
          next.textContent =
            "Fix it, publish a new GitHub release, then check again.";
          versions.appendChild(next);
        }
        if (version.retryable) {
          const again = document.createElement("button");
          again.type = "button";
          again.className = "button-base account-button";
          again.textContent = "Run checks again";
          const againNote = document.createElement("p");
          againNote.className = "message error";
          againNote.hidden = true;
          again.addEventListener("click", async () => {
            again.disabled = true;
            againNote.hidden = true;
            const response = await fetch(
              `/api/creator/plugins/${encodeURIComponent(plugin.package)}/versions/${encodeURIComponent(version.version)}/checks`,
              {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: "{}",
              },
            );
            if (!response.ok) {
              again.disabled = false;
              againNote.textContent = await response.text();
              againNote.hidden = false;
              return;
            }
            this.initializePluginsSection();
          });
          versions.appendChild(again);
          versions.appendChild(againNote);
        }
      }
      card.appendChild(versions);
      const actions = document.createElement("div");
      actions.className = "plugin-card-actions";
      const check = document.createElement("button");
      check.type = "button";
      check.className = "button-base account-button";
      check.textContent = "Check for a new release";
      const checking = pluginReleaseInProgress(plugin);
      check.hidden = checking;
      const checkNote = document.createElement("p");
      checkNote.className = "message error";
      checkNote.hidden = true;
      check.addEventListener("click", async () => {
        check.hidden = true;
        checkNote.hidden = true;
        if (!versions.querySelector(".plugin-check-spin")) {
          versions.prepend(
            this.renderPluginCheckTable("Latest release", [
              {
                state: "run",
                label: "Release",
                detail: "Looking for the latest release.",
              },
            ]),
          );
        }
        const response = await fetch(
          `/api/creator/plugins/${encodeURIComponent(plugin.package)}/check`,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: "{}",
          },
        );
        if (!response.ok) {
          checkNote.textContent = await response.text();
          checkNote.hidden = false;
          check.hidden = false;
          return;
        }
        this.initializePluginsSection();
      });
      actions.appendChild(check);
      actions.appendChild(checkNote);
      const ready = (plugin.versions || []).some(
        (version) => version.status === "ready",
      );
      if (ready && plugin.status !== "live") {
        const publish = document.createElement("button");
        publish.type = "button";
        publish.className = "button-base account-button";
        publish.textContent = "Publish";
        publish.addEventListener("click", async () => {
          publish.disabled = true;
          const response = await fetch(
            `/api/creator/plugins/${encodeURIComponent(plugin.package)}/publish`,
            {
              method: "POST",
              headers: { "content-type": "application/json" },
              body: "{}",
            },
          );
          if (!response.ok) {
            publish.disabled = false;
            const error = document.createElement("p");
            error.className = "message error";
            error.textContent = await response.text();
            card.appendChild(error);
            return;
          }
          this.initializePluginsSection();
        });
        actions.appendChild(publish);
      }
      const neverPublished =
        plugin.status === "draft" &&
        !(plugin.versions || []).some((version) => version.status === "published");
      if (neverPublished) {
        const remove = document.createElement("button");
        remove.type = "button";
        remove.className = "button-base account-button";
        remove.textContent = "Remove plugin";
        const removeNote = document.createElement("p");
        removeNote.className = "message error";
        removeNote.hidden = true;
        remove.addEventListener("click", async () => {
          const confirmed = window.confirm(
            `Remove ${plugin.name || plugin.package}? Its imported releases and check results are deleted. You can register the repository again afterwards.`,
          );
          if (!confirmed) return;
          remove.disabled = true;
          removeNote.hidden = true;
          const response = await fetch(
            `/api/creator/plugins/${encodeURIComponent(plugin.package)}`,
            {
              method: "DELETE",
              headers: JSON_POST_HEADERS,
              body: "{}",
            },
          );
          if (!response.ok) {
            remove.disabled = false;
            removeNote.textContent = await response.text();
            removeNote.hidden = false;
            return;
          }
          this.initializePluginsSection();
        });
        actions.appendChild(remove);
        actions.appendChild(removeNote);
      }
      card.appendChild(actions);
      list.appendChild(card);
    }
    content.appendChild(list);
    return block;
  }

  // Saves the card from the Stripe setup the buyer just finished. The webhook
  // does the same, but may not reach this deployment.
  async finishPaymentSetup(sessionId) {
    try {
      await fetch("/api/billing/setup-complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId }),
      });
    } catch {
      // The webhook can still complete it; the tab shows whatever is saved.
    }
  }

  async renderPaymentTab() {
    const content = document.getElementById("paymentInfo");
    if (!content || content.dataset.loading === "1") return;
    content.dataset.loading = "1";
    content.replaceChildren();
    const waiting = document.createElement("p");
    waiting.className = "account-muted";
    waiting.textContent = "Loading payment method...";
    content.appendChild(waiting);
    if (this.paymentSetupDone) {
      await this.paymentSetupDone;
      this.paymentSetupDone = null;
    }
    let body = null;
    try {
      const response = await fetch("/api/billing/payment-method");
      if (!response.ok) {
        content.replaceChildren();
        const error = document.createElement("p");
        error.className = "message error";
        error.textContent = await response.text();
        content.appendChild(error);
        content.dataset.loading = "";
        return;
      }
      body = await response.json();
    } catch {
      content.replaceChildren();
      const error = document.createElement("p");
      error.className = "message error";
      error.textContent = "The payment method could not be loaded.";
      content.appendChild(error);
      content.dataset.loading = "";
      return;
    }
    content.replaceChildren();
    content.dataset.loading = "";
    const params = new URLSearchParams(window.location.search);
    if (params.get("return") === "editor" && body.ready) {
      const back = document.createElement("p");
      back.className = "account-note";
      back.textContent = "You can close this tab and return to the editor.";
      content.appendChild(back);
    }
    const summary = document.createElement("p");
    summary.textContent = body.ready
      ? `${this.paymentMethodLabel(body)} · ${this.countryName(body.country)}${body.taxId ? ` · ${body.taxId}` : ""}`
      : "Add a payment method and billing address to see plugin prices and buy in one click.";
    content.appendChild(summary);
    const action = document.createElement("button");
    action.type = "button";
    action.className = "button-base account-button";
    action.textContent = body.ready ? "Change" : "Add payment method";
    action.addEventListener("click", () =>
      this.startPaymentSetup(action, params.get("return") === "editor"),
    );
    content.appendChild(action);
  }

  countryName(code) {
    if (!code) return "";
    try {
      return new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code;
    } catch {
      return code;
    }
  }

  paymentMethodLabel(body) {
    const email = body.email ? ` (${body.email})` : "";
    if (body.type === "paypal") return `PayPal${email}`;
    if (body.type === "link") return `Link${email}`;
    if (body.type === "revolut_pay") return "Revolut Pay";
    if (body.type === "amazon_pay") return "Amazon Pay";
    if (body.type === "klarna") return "Klarna";
    if (body.wallet === "apple_pay")
      return this.cardLabel("Apple Pay", body.last4);
    if (body.wallet === "google_pay")
      return this.cardLabel("Google Pay", body.last4);
    return this.cardLabel(body.brand, body.last4);
  }

  async renderPurchasesTab() {
    const content = document.getElementById("purchasesInfo");
    if (!content || content.dataset.loading === "1") return;
    content.dataset.loading = "1";
    content.replaceChildren();
    const waiting = document.createElement("p");
    waiting.className = "account-muted";
    waiting.textContent = "Loading purchases...";
    content.appendChild(waiting);
    let body = null;
    try {
      const response = await fetch("/api/billing/purchases");
      if (!response.ok) throw new Error(await response.text());
      body = await response.json();
    } catch (error) {
      content.replaceChildren();
      const message = document.createElement("p");
      message.className = "message error";
      message.textContent = error.message || "Purchases could not be loaded.";
      content.appendChild(message);
      content.dataset.loading = "";
      return;
    }
    content.replaceChildren();
    content.dataset.loading = "";
    const note = document.createElement("p");
    note.className = "account-muted";
    note.textContent =
      "Purchases are one-time payments, separate from your subscription.";
    content.appendChild(note);
    if (!(body.purchases || []).length) {
      const empty = document.createElement("p");
      empty.textContent = "You have not bought a plugin yet.";
      content.appendChild(empty);
      return;
    }
    const list = document.createElement("ul");
    list.className = "plugin-version-list";
    for (const purchase of body.purchases) {
      const item = document.createElement("li");
      const when = purchase.createdAt
        ? new Date(purchase.createdAt).toLocaleDateString()
        : "";
      const gross = `€${(purchase.grossCents / 100).toFixed(2)}`;
      item.append(
        `${purchase.name} · ${gross} incl. €${(purchase.taxCents / 100).toFixed(2)} VAT${when ? ` · ${when}` : ""} · ${purchase.status} `,
      );
      if (purchase.invoicePdf) {
        const link = document.createElement("a");
        link.href = purchase.invoicePdf;
        link.textContent = "View invoice";
        item.append(link, " ");
      }
      if (purchase.creditNotePdf) {
        const link = document.createElement("a");
        link.href = purchase.creditNotePdf;
        link.textContent = "Credit note";
        item.append(link, " ");
      }
      if (purchase.refund?.eligible) {
        const until = purchase.refund.until
          ? new Date(purchase.refund.until).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
            })
          : "";
        const button = document.createElement("button");
        button.type = "button";
        button.className = "button-base account-button";
        button.textContent = until ? `Refundable until ${until}` : "Refund";
        button.addEventListener("click", () => {
          const confirmed = window.confirm(
            `Refund ${gross}? ${purchase.name} will be removed from Counterpunch the next time the editor checks your purchases.`,
          );
          if (!confirmed) return;
          button.disabled = true;
          void fetch("/api/plugins/refund", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ package: purchase.package }),
          }).then(async (response) => {
            if (!response.ok) {
              const payload = await response.json().catch(() => ({}));
              button.disabled = false;
              this.showAuthError(
                payload.error || "The refund could not be completed.",
              );
              return;
            }
            content.dataset.loading = "";
            this.renderPurchasesTab();
          });
        });
        item.append(button);
      } else if (purchase.refund?.reason && purchase.status === "paid") {
        item.append(purchase.refund.reason);
      } else if (purchase.status === "refunded") {
        item.append("Refunded");
      }
      list.appendChild(item);
    }
    content.appendChild(list);
  }

  cardLabel(brand, last4) {
    const name = brand
      ? brand.charAt(0).toUpperCase() + brand.slice(1)
      : "Card";
    return last4 ? `${name} ·· ${last4}` : name;
  }

  async startPaymentSetup(button, returnToEditor) {
    button.disabled = true;
    const response = await fetch("/api/billing/payment-method", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ returnTo: returnToEditor ? "editor" : "" }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload.url) {
      button.disabled = false;
      this.showAuthError(payload.error || "Payment setup could not start.");
      return;
    }
    window.location.href = payload.url;
  }

  renderPluginPayouts(overview) {
    const host = document.createElement("section");
    host.className = "plugins-payouts";
    const enabled =
      overview.payout?.payouts_enabled && overview.payout?.transfers_active;
    const status = payoutStatusOf(overview.payout);
    host.dataset.payoutStatus = status;
    const sentence = document.createElement("p");
    if (status === "in_review" || status === "rejected")
      sentence.className = status === "rejected" ? "message error" : "message";
    sentence.textContent = payoutStatusText(status).summary;
    host.appendChild(sentence);
    if (overview.payout?.country) {
      const reference = document.createElement("p");
      reference.className = "account-note";
      reference.textContent = `Payout country: ${connectCountryName(overview.payout.country)}. Country-based plugin prices use this country.`;
      host.appendChild(reference);
    }
    if (status === "in_review" || status === "rejected") {
      // Nothing for the creator to fill in. Stripe's onboarding form would
      // only repeat that the details are submitted.
    } else if (!enabled) {
      let countrySelect = null;
      if (!overview.payout) {
        const group = document.createElement("div");
        group.className = "form-group";
        const label = document.createElement("label");
        label.htmlFor = "payoutCountry";
        label.textContent = "Payout country";
        countrySelect = document.createElement("select");
        countrySelect.id = "payoutCountry";
        const empty = document.createElement("option");
        empty.value = "";
        empty.textContent = "Choose your country";
        countrySelect.appendChild(empty);
        for (const [code, name] of CONNECT_PAYOUT_COUNTRIES) {
          const option = document.createElement("option");
          option.value = code;
          option.textContent = name;
          countrySelect.appendChild(option);
        }
        const note = document.createElement("p");
        note.className = "message warning";
        note.textContent =
          "The payout country cannot be changed. Choose the country where you will be paid.";
        group.append(label, countrySelect, note);
        host.appendChild(group);
      }
      const start = document.createElement("button");
      start.type = "button";
      start.className = "button-base account-button";
      start.textContent = overview.payout
        ? "Continue payout setup"
        : "Set up payouts";
      if (countrySelect) start.disabled = true;
      countrySelect?.addEventListener("change", () => {
        start.disabled = !countrySelect.value;
        host.dataset.payoutSetup = "active";
      });
      const editor = document.createElement("div");
      editor.className = "plugins-payout-editor";
      start.addEventListener("click", () =>
        this.mountPluginPayouts(
          editor,
          overview,
          countrySelect?.value || overview.payout?.country,
          ["account-onboarding"],
        ),
      );
      host.append(start, editor);
    } else {
      const canEdit = document.createElement("p");
      canEdit.className = "account-note";
      canEdit.textContent = `You can update your name, address, and bank account. The payout country stays ${connectCountryName(overview.payout.country)}.`;
      host.appendChild(canEdit);
      const actions = document.createElement("div");
      actions.className = "plugin-card-actions";
      const update = document.createElement("button");
      update.type = "button";
      update.className = "button-base account-button";
      update.textContent = "Update payout details";
      const money = document.createElement("button");
      money.type = "button";
      money.className = "button-base account-button";
      money.textContent = "Balance and payouts";
      actions.append(update, money);
      host.appendChild(actions);
      const editor = document.createElement("div");
      editor.className = "plugins-payout-editor";
      host.appendChild(editor);
      update.addEventListener("click", () =>
        this.mountPluginPayouts(editor, overview, null, ["account-management"]),
      );
      money.addEventListener("click", () =>
        this.mountPluginPayouts(editor, overview, null, [
          "balances",
          "payouts",
        ]),
      );
    }
    return host;
  }

  async mountPluginPayouts(host, overview, country, names) {
    host
      .closest(".plugins-payouts")
      ?.setAttribute("data-payout-setup", "active");
    host.replaceChildren();
    const waiting = document.createElement("p");
    waiting.className = "account-note";
    waiting.textContent = "Opening payout setup…";
    host.appendChild(waiting);
    if (!overview.payout) {
      const created = await fetch("/api/creator/stripe/account", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ country }),
      });
      if (!created.ok) {
        host.replaceChildren();
        const error = document.createElement("p");
        error.className = "message error";
        error.textContent = await created.text();
        host.appendChild(error);
        return;
      }
      overview.payout = await created.json();
    }
    if (!window.StripeConnect) {
      await new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://connect-js.stripe.com/v1.0/connect.js";
        script.onload = resolve;
        script.onerror = () => reject(new Error("Could not load Stripe"));
        document.head.appendChild(script);
      });
    }
    let instance;
    try {
      instance = window.StripeConnect.init({
        publishableKey: overview.publishableKey,
        fetchClientSecret: async () => {
          const response = await fetch("/api/creator/stripe/account-session", {
            method: "POST",
            headers: JSON_POST_HEADERS,
            body: "{}",
          });
          const body = await response.json().catch(() => ({}));
          if (!response.ok || !body.client_secret) {
            throw new Error(body.error || "Payout setup could not start.");
          }
          if (!overview.publishableKey && body.publishableKey)
            overview.publishableKey = body.publishableKey;
          return body.client_secret;
        },
      });
    } catch (error) {
      host.replaceChildren();
      const message = document.createElement("p");
      message.className = "message error";
      message.textContent =
        error instanceof Error
          ? error.message
          : "Payout setup could not start.";
      host.appendChild(message);
      return;
    }
    const labels = {
      "account-onboarding": "Set up payouts",
      "account-management": "Payout details",
      balances: "Balance",
      payouts: "Payouts",
    };
    host.replaceChildren();
    for (const name of names) {
      const heading = document.createElement("h3");
      heading.textContent = labels[name] || name;
      host.append(heading, instance.create(name));
    }
  }

  async pollSubscriptionSetup() {
    const started = Date.now();
    const tick = async () => {
      await this.initializeSubscriptionSection();
      const sub = this.accountMembership;
      const credits = this.accountCredits;
      const includedCreditReady =
        sub?.isSponsored ||
        credits?.isUnlimited ||
        (Number(credits?.allowanceCents) > 0 &&
          Number(credits?.balanceCents) >= Number(credits.allowanceCents));
      if (
        sub &&
        (sub.status === "active" || sub.status === "trialing") &&
        includedCreditReady
      ) {
        this.showAuthSuccess("Your subscription is active.");
        return;
      }
      if (sub && (sub.status === "incomplete" || sub.status === "past_due")) {
        this.showAuthError(
          "The first payment did not go through. Update your payment method to finish subscribing.",
        );
        return;
      }
      if (Date.now() - started > 15000) {
        this.showAuthError(
          "Your subscription is still being set up. Refresh this page in a moment.",
        );
        return;
      }
      setTimeout(tick, 2000);
    };
    setTimeout(tick, 2000);
  }

  cloudUsageOverPlan(usage, productId) {
    const product = getPricingProducts()[productId];
    if (!usage || !product) {
      return { fonts: false, glyphs: false, invitees: false };
    }
    return {
      fonts:
        product.maxFontsOwned != null &&
        usage.fontCount > product.maxFontsOwned,
      glyphs:
        product.maxGlyphsPerFont != null &&
        (usage.highestGlyphs?.glyphCount || 0) > product.maxGlyphsPerFont,
      invitees:
        product.maxInvitesPerAsset != null &&
        (usage.highestInvitees?.inviteeCount || 0) > product.maxInvitesPerAsset,
    };
  }

  downgradeAlertHtml(usage, over) {
    const lines = [
      {
        over: over.fonts,
        text: `${Number(usage?.fontCount || 0).toLocaleString("en-US")} ${
          usage?.fontCount === 1 ? "font" : "fonts"
        }`,
      },
    ];
    if (usage?.highestGlyphs) {
      lines.push({
        over: over.glyphs,
        text: `${usage.highestGlyphs.name} — ${Number(
          usage.highestGlyphs.glyphCount,
        ).toLocaleString("en-US")} glyphs`,
      });
    }
    if (usage?.highestInvitees) {
      lines.push({
        over: over.invitees,
        text: `${usage.highestInvitees.name} — ${Number(
          usage.highestInvitees.inviteeCount,
        ).toLocaleString("en-US")} invitees`,
      });
    }
    return `<div class="pricing-downgrade-alert">${lines
      .map(
        (line) =>
          `<div class="${line.over ? "is-over" : ""}">${this.escapeHtml(line.text)}</div>`,
      )
      .join("")}</div>`;
  }

  updatePlanChangeButtons() {
    const state = this.pricingChangeState;
    if (!state) return;
    const rank = ["basic", "advanced", "pro", "enterprise"];
    const toggle = document.getElementById("billingToggle");
    const selectedPeriod = toggle?.checked ? "yearly" : "monthly";
    document
      .querySelectorAll(".pricing-downgrade-alert")
      .forEach((node) => node.remove());
    document.querySelectorAll(".pricing-subscribe").forEach((btn) => {
      const productId = btn.dataset.product || "basic";
      btn.style.display = "";
      btn.disabled = false;
      if (!state.hasActiveSubscription) {
        btn.textContent = paidPlanCard.buttonText;
        if (state.isLoggedIn) {
          btn.onclick = () => window.appNavigator.startSubscription(productId);
        }
        return;
      }
      const samePlan = productId === state.currentPricingPlan;
      const samePeriod = selectedPeriod === state.billingPeriod;
      if (samePlan && samePeriod) {
        btn.disabled = true;
        btn.textContent = "Current plan";
        btn.onclick = null;
        return;
      }
      const currentRank = rank.indexOf(state.currentPricingPlan);
      const targetRank = rank.indexOf(productId);
      if (!samePlan && targetRank > currentRank) {
        btn.textContent = "Upgrade";
        btn.onclick = () => window.appNavigator.startPlanChange(productId);
        return;
      }
      if (!samePlan && targetRank < currentRank) {
        btn.textContent = "Downgrade";
        const over = this.cloudUsageOverPlan(this.pricingCloudUsage, productId);
        const blocked = over.fonts || over.glyphs || over.invitees;
        btn.disabled = blocked;
        btn.onclick = blocked
          ? null
          : () => window.appNavigator.startPlanChange(productId);
        if (blocked) {
          btn.insertAdjacentHTML(
            "afterend",
            this.downgradeAlertHtml(this.pricingCloudUsage, over),
          );
        }
        return;
      }
      btn.textContent =
        selectedPeriod === "yearly" ? "Switch to yearly" : "Switch to monthly";
      btn.onclick = () => window.appNavigator.startPlanChange(productId);
    });
  }

  renderCloudFonts(usage) {
    const info = document.getElementById("cloudFontsInfo");
    if (!info) return;
    if (!usage) {
      info.innerHTML = `<p style="color: var(--text-secondary)">Cloud font usage is unavailable.</p>`;
      return;
    }
    const rows = cloudFontAccountRows(usage)
      .map(
        (row) => `
        <div class="subscription-plan-row">
          <span class="subscription-plan-label">${this.escapeHtml(row.label)}</span>
          <span class="subscription-plan-value">${this.escapeHtml(row.value)}</span>
        </div>`,
      )
      .join("");
    info.innerHTML = `<div class="subscription-plan-info">${rows}</div>`;
    void this.loadCloudFontList();
  }

  async loadCloudFontList() {
    const list = document.getElementById("cloudFontList");
    if (!list) return;
    try {
      const response = await fetch("/api/cloud/assets", {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to load Cloud fonts");
      }
      const data = await response.json();
      const owned = (data.assets || []).filter(
        (asset) => asset.role === "owner",
      );
      if (!owned.length) {
        list.innerHTML = `<p style="color: var(--text-secondary)">No Cloud fonts.</p>`;
        return;
      }
      list.innerHTML = owned
        .map(
          (asset) => `
          <div class="subscription-plan-row">
            <span class="subscription-plan-label">${this.escapeHtml(asset.name || "Untitled font")}</span>
            <button class="button-base subscription-button danger" type="button" data-cloud-font-delete="${this.escapeHtml(asset.id)}" data-cloud-font-name="${this.escapeHtml(asset.name || "Untitled font")}">Delete</button>
          </div>`,
        )
        .join("");
      list.querySelectorAll("[data-cloud-font-delete]").forEach((button) => {
        button.addEventListener("click", () => {
          this.deleteCloudFont(
            button.getAttribute("data-cloud-font-delete"),
            button.getAttribute("data-cloud-font-name"),
          );
        });
      });
    } catch (error) {
      console.error("Failed to load Cloud fonts:", error);
      list.innerHTML = `<p style="color: var(--text-secondary)">Cloud fonts are unavailable.</p>`;
    }
  }

  async deleteCloudFont(assetId, name) {
    if (!assetId) return;
    const confirmed = confirm(
      `Delete "${name || "this font"}" from Cloud permanently? This removes the Cloud copy for everyone and cannot be undone.`,
    );
    if (!confirmed) return;
    const list = document.getElementById("cloudFontList");
    if (list) {
      list.querySelectorAll("[data-cloud-font-delete]").forEach((button) => {
        button.disabled = true;
      });
    }
    try {
      // The server purges in bounded batches and answers complete:false until
      // done; poll for up to 15 minutes (the hourly cron finishes the rest).
      const deadline = Date.now() + 15 * 60 * 1000;
      let finished = false;
      let transportFailures = 0;
      for (let attempt = 0; Date.now() < deadline; attempt += 1) {
        if (attempt > 0)
          await new Promise((resolve) => setTimeout(resolve, 150));
        let response;
        try {
          response = await fetch(
            `/api/cloud/assets/${encodeURIComponent(assetId)}`,
            {
              method: "DELETE",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
            },
          );
        } catch (error) {
          transportFailures += 1;
          if (transportFailures >= 5) throw error;
          continue;
        }
        transportFailures = 0;
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || "Failed to delete Cloud font");
        }
        if (data.complete !== false) {
          finished = true;
          break;
        }
      }
      if (!finished) {
        throw new Error(
          "The delete is still running on the server and will finish in the background.",
        );
      }
      await this.initializeSubscriptionSection();
    } catch (error) {
      console.error("Failed to delete Cloud font:", error);
      alert(
        error instanceof Error ? error.message : "Failed to delete Cloud font",
      );
      await this.loadCloudFontList();
    }
  }

  /**
   * Initialize subscription section
   */
  async initializeSubscriptionSection() {
    const subscriptionInfo = document.getElementById("subscriptionInfo");
    const creditsSection = document.getElementById("creditsSection");
    const creditsInfo = document.getElementById("creditsInfo");

    if (!subscriptionInfo) return;

    try {
      console.log("Fetching subscription status...");
      const response = await fetch("/api/subscription/status");

      if (!response.ok) {
        throw new Error("Failed to load subscription status");
      }

      const data = await response.json();
      console.log("Subscription data:", data);
      this.accountMembership = data.membership || data.subscription || null;
      this.accountCredits = data.credits || null;
      this.pricingCloudUsage = data.cloudUsage || null;
      this.renderCloudFonts(data.cloudUsage || null);

      if (data.hasSubscription) {
        const sub = data.subscription;
        const isSponsored = !!sub.isSponsored;
        const statusBadge = isSponsored
          ? '<span class="subscription-trial-badge">Sponsored</span>'
          : "";
        const periodEnd = sub.currentPeriodEnd
          ? new Date(sub.currentPeriodEnd * 1000).toLocaleDateString()
          : null;
        const paymentProblem =
          sub.status === "past_due" || sub.status === "incomplete";
        const periodWord = sub.billingPeriod === "yearly" ? "year" : "month";
        const dateLabel = sub.cancelAtPeriodEnd
          ? "Access until"
          : "Next billing date";

        let billingRows = "";
        if (sub.showPriceDetails && sub.priceCents != null) {
          billingRows = `
            <div class="subscription-plan-row">
              <span class="subscription-plan-label">Price</span>
              <span class="subscription-plan-value">€${(sub.priceCents / 100).toFixed(2)}/${periodWord} excl. tax</span>
            </div>
            <div class="subscription-plan-row">
              <span class="subscription-plan-label">Billing country</span>
              <span class="subscription-plan-value">${this.escapeHtml(sub.countryCode || "")}</span>
            </div>
            <div class="subscription-plan-row">
              <span class="subscription-plan-label">${dateLabel}</span>
              <span class="subscription-plan-value">${periodEnd || ""}</span>
            </div>
          `;
        } else if (periodEnd) {
          billingRows = `
            <div class="subscription-plan-row">
              <span class="subscription-plan-label">Access until</span>
              <span class="subscription-plan-value">${periodEnd}</span>
            </div>
          `;
        }
        if (paymentProblem) {
          billingRows += `
            <div class="subscription-plan-row">
              <span class="subscription-plan-label">Payment</span>
              <span class="subscription-plan-value" style="color: #ff4444;">Payment failed. Update your card to keep the subscription.</span>
            </div>
          `;
        }
        if (data.planCredit) {
          billingRows += `
            <div class="subscription-plan-row">
              <span class="subscription-plan-label">Plan credit</span>
              <span class="subscription-plan-value">${data.planCredit.balance}</span>
            </div>
          `;
        }

        const actionsMarkup = sub.showBillingControls
          ? `
            <div class="subscription-actions">
              ${
                paymentProblem
                  ? '<button class="button-base subscription-button" onclick="window.appNavigator.openCustomerPortal()">Update payment method</button>'
                  : ""
              }
              ${
                sub.cancelAtPeriodEnd
                  ? '<button class="button-base subscription-button" onclick="window.appNavigator.reactivateSubscription()">Reactivate Subscription</button>'
                  : '<button class="button-base subscription-button danger" onclick="window.appNavigator.cancelSubscription()">Cancel Subscription</button>'
              }
            </div>
          `
          : '<p style="color: var(--text-secondary); margin: 16px 0 0 0;">This membership is managed internally.</p>';

        subscriptionInfo.innerHTML = `
          <div class="subscription-plan-info">
            <div class="subscription-plan-row">
              <span class="subscription-plan-label">Plan</span>
              <span class="subscription-plan-value">${this.escapeHtml(sub.planName || "Membership")}${
                sub.billingPeriod
                  ? ` (${sub.billingPeriod === "yearly" ? "Yearly" : "Monthly"})`
                  : ""
              }${statusBadge}</span>
            </div>
            ${billingRows}
            ${
              sub.cancelAtPeriodEnd && sub.showBillingControls
                ? `
            <div class="subscription-plan-row">
              <span class="subscription-plan-label">Status</span>
              <span class="subscription-plan-value">Cancels on ${periodEnd}. Access continues until then.</span>
            </div>
            `
                : ""
            }
          </div>
          ${actionsMarkup}
        `;

        if (creditsSection && creditsInfo) {
          creditsSection.classList.remove("hidden");

          if (sub.isSponsored || data.credits.isUnlimited) {
            creditsInfo.innerHTML = `
              <div class="credits-balance">
                <span class="credits-amount">Included</span>
                <span class="credits-label">AI agent usage is covered by Counterpunch</span>
              </div>
            `;
          } else {
            const allowance =
              data.credits.allowanceCents != null
                ? `€${(data.credits.allowanceCents / 100).toFixed(2)} included this period`
                : "";
            const unbilledNote =
              sub.billingPeriod === "yearly"
                ? "This is invoiced on its own once a month."
                : "This is added to your next monthly invoice.";
            const unbilled =
              data.credits.unreportedOverageCents > 0
                ? `<div class="credits-overage-help">Unbilled usage beyond credit: €${(
                    data.credits.unreportedOverageCents / 100
                  ).toFixed(2)}. ${unbilledNote}</div>`
                : "";
            const yearlyNote =
              sub.billingPeriod === "yearly"
                ? `<div class="credits-overage-help">Your included credit lasts until ${periodEnd || "the end of the yearly period"}.</div>`
                : "";

            creditsInfo.innerHTML = `
              <div class="credits-balance">
                <span class="credits-amount">${data.credits.balance}</span>
                <span class="credits-label">available credits${allowance ? ` · ${allowance}` : ""}</span>
              </div>
              ${yearlyNote}
              ${unbilled}
              <div class="credits-overage-setting">
                <label>
                  <input
                    type="checkbox"
                    id="overageAllowed"
                    ${data.credits.overageAllowed ? "checked" : ""}
                    onchange="window.appNavigator.updateOverageSetting(this.checked)"
                  />
                  <span>Enable pay-as-you-go when credits run out</span>
                </label>
                <div class="credits-overage-help">
                  When enabled, usage beyond your included credit is invoiced monthly. Otherwise, AI agent features stop at €0 until the next credit grant.
                </div>
              </div>
            `;
          }
        }
      } else {
        this.accountMembership = null;
        const previous = data.previousSubscription;
        const endedOn = previous?.currentPeriodEnd
          ? new Date(previous.currentPeriodEnd * 1000).toLocaleDateString()
          : null;
        if (creditsSection && creditsInfo) {
          creditsSection.classList.remove("hidden");
          creditsInfo.innerHTML = `
            <div class="credits-balance">
              <span class="credits-amount">${data.credits.balance}</span>
              <span class="credits-label">free monthly AI agent credit</span>
            </div>
          `;
        }
        subscriptionInfo.innerHTML = `
          <p style="color: var(--text-secondary); margin-bottom: 16px;">
            ${
              previous
                ? `${this.escapeHtml(previous.planName || "Membership")} ended${endedOn ? ` on ${endedOn}` : ""}.`
                : "You don't have an active subscription."
            }
          </p>
          ${
            data.planCredit
              ? `<div class="subscription-plan-row">
                  <span class="subscription-plan-label">Plan credit</span>
                  <span class="subscription-plan-value">${data.planCredit.balance}</span>
                </div>`
              : ""
          }
          <a href="/pricing" class="button-base subscription-button">View Pricing Plans</a>
        `;
      }
      if (data.billingTestMode) {
        subscriptionInfo.insertAdjacentHTML(
          "beforeend",
          `<div class="subscription-actions" style="margin-top: 20px;">
            <button class="button-base subscription-button danger" type="button" onclick="window.appNavigator.resetTestSubscription()">Reset test subscription</button>
            <p style="color: var(--text-secondary); margin: 8px 0 0;">Deletes this test subscription and its Stripe customer, and restores the free €${Number(getFreeTrialProduct().freeCredits).toFixed(2)} AI credit.</p>
          </div>`,
        );
      }
    } catch (error) {
      console.error("Error loading subscription:", error);
      this.accountMembership = null;
      this.renderCloudFonts(null);
      subscriptionInfo.innerHTML = `
        <p style="color: #ff4444;">Failed to load subscription information. Please try again.</p>
      `;
    }
  }

  /**
   * Get pricing product configuration
   */
  getPricingProducts() {
    return getPricingProducts(); // Use exported function
  }

  /**
   * Generate pricing card HTML
   */
  generatePricingCardHTML(
    productId,
    elementIdPrefix = null,
    hasActiveSubscription = false,
  ) {
    const products = this.getPricingProducts();
    const product = products[productId];

    if (!product) {
      console.error(`Unknown product: ${productId}`);
      return "";
    }

    // Default to productId if no prefix specified
    const prefix = elementIdPrefix !== null ? elementIdPrefix : productId;

    const priceId = prefix + "Price";
    const periodId = prefix + "Period";
    const creditAmountId = prefix + "CreditAmount";
    const monthlyPrice = product.pricing.monthly.base.toFixed(2);
    const monthlyCredit = (
      product.pricing.monthly.base * product.creditShare
    ).toFixed(2);

    // Determine button behavior based on login status
    const isLoggedIn = this.isAuthenticated();
    const buttonOnClick = isLoggedIn
      ? `window.appNavigator.startSubscription()`
      : `window.appNavigator.redirectToLogin()`;

    // Hide button if user has active subscription
    const buttonHTML = hasActiveSubscription
      ? ""
      : `
          <button 
            class="pricing-button ${paidPlanCard.buttonClass}" 
            id="startSubscriptionBtn"
            onclick="${buttonOnClick}"
          >
            ${paidPlanCard.buttonText}
          </button>`;

    return `
      <div class="pricing-card pricing-card-paid">
        <div class="pricing-badge">${product.badge}</div>
        
        <div class="pricing-card-header">
          <h2>${product.name}</h2>
          <div class="pricing-prices" id="${priceId}_prices">
            <div class="pricing-price-line">
              <div class="pricing-price-label" id="${priceId}_consumerLabel" hidden>End-consumer price</div>
              <div class="pricing-price">
                <span class="pricing-amount" id="${priceId}">€${monthlyPrice}</span>
                <span class="pricing-period" id="${periodId}">per month</span>
                <span class="pricing-vat-badge" id="${priceId}_vat" hidden>incl. VAT</span>
              </div>
            </div>
            <div class="pricing-price-line pricing-price-line-business" id="${priceId}_business" hidden>
              <div class="pricing-price-label">Business price<br />(VAT ID is added at checkout)</div>
              <div class="pricing-price">
                <span class="pricing-amount" id="${priceId}_businessAmount"></span>
                <span class="pricing-period" id="${priceId}_businessPeriod">per month</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="pricing-card-content">
          <ul class="pricing-features">
            ${paidPlanCard.features
              .map((feature) => {
                if (feature.dynamic) {
                  return `
                    <li>
                      <span class="material-symbols-outlined pricing-${
                        feature.icon === "check_circle" ? "check" : "x"
                      }">${feature.icon}</span>
                      <span id="${creditAmountId}">€${monthlyCredit} ${
                        feature.text
                      } per month</span>
                    </li>
                  `;
                } else {
                  return `
                    <li>
                      <span class="material-symbols-outlined pricing-${
                        feature.icon === "check_circle" ? "check" : "x"
                      }">${feature.icon}</span>
                      <span>${feature.text}</span>
                    </li>
                  `;
                }
              })
              .join("")}
          </ul>
          ${buttonHTML}
          <div class="pricing-cloud">
            <div class="pricing-divider">Online Collaboration</div>
            <ul class="pricing-features">
              <li>
                <span class="material-symbols-outlined pricing-check">check_circle</span>
                <span>${product.maxFontsOwned.toLocaleString("en-US")} cloud ${product.maxFontsOwned === 1 ? "font" : "fonts"}</span>
              </li>
              <li>
                <span class="material-symbols-outlined pricing-check">check_circle</span>
                <span>${product.maxInvitesPerAsset.toLocaleString("en-US")} ${product.maxInvitesPerAsset === 1 ? "invitee" : "invitees"} per font</span>
              </li>
              <li>
                <span class="material-symbols-outlined pricing-check">check_circle</span>
                <span>${product.maxGlyphsPerFont.toLocaleString("en-US")} glyphs per font</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Start subscription checkout
   */
  async startSubscription(productId = "basic") {
    const btn = document.querySelector(
      `.pricing-subscribe[data-product="${productId}"]`,
    );
    if (!btn) return;

    const toggle = document.getElementById("billingToggle");
    const billingPeriod = toggle?.checked ? "yearly" : "monthly";

    btn.disabled = true;
    btn.textContent = "Creating checkout session...";

    try {
      const response = await fetch("/api/subscription/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ billingPeriod, productId }),
      });

      const data = await response.json();

      if (response.ok && data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error(data.error || "Failed to create checkout session");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      this.showAuthError(error.message);
      btn.disabled = false;
      btn.textContent = paidPlanCard.buttonText;
    }
  }

  async startPlanChange(productId) {
    const products = getPricingProducts();
    const product = products[productId];
    const toggle = document.getElementById("billingToggle");
    const billingPeriod = toggle?.checked ? "yearly" : "monthly";
    const periodLabel = billingPeriod === "yearly" ? "yearly" : "monthly";
    if (
      !confirm(
        `Switch to ${product?.name || "this plan"} (${periodLabel})? Unused time on your current plan becomes plan credit and pays the new period, which starts today.`,
      )
    ) {
      return;
    }
    const btn = document.querySelector(
      `.pricing-subscribe[data-product="${productId}"]`,
    );
    if (btn) {
      btn.disabled = true;
      btn.textContent = "Switching...";
    }
    try {
      const response = await fetch("/api/subscription/change", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ billingPeriod, productId }),
      });
      const data = await response.json();
      if (response.status === 409) {
        this.pricingCloudUsage = data.cloudUsage || this.pricingCloudUsage;
        this.updatePlanChangeButtons();
        throw new Error(
          data.error || "This plan is below your current cloud usage.",
        );
      }
      if (!response.ok) {
        throw new Error(data.error || "Failed to change plan");
      }
      if (data.paymentFailed) {
        this.showAuthError(
          "Your plan was changed, but the card payment failed. Update your payment method to pay the rest.",
        );
      } else {
        this.showAuthSuccess(
          "Your plan was changed. The new period starts today.",
        );
      }
      if (this.pricingChangeState) {
        this.pricingChangeState.currentPricingPlan = data.productId;
        this.pricingChangeState.billingPeriod = data.billingPeriod;
        this.pricingChangeState.hasActiveSubscription = true;
      }
      this.updatePlanChangeButtons();
      this.updatePricingSubscriptionState({
        isLoggedIn: true,
        currentPricingPlan: data.productId,
        hasActiveSubscription: true,
        currentSubscription: { productId: data.productId },
      });
      this.initializeSubscriptionSection();
    } catch (error) {
      console.error("Plan change error:", error);
      this.showAuthError(error.message);
      this.updatePlanChangeButtons();
    }
  }

  /**
   * Open Stripe Customer Portal
   */
  async openCustomerPortal() {
    try {
      const response = await fetch("/api/subscription/portal", {
        method: "POST",
        headers: JSON_POST_HEADERS,
        body: "{}",
      });

      const data = await response.json();

      if (response.ok && data.url) {
        // Redirect to Stripe Customer Portal
        window.location.href = data.url;
      } else {
        throw new Error(data.error || "Failed to open customer portal");
      }
    } catch (error) {
      console.error("Portal error:", error);
      this.showAuthError(error.message);
    }
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription() {
    if (
      !confirm(
        "Are you sure you want to cancel your subscription? You'll still have access until the end of your billing period.",
      )
    ) {
      return;
    }

    try {
      const response = await fetch("/api/subscription/cancel", {
        method: "POST",
        headers: JSON_POST_HEADERS,
        body: "{}",
      });

      const data = await response.json();

      if (response.ok) {
        this.showAuthSuccess(data.message);
        // Reload subscription info
        this.initializeSubscriptionSection();
      } else {
        throw new Error(data.error || "Failed to cancel subscription");
      }
    } catch (error) {
      console.error("Cancel error:", error);
      this.showAuthError(error.message);
    }
  }

  async resetTestSubscription() {
    if (
      !confirm(
        `Delete the test subscription and its Stripe customer? AI credit returns to the free €${Number(getFreeTrialProduct().freeCredits).toFixed(2)}.`,
      )
    ) {
      return;
    }

    try {
      const response = await fetch("/api/subscription/reset-test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.details || data.error || "Failed to reset the test subscription",
        );
      }
      this.showAuthSuccess(data.message);
      this.initializeSubscriptionSection();
    } catch (error) {
      console.error("Test subscription reset error:", error);
      this.showAuthError(error.message);
    }
  }

  /**
   * Reactivate cancelled subscription
   */
  async reactivateSubscription() {
    try {
      const response = await fetch("/api/subscription/reactivate", {
        method: "POST",
        headers: JSON_POST_HEADERS,
        body: "{}",
      });

      const data = await response.json();

      if (response.ok) {
        this.showAuthSuccess(data.message);
        // Reload subscription info
        this.initializeSubscriptionSection();
      } else {
        throw new Error(data.error || "Failed to reactivate subscription");
      }
    } catch (error) {
      console.error("Reactivate error:", error);
      this.showAuthError(error.message);
    }
  }

  /**
   * Update overage setting
   */
  async updateOverageSetting(allowed) {
    try {
      const response = await fetch("/api/subscription/update", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ overageAllowed: allowed }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update setting");
      }

      // Show brief confirmation
      const label = document.querySelector(".credits-overage-setting label");
      const originalText = label.querySelector("span").textContent;
      label.querySelector("span").textContent = allowed
        ? "Pay-as-you-go enabled ✓"
        : "Pay-as-you-go disabled ✓";
      setTimeout(() => {
        label.querySelector("span").textContent = originalText;
      }, 2000);
    } catch (error) {
      console.error("Update setting error:", error);
      // Revert checkbox
      document.getElementById("overageAllowed").checked = !allowed;
      this.showAuthError("Failed to update setting");
    }
  }

  /**
   * Initialize email change form handler
   */
  initializeEmailChangeForm() {
    const emailChangeForm = document.getElementById("emailChangeForm");
    const messageContainer = document.getElementById("emailChangeMessage");

    if (!emailChangeForm) return;

    emailChangeForm.addEventListener("submit", async (e) => {
      e.preventDefault();

      const newEmail = document.getElementById("newEmail").value;
      const submitBtn = emailChangeForm.querySelector('button[type="submit"]');

      submitBtn.disabled = true;
      submitBtn.textContent = "Sending...";
      messageContainer.style.display = "none";

      try {
        const response = await fetch("/api/auth/request-email-change", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ newEmail }),
        });

        const data = await response.json();

        if (response.ok) {
          messageContainer.innerHTML = `✓ ${data.message}`;
          messageContainer.className = "message success";
          messageContainer.style.display = "block";
          emailChangeForm.reset();
        } else {
          messageContainer.innerHTML = `✗ ${
            data.error || "Failed to send confirmation email"
          }`;
          messageContainer.className = "message error";
          messageContainer.style.display = "block";
        }
      } catch (error) {
        console.error("Email change form error:", error);
        messageContainer.innerHTML = `✗ Network error. Please try again.`;
        messageContainer.className = "message error";
        messageContainer.style.display = "block";
      } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = "Send Confirmation Email";
      }
    });
  }

  /**
   * Load editor version info from GitHub for the home page
   */
  async loadEditorVersions() {
    try {
      const [latestRes, listRes] = await Promise.all([
        fetch(
          "https://api.github.com/repos/counterpunchspace/editor/releases/latest",
        ),
        fetch(
          "https://api.github.com/repos/counterpunchspace/editor/releases?per_page=10",
        ),
      ]);
      const latest = await latestRes.json();
      const releases = await listRes.json();
      const prerelease = releases.find((r) => r.prerelease);
      const midnight = (ts) => {
        const d = new Date(ts);
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
      };
      const daysAgo = (d) =>
        Math.floor((midnight(Date.now()) - midnight(d)) / 86400000);
      const ageLabel = (n) =>
        n === 0 ? "today" : n === 1 ? "yesterday" : `${n} days ago`;
      const stableLabel = document.getElementById("stableReleaseLabel");
      if (stableLabel && latest.tag_name) {
        const d = daysAgo(latest.published_at);
        stableLabel.innerHTML = `v${latest.tag_name.replace(/^v/, "")}  ·  <span class="material-symbols-outlined" style="font-size:12px;vertical-align:middle">calendar_month</span> ${ageLabel(d)}  ·  <a href="https://github.com/counterpunchspace/editor/releases/tag/${latest.tag_name}" target="_blank" rel="noopener noreferrer">changelog</a>`;
      }
      const previewLabel = document.getElementById("previewReleaseLabel");
      if (previewLabel && prerelease) {
        const d = daysAgo(prerelease.published_at);
        previewLabel.innerHTML = `v${prerelease.tag_name.replace(/^v/, "")}  ·  <span class="material-symbols-outlined" style="font-size:12px;vertical-align:middle">calendar_month</span> ${ageLabel(d)}  ·  <a href="https://github.com/counterpunchspace/editor/releases/tag/${prerelease.tag_name}" target="_blank" rel="noopener noreferrer">changelog</a>`;
      }
    } catch (e) {
      console.error("Failed to load editor versions:", e);
    }
  }

  /**
   * Initialize pricing page toggle
   */
  async initializePricingPage() {
    // Check subscription status first
    const isLoggedIn = this.isAuthenticated();
    let currentPricingPlan = isLoggedIn ? "free-trial" : "free";
    let hasActiveSubscription = false;
    let currentSubscription = null;
    if (isLoggedIn) {
      try {
        const response = await fetch("/api/subscription/status");
        if (response.ok) {
          const data = await response.json();
          currentSubscription = data.membership || data.subscription || null;
          hasActiveSubscription =
            data.hasSubscription &&
            (currentSubscription?.status === "active" ||
              currentSubscription?.status === "trialing");
          if (hasActiveSubscription && currentSubscription?.productId) {
            currentPricingPlan = currentSubscription.productId;
          }
          this.pricingCloudUsage = data.cloudUsage || null;
        }
      } catch (error) {
        console.error("Failed to fetch subscription status:", error);
      }
    }

    this.pricingChangeState = {
      isLoggedIn,
      currentPricingPlan,
      hasActiveSubscription,
      billingPeriod: currentSubscription?.billingPeriod || null,
    };

    this.updatePricingSubscriptionState({
      isLoggedIn,
      currentPricingPlan,
      hasActiveSubscription,
      currentSubscription,
    });

    // Initialize the pricing UI
    await this.initializePricingUI({
      toggleId: "billingToggle",
      priceId: "basicPrice",
      periodId: "basicPeriod",
      creditAmountId: "basicCreditAmount",
      monthlyLabelId: "monthlyLabel",
      yearlyLabelId: "yearlyLabel",
      countrySelectId: "countrySelect",
    });
  }

  updatePricingSubscriptionState({
    isLoggedIn,
    currentPricingPlan,
    hasActiveSubscription,
    currentSubscription,
  }) {
    const freeCard = document.querySelector(".pricing-card-free");
    const freeTrialCard = document.getElementById("freeTrialPricingCard");
    const freeTrialButton = document.getElementById(
      "freeTrialCreateAccountBtn",
    );
    const withoutPaidPlan = !hasActiveSubscription;

    const planTable = document.querySelector(".pricing-table");
    const currentColumn = hasActiveSubscription
      ? currentPricingPlan
      : isLoggedIn
        ? "freetrial"
        : "free";
    if (planTable) planTable.dataset.current = currentColumn;

    freeTrialButton?.classList.toggle("hidden", isLoggedIn);
    const freeIsCurrent = !isLoggedIn && withoutPaidPlan;
    const trialIsCurrent = isLoggedIn && withoutPaidPlan;
    freeCard?.classList.toggle("pricing-card-current", freeIsCurrent);
    freeTrialCard?.classList.toggle("pricing-card-current", trialIsCurrent);
    document.querySelectorAll("[data-pricing-product]").forEach((card) => {
      card.classList.toggle(
        "pricing-card-current",
        hasActiveSubscription &&
          card.dataset.pricingProduct === currentPricingPlan,
      );
    });
  }

  /**
   * Reusable pricing UI initialization
   */
  async initializePricingUI(elementIds) {
    const toggle = document.getElementById(elementIds.toggleId);
    const monthlyLabel = document.getElementById(elementIds.monthlyLabelId);
    const yearlyLabel = document.getElementById(elementIds.yearlyLabelId);
    const countrySelect = document.getElementById(elementIds.countrySelectId);
    const products = getPricingProducts();
    const fillQuotas = () => {
      document.querySelectorAll("[data-quota]").forEach((cell) => {
        const product = products[cell.dataset.plan];
        if (!product) return;
        const value = {
          fonts: product.maxFontsOwned,
          invites: product.maxInvitesPerAsset,
          glyphs: product.maxGlyphsPerFont,
        }[cell.dataset.quota];
        if (value == null) return;
        cell.textContent = Number(value).toLocaleString("en-US");
      });
    };
    const planField = (id, selector) => {
      for (const node of document.querySelectorAll(`[data-plan="${id}"]`)) {
        if (node.matches(selector)) return node;
        const child = node.querySelector(selector);
        if (child) return child;
      }
      return null;
    };
    fillQuotas();

    const cards = ["basic", "advanced", "pro", "enterprise"].flatMap((id) => {
      const product = products[id];
      const price = planField(id, "[data-price]");
      if (!product || !price || !(product.pricing?.monthly?.base > 0))
        return [];
      return [
        {
          product,
          price,
          period: planField(id, "[data-period]"),
          credit: planField(id, "[data-credit]"),
          vat: planField(id, "[data-vat]"),
          consumerLabel: planField(id, "[data-consumer-label]"),
          business: planField(id, "[data-business]"),
          businessAmount: planField(id, "[data-business-amount]"),
          businessPeriod: planField(id, "[data-business-period]"),
        },
      ];
    });

    if (
      !toggle ||
      !monthlyLabel ||
      !yearlyLabel ||
      !countrySelect ||
      cards.length === 0
    ) {
      return;
    }

    // Store PPP data
    let pppData = {};
    let currentMultiplier = 1.0;

    // Load and parse PPP data
    try {
      // Valid ISO 3166-1 alpha-3 country codes (actual countries, not regions/aggregates)
      const validCountryCodes = new Set([
        "AFG",
        "ALB",
        "DZA",
        "ASM",
        "AND",
        "AGO",
        "AIA",
        "ATG",
        "ARG",
        "ARM",
        "ABW",
        "AUS",
        "AUT",
        "AZE",
        "BHS",
        "BHR",
        "BGD",
        "BRB",
        "BLR",
        "BEL",
        "BLZ",
        "BEN",
        "BMU",
        "BTN",
        "BOL",
        "BIH",
        "BWA",
        "BRA",
        "BRN",
        "BGR",
        "BFA",
        "BDI",
        "KHM",
        "CMR",
        "CAN",
        "CPV",
        "CYM",
        "CAF",
        "TCD",
        "CHL",
        "CHN",
        "COL",
        "COM",
        "COG",
        "COD",
        "CRI",
        "CIV",
        "HRV",
        "CUB",
        "CUW",
        "CYP",
        "CZE",
        "DNK",
        "DJI",
        "DMA",
        "DOM",
        "ECU",
        "EGY",
        "SLV",
        "GNQ",
        "ERI",
        "EST",
        "SWZ",
        "ETH",
        "FLK",
        "FRO",
        "FJI",
        "FIN",
        "FRA",
        "GUF",
        "PYF",
        "GAB",
        "GMB",
        "GEO",
        "DEU",
        "GHA",
        "GIB",
        "GRC",
        "GRL",
        "GRD",
        "GLP",
        "GUM",
        "GTM",
        "GGY",
        "GIN",
        "GNB",
        "GUY",
        "HTI",
        "HND",
        "HKG",
        "HUN",
        "ISL",
        "IND",
        "IDN",
        "IRN",
        "IRQ",
        "IRL",
        "IMN",
        "ISR",
        "ITA",
        "JAM",
        "JPN",
        "JEY",
        "JOR",
        "KAZ",
        "KEN",
        "KIR",
        "PRK",
        "KOR",
        "KWT",
        "KGZ",
        "LAO",
        "LVA",
        "LBN",
        "LSO",
        "LBR",
        "LBY",
        "LIE",
        "LTU",
        "LUX",
        "MAC",
        "MDG",
        "MWI",
        "MYS",
        "MDV",
        "MLI",
        "MLT",
        "MHL",
        "MTQ",
        "MRT",
        "MUS",
        "MYT",
        "MEX",
        "FSM",
        "MDA",
        "MCO",
        "MNG",
        "MNE",
        "MSR",
        "MAR",
        "MOZ",
        "MMR",
        "NAM",
        "NRU",
        "NPL",
        "NLD",
        "NCL",
        "NZL",
        "NIC",
        "NER",
        "NGA",
        "NIU",
        "NFK",
        "MKD",
        "MNP",
        "NOR",
        "OMN",
        "PAK",
        "PLW",
        "PSE",
        "PAN",
        "PNG",
        "PRY",
        "PER",
        "PHL",
        "PCN",
        "POL",
        "PRT",
        "PRI",
        "QAT",
        "REU",
        "ROU",
        "RUS",
        "RWA",
        "BLM",
        "SHN",
        "KNA",
        "LCA",
        "MAF",
        "SPM",
        "VCT",
        "WSM",
        "SMR",
        "STP",
        "SAU",
        "SEN",
        "SRB",
        "SYC",
        "SLE",
        "SGP",
        "SXM",
        "SVK",
        "SVN",
        "SLB",
        "SOM",
        "ZAF",
        "SGS",
        "SSD",
        "ESP",
        "LKA",
        "SDN",
        "SUR",
        "SJM",
        "SWE",
        "CHE",
        "SYR",
        "TWN",
        "TJK",
        "TZA",
        "THA",
        "TLS",
        "TGO",
        "TKL",
        "TON",
        "TTO",
        "TUN",
        "TUR",
        "TKM",
        "TCA",
        "TUV",
        "UGA",
        "UKR",
        "ARE",
        "GBR",
        "USA",
        "UMI",
        "URY",
        "UZB",
        "VUT",
        "VEN",
        "VNM",
        "VGB",
        "VIR",
        "WLF",
        "ESH",
        "YEM",
        "ZMB",
        "ZWE",
      ]);

      // Country name overrides
      const countryNameOverrides = {
        "West Bank and Gaza": "Palestine",
      };

      const response = await fetch("/data/ppp/ppp.csv");
      const csvText = await response.text();
      const lines = csvText.split("\n");

      // Parse CSV line properly
      const parseCSVLine = (line) => {
        const result = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
          const char = line[i];

          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === "," && !inQuotes) {
            result.push(current);
            current = "";
          } else {
            current += char;
          }
        }
        result.push(current);
        return result;
      };

      // Helper to find latest available GDP value
      const getLatestGdp = (parts) => {
        // Start from the end (most recent year) and work backwards
        for (let i = parts.length - 1; i >= 4; i--) {
          const value = parseFloat(parts[i]);
          if (value && !isNaN(value) && value > 0) {
            return value;
          }
        }
        return null;
      };

      // Find US GDP per capita
      const usLine = lines.find((line) => line.includes('"United States"'));
      if (!usLine) throw new Error("US data not found");

      const usParts = parseCSVLine(usLine);
      const usGdp = getLatestGdp(usParts);

      if (!usGdp || isNaN(usGdp)) {
        throw new Error("Invalid US GDP data");
      }

      console.log("US GDP per capita (latest):", usGdp);

      // Parse all countries
      const countries = [];
      const seenCountryCodes = new Set();
      const missingCountries = [];
      for (const line of lines) {
        if (
          !line.includes("GDP per capita, PPP") ||
          line.startsWith('"Data Source')
        )
          continue;

        const parts = parseCSVLine(line);
        if (parts.length < 5) continue;

        let countryName = parts[0];
        const countryCode = parts[1];

        // Apply country name override if exists
        if (countryNameOverrides[countryName]) {
          countryName = countryNameOverrides[countryName];
        }

        // Skip if not a valid country code
        if (!validCountryCodes.has(countryCode)) {
          continue;
        }
        seenCountryCodes.add(countryCode);

        const gdp = getLatestGdp(parts);

        // Skip if no valid GDP data
        if (!gdp) {
          missingCountries.push({
            name: countryName,
            code: countryCode,
            reason: "No GDP in the PPP file",
          });
          continue;
        }

        // Calculate PPP multiplier (ratio of country GDP to US GDP)
        const multiplier = gdp / usGdp;

        countries.push({
          name: countryName,
          code: countryCode,
          gdp: gdp,
          multiplier: multiplier,
        });

        pppData[countryCode] = multiplier;
      }

      try {
        const overrideResponse = await fetch("/data/ppp/overrides.json");
        if (overrideResponse.ok) {
          const overrideFile = await overrideResponse.json();
          const entries = Array.isArray(overrideFile)
            ? overrideFile
            : overrideFile.entries;
          applyPppOverrides(countries, entries, usGdp);
          for (const country of countries) {
            if (!pppData[country.code]) {
              pppData[country.code] = country.multiplier;
              seenCountryCodes.add(country.code);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load PPP overrides:", error);
      }

      for (const code of validCountryCodes) {
        if (!seenCountryCodes.has(code)) {
          missingCountries.push({
            name: code,
            code,
            reason: "Not in the PPP file",
          });
        }
      }
      for (let i = missingCountries.length - 1; i >= 0; i--) {
        if (pppData[missingCountries[i].code]) missingCountries.splice(i, 1);
      }
      missingCountries.sort((a, b) => a.name.localeCompare(b.name));
      this.pricingCostCountries = [...countries].sort(
        (a, b) => b.multiplier - a.multiplier || a.name.localeCompare(b.name),
      );
      this.pricingMissingCountries = missingCountries;

      console.log("Loaded countries:", countries.length);

      // Fetch detected country from IP
      let detectedCountryCode = "USA"; // Default fallback
      try {
        const countryResponse = await fetch("/api/subscription/countries");
        if (countryResponse.ok) {
          const countryData = await countryResponse.json();
          detectedCountryCode = countryData.detectedCountry || "USA";
          console.log("Detected country from IP:", detectedCountryCode);
        }
      } catch (error) {
        console.error("Failed to detect country:", error);
      }

      // Sort countries alphabetically
      countries.sort((a, b) => a.name.localeCompare(b.name));

      // Populate dropdown
      countrySelect.innerHTML = "";
      countries.forEach((country) => {
        const option = document.createElement("option");
        option.value = country.code;
        option.textContent = country.name;
        // Select detected country or USA as fallback
        if (country.code === detectedCountryCode) {
          option.selected = true;
          currentMultiplier = pppData[country.code] || 1.0;
        }
        countrySelect.appendChild(option);
      });
    } catch (error) {
      console.error("Error loading PPP data:", error);
      countrySelect.innerHTML =
        '<option value="USA">United States (default)</option>';
    }

    let pricingGeneration = 0;
    const page = this;

    function money(amount) {
      return `<span class="pricing-currency">€</span>${amount.toFixed(2)}`;
    }

    function animateAmount(card, key, element, endValue, formatFn) {
      if (!element) return;
      const startValue = card[key];
      if (card[`${key}Timer`]) clearTimeout(card[`${key}Timer`]);
      if (startValue == null || startValue === endValue) {
        element.innerHTML = formatFn(endValue);
        card[key] = endValue;
        return;
      }
      const steps = 10;
      const stepDuration = 30;
      let currentStep = 0;
      const tick = () => {
        currentStep += 1;
        const progress = currentStep / steps;
        const currentValue = startValue + (endValue - startValue) * progress;
        element.innerHTML = formatFn(currentValue);
        card[key] = currentValue;
        if (currentStep < steps) {
          card[`${key}Timer`] = setTimeout(tick, stepDuration);
        } else {
          card[key] = endValue;
          card[`${key}Timer`] = null;
        }
      };
      tick();
    }

    async function taxFor(exclusiveCents, priceDisplay) {
      if (priceDisplay === "exclusive") return null;
      const taxResponse = await fetch(
        `/api/subscription/tax-preview?country=${encodeURIComponent(countrySelect.value)}&amountCents=${exclusiveCents}`,
      );
      if (!taxResponse.ok) return null;
      const tax = await taxResponse.json();
      if (tax.percentage == null || !(tax.taxCents > 0)) return null;
      const percent = Number(tax.percentage);
      return {
        inclusive: tax.totalCents / 100,
        percentLabel: Number.isInteger(percent)
          ? String(percent)
          : percent.toFixed(1),
      };
    }

    async function updatePricing() {
      const generation = ++pricingGeneration;
      try {
        const isYearly = toggle.checked;
        const selectedCountry = countrySelect.value;
        const interpolatedMultiplier = pppMultiplier(currentMultiplier);
        const alpha2 = pppCountryToStripe(selectedCountry);
        const priceDisplay = euPriceDisplay(alpha2);
        const periodText = isYearly ? "per year" : "per month";
        const canPreviewTax = selectedCountry && selectedCountry !== "loading";

        monthlyLabel.classList.toggle("active", !isYearly);
        yearlyLabel.classList.toggle("active", isYearly);

        const updates = await Promise.all(
          cards.map(async (card) => {
            const monthly =
              card.product.pricing.monthly.base * interpolatedMultiplier;
            const exclusive = isYearly ? monthly * yearlyMonths : monthly;
            let displayPrice = exclusive;
            let vatLabel = "";
            let priceLabel = "Price";
            let showBusinessPrice = false;

            if (canPreviewTax && priceDisplay !== "exclusive") {
              try {
                const tax = await taxFor(
                  Math.round(exclusive * 100),
                  priceDisplay,
                );
                if (tax && priceDisplay === "inclusive") {
                  displayPrice = tax.inclusive;
                  vatLabel = `incl. ${tax.percentLabel}% VAT`;
                  priceLabel = `Incl. ${tax.percentLabel}% VAT`;
                } else if (tax) {
                  displayPrice = tax.inclusive;
                  showBusinessPrice = true;
                  priceLabel = `Incl. ${tax.percentLabel}% VAT`;
                }
              } catch (error) {
                console.error("Tax preview failed:", error);
              }
            }

            return {
              card,
              exclusive,
              monthlyRevenue:
                card.product.pricing.monthly.base * interpolatedMultiplier,
              displayPrice,
              vatLabel,
              priceLabel,
              showBusinessPrice,
              credit: exclusive * (Number(card.product.creditShare) || 0),
            };
          }),
        );
        if (generation !== pricingGeneration) return;

        const priceLabel = document.getElementById("pricingPriceLabel");
        const businessRow = document.querySelector("[data-business-row]");
        if (updates[0]) {
          if (priceLabel) priceLabel.textContent = updates[0].priceLabel;
          if (businessRow) businessRow.hidden = !updates[0].showBusinessPrice;
        }

        for (const update of updates) {
          const { card } = update;
          if (card.vat) {
            card.vat.textContent = update.vatLabel;
            card.vat.hidden = !update.vatLabel;
          }
          if (card.consumerLabel)
            card.consumerLabel.hidden = !update.showBusinessPrice;
          if (card.business) card.business.hidden = !update.showBusinessPrice;
          if (card.period) card.period.textContent = periodText;
          if (card.businessPeriod) card.businessPeriod.textContent = periodText;
          animateAmount(
            card,
            "displayedPrice",
            card.price,
            update.displayPrice,
            money,
          );
          if (update.showBusinessPrice) {
            animateAmount(
              card,
              "displayedBusiness",
              card.businessAmount,
              update.exclusive,
              money,
            );
          }
          animateAmount(
            card,
            "displayedCredit",
            card.credit,
            update.credit,
            money,
          );
          page.pricingMonthlyRevenue = page.pricingMonthlyRevenue || {};
          page.pricingMonthlyRevenue[card.product.id] = update.monthlyRevenue;
        }
        page.pricingMonthlyRevenue.free = 0;
        page.pricingMonthlyRevenue.freetrial = 0;
        page.renderPricingServerCosts();
      } catch (error) {
        console.error("Error in updatePricing:", error);
      }
      page.updatePlanChangeButtons();
    }

    // Handle country selection
    countrySelect.addEventListener("change", function () {
      const selectedCode = countrySelect.value;
      currentMultiplier = pppData[selectedCode] || 1.0;
      updatePricing();
    });

    // Set initial state
    monthlyLabel.classList.add("active");
    updatePricing();

    // Add event listeners for billing toggle
    toggle.addEventListener("change", updatePricing);

    // Make labels clickable
    monthlyLabel.addEventListener("click", function (e) {
      e.preventDefault();
      toggle.checked = false;
      updatePricing();
    });

    yearlyLabel.addEventListener("click", function (e) {
      e.preventDefault();
      toggle.checked = true;
      updatePricing();
    });
  }

  /**
   * Swap handbook screenshots between light/dark assets to match theme.
   */
  refreshDocumentationScreenshots() {
    const isLight =
      document.documentElement.getAttribute("data-theme") === "light";
    document
      .querySelectorAll("img.docs-screenshot[data-dark-src]")
      .forEach((img) => {
        const lightSrc = img.getAttribute("data-light-src");
        const darkSrc = img.getAttribute("data-dark-src");
        if (!lightSrc || !darkSrc) return;
        const nextSrc = isLight ? lightSrc : darkSrc;
        if (img.getAttribute("src") !== nextSrc) {
          img.setAttribute("src", nextSrc);
        }
      });
  }

  /**
   * Keep the active handbook Contents item visible in the sticky sidebar.
   */
  scrollDocumentationTocIntoView() {
    const current = document.querySelector(".article-toc a.is-current");
    if (current && typeof current.scrollIntoView === "function") {
      current.scrollIntoView({ block: "nearest" });
    }
  }

  /**
   * Initialize article TOC with visibility-based opacity
   */
  initializeArticleTOC() {
    // Clean up previous TOC listeners if they exist
    if (this._articleTOCCleanup) {
      this._articleTOCCleanup();
      this._articleTOCCleanup = null;
    }

    const tocLinks = document.querySelectorAll(".article-toc a");
    if (tocLinks.length === 0) return;

    // Create a map of section ranges to their corresponding TOC links
    const sectionRanges = [];
    tocLinks.forEach((link) => {
      const href = link.getAttribute("href");
      if (href && href.startsWith("#")) {
        const sectionId = href.substring(1);
        const heading = document.getElementById(sectionId);
        if (heading) {
          sectionRanges.push({ heading, link });
        }
      }
    });

    if (sectionRanges.length === 0) return;

    // Calculate section boundaries (from heading to next heading)
    sectionRanges.forEach((range, index) => {
      const nextHeading = sectionRanges[index + 1]?.heading;
      range.endElement = nextHeading || null;
    });

    // Function to calculate visibility of a section range
    const calculateSectionVisibility = (startElement, endElement) => {
      const windowHeight = window.innerHeight;
      const topDeadZone = 40;
      const bottomDeadZone = 60;
      const effectiveViewportTop = topDeadZone;
      const effectiveViewportBottom = windowHeight - bottomDeadZone;

      // Get the top of the section
      const sectionTop = startElement.getBoundingClientRect().top;

      // Get the bottom of the section (either the next heading or end of article)
      let sectionBottom;
      if (endElement) {
        sectionBottom = endElement.getBoundingClientRect().top;
      } else {
        // Last section - use the article container's bottom
        const articleDetail = document.querySelector(".article-detail");
        sectionBottom = articleDetail
          ? articleDetail.getBoundingClientRect().bottom
          : sectionTop + 1000;
      }

      // Calculate total section height
      const totalHeight = sectionBottom - sectionTop;
      if (totalHeight <= 0) return 0;

      // If section is completely above effective viewport
      if (sectionBottom < effectiveViewportTop) return 0;

      // If section is completely below effective viewport
      if (sectionTop > effectiveViewportBottom) return 0;

      // Calculate what portion of the section is visible within the effective viewport
      const viewportSectionTop = Math.max(effectiveViewportTop, sectionTop);
      const viewportSectionBottom = Math.min(
        effectiveViewportBottom,
        sectionBottom,
      );
      const visibleHeight = Math.max(
        0,
        viewportSectionBottom - viewportSectionTop,
      );

      // Use the smaller of section height or viewport height as normalizer
      // Short sections: normalized by their own height (full opacity when fully visible)
      // Long sections: normalized by viewport height (full opacity when filling viewport)
      const effectiveViewportHeight =
        effectiveViewportBottom - effectiveViewportTop;
      const normalizer = Math.min(totalHeight, effectiveViewportHeight);
      return Math.max(0, Math.min(1, visibleHeight / normalizer));
    };

    // Function to update TOC line opacities
    const updateTOCOpacity = () => {
      sectionRanges.forEach((range) => {
        const visibility = calculateSectionVisibility(
          range.heading,
          range.endElement,
        );
        // Apply power curve to make fade-out more pronounced (earlier and steeper)
        // Using power of 2.5 makes the opacity drop off more quickly as content scrolls away
        const opacity = Math.pow(visibility, 2.5);
        range.link.style.setProperty(
          "--visibility-opacity",
          opacity.toFixed(3),
        );
      });
    };

    // Initial update
    updateTOCOpacity();

    // Update on scroll with throttling for performance
    let scrollTimeout;
    const handleScroll = () => {
      if (scrollTimeout) {
        window.cancelAnimationFrame(scrollTimeout);
      }
      scrollTimeout = window.requestAnimationFrame(updateTOCOpacity);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Also update on window resize
    let resizeTimeout;
    const handleResize = () => {
      if (resizeTimeout) {
        window.cancelAnimationFrame(resizeTimeout);
      }
      resizeTimeout = window.requestAnimationFrame(updateTOCOpacity);
    };

    window.addEventListener("resize", handleResize, { passive: true });

    // Clean up listeners when navigating away
    const cleanup = () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };

    // Store cleanup function for potential future use
    this._articleTOCCleanup = cleanup;
  }

  /**
   * Show success message
   */
  showAuthSuccess(message) {
    const notification = document.createElement("div");
    notification.style.cssText = `
      position: fixed;
      top: 60px;
      right: 20px;
      background: rgba(46, 213, 115, 0.95);
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      z-index: 10000;
      font-family: var(--font-families-sans);
      font-size: 14px;
      max-width: 400px;
    `;
    notification.textContent = `✓ ${message}`;

    document.body.appendChild(notification);

    // Remove after 5 seconds
    setTimeout(() => {
      notification.style.opacity = "0";
      notification.style.transition = "opacity 0.3s ease";
      setTimeout(() => notification.remove(), 300);
    }, 5000);
  }
}

// Initialize the app navigator
const app = new AppNavigator();

// Expose to window for potential external control
window.appNavigator = app;
